/**
 * Agency Portfolio Page
 *
 * Asana-portfolio-style overview of all client workspaces.
 * Five views: Grid (spreadsheet rows), Board (health-tier columns),
 * Timeline (scheduled-post heat-map), Workload (staff-centric), Inbox.
 *
 * All data is live — fetched via usePortfolioClients / usePortfolioHighlights
 * from /api/agency/portfolio. Mutations: status update, plan batch, send digest,
 * carte blanche toggle, brand reassign, workspace edit/delete, saved views.
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { getISOWeek, startOfDay, endOfDay, isSameDay } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tierLabel, lifecycleStageLabel, healthSignalLabel } from '@/utils/portfolioLabels';
import { invalidateReleaseState } from '@/lib/invalidateRelease';
import { useTimezone } from '@/contexts/TimezoneContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Icon } from '@/components/ui/Icon';
import { SelectChevron } from '@/components/ui/Select';
import { filterTriggerHover, filterTriggerFocus } from '@/components/ui/filterTriggerStyles';
import { ICONS } from '@/constants/icons';
import { Tooltip, TooltipRoot, TooltipTrigger, TooltipPortal, TooltipContent } from '@/components/ui/Tooltip';
import { getContrastTextColor } from '@/utils/colors';
import { STATUS_STYLES } from '@/utils/calendarStatus';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Toggle } from '@/components/ui/Toggle';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useDebounce } from '@/hooks/useDebounce';
import { ComparePanel } from '@/components/portfolio/ComparePanel';
import { AwardMomentsStrip } from '@/components/portfolio/AwardMomentsStrip';
import { RepurposingOpportunitiesStrip } from '@/components/portfolio/RepurposingOpportunitiesStrip';
import { StatusUpdateComposeModal } from '@/components/portfolio/StatusUpdateComposeModal';
import { StatusUpdateHistoryModal } from '@/components/portfolio/StatusUpdateHistoryModal';
import { SpeedDialFab, type SpeedDialActionId } from '@/components/portfolio/SpeedDialFab';
import { useOverlayOpen } from '@/hooks/useOverlayOpen';
import { usesAgencyStaffNav } from '@/utils/bottomNavMode';
import { CreateBrandSpaceModal } from '@/components/portfolio/CreateBrandSpaceModal';
import { EditBrandSpaceModal } from '@/components/portfolio/EditBrandSpaceModal';
import { BrandSpaceUsageText } from '@/components/portfolio/BrandSpaceUsageText';
import { useBrandSpaceUsage } from '@/hooks/useBrandSpaceUsage';
import { PlanBatchModal } from '@/components/portfolio/PlanBatchModal';
import { SendDigestModal } from '@/components/portfolio/SendDigestModal';
import { PipelineBar } from '@/components/portfolio/PipelineBar';
import { RunwayBar } from '@/components/portfolio/RunwayBar';
import { RiskRadarChips } from '@/components/portfolio/RiskRadarChips';
import { LowPipelineLabel } from '@/components/portfolio/LowPipelineLabel';
import { ChannelHealthDot } from '@/components/portfolio/ChannelHealthDot';
import { RenewalCliffBadge } from '@/components/portfolio/RenewalCliffBadge';
import { VelocityTrendArrow } from '@/components/portfolio/VelocityTrendArrow';
import { SparklineMini } from '@/components/portfolio/SparklineMini';
import { WorkloadView } from '@/components/portfolio/WorkloadView';
import { GlossaryTooltip } from '@/components/portfolio/GlossaryTooltip';
import { countLowPipeline, formatRunsDryOn, isLowPipeline, pipelineBarDays } from '@/utils/lowPipeline';
import { GlossaryModal } from '@/components/portfolio/GlossaryModal';
import { PortfolioInboxView } from '@/components/portfolio/PortfolioInboxView';
import { TimelineControls, type TimelineRange } from '@/components/portfolio/TimelineControls';
import {
  getSpanRange,
  getWeekStart,
  daysInPeriod,
  toDateInputValue,
  fromDateInputValue,
  type TimelinePreset,
} from '@/utils/statusUpdatePeriod';
import {
  usePortfolioStore,
  SAVED_VIEW_CONFIGS,
  BADGE_CONFIGS,
  type SavedView,
  type BadgeId,
} from '@/stores/portfolioStore';
import type { StatusUpdateDraft } from '@/types/portfolio';
import {
  type AssetInventory,
  type PortfolioClient,
  type HealthTier,
  type HealthDimension,
} from '@/data/portfolio.types';
import {
  usePortfolioClients,
  useSaveStatusUpdate,
  useAgencyStaff,
  usePortfolioHighlights,
  usePortfolioTimeline,
  usePortfolioViews,
  useCreatePortfolioView,
  useDeletePortfolioView,
  type PortfolioHighlightsPeriod,
} from '@/hooks/usePortfolio';
import { useReleaseInboxCounts } from '@/hooks/useReleaseSections';
import { PortfolioLocksProvider, usePortfolioLocks } from '@/hooks/usePortfolioLocks';
import { useGatingStore } from '@/stores/gating.store';
import { LockBadge } from '@/components/gating/LockBadge';
import { LockedTeaserRow } from '@/components/gating/LockedTeaserRow';
import { PortfolioFeatureGate } from '@/components/portfolio/PortfolioFeatureGate';
import type { PortfolioViewRecord, TimelineNote, TimelineHoliday } from '@/services/portfolio.service';
import { campaignMatchesDay } from '@/utils/campaignMatcher';
import type { CalendarNote } from '@/types/calendar-notes';
import { parseBoardColumnParam } from '@/types/releaseBoard';
import { showToast } from '@/components/ui/Toast';
import { avatarTextClass } from '@/utils/avatarContrast';
import { useMutation, useQuery, useQueryClient, useIsMutating } from '@tanstack/react-query';
import * as agencyService from '@/services/agency.service';
import { setAccessToken, formatApiError, isGlobalGatingHandledError } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useAgencyGovernance } from '@/hooks/useAgencyGovernance';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';

// ── Types ─────────────────────────────────────────────────────────────────────

type ViewMode = 'grid' | 'board' | 'timeline' | 'workload' | 'inbox';

// Deep-linkable via ?tab= (matches the ?tab= convention used by Settings.tsx,
// AgencySettings.tsx, StatusAdmin.tsx and ApprovalBoard.tsx for tabbed pages).
const VIEW_MODES: readonly ViewMode[] = ['grid', 'board', 'timeline', 'workload', 'inbox'];
function isViewMode(value: string | null): value is ViewMode {
  return value !== null && (VIEW_MODES as readonly string[]).includes(value);
}

// Deep-linkable Timeline range via ?tp=<preset>[&tfrom=&tto=] (only
// meaningful on the timeline tab; harmless to leave in the URL on other
// tabs — see setView / handleTimelineRangeChange below). Namespaced with a
// `t` prefix so it never collides with `tab`, `createBrand`, `openBrand`,
// `action` or `panel`.
const TIMELINE_PRESETS: readonly TimelinePreset[] = ['2w', 'month', 'quarter', 'custom'];
function isTimelinePreset(value: string | null): value is TimelinePreset {
  return value !== null && (TIMELINE_PRESETS as readonly string[]).includes(value);
}

const TIMELINE_YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Mirrors TimelineControls' MAX_CUSTOM_RANGE_DAYS — keep in sync. */
const MAX_TIMELINE_URL_RANGE_DAYS = 92;

/**
 * Resolves `?tp=`/`?tfrom=`/`?tto=` into a concrete `TimelineRange`, or
 * `null` when absent/invalid — callers fall back to the saved per-agency
 * pref or the default, never crash on a garbage/tampered URL.
 *
 * Relative presets (`2w`/`month`/`quarter`) are ALWAYS re-anchored to TODAY
 * (never restore stale absolute dates from an old shared link) — only
 * `tp=custom` uses the literal `tfrom`/`tto` bounds, reconstructed the same
 * way TimelineControls' `handleCustomRangeChange` builds a custom range
 * (start-of-day `from`, end-of-day `to`) so a round-trip through the URL is
 * stable.
 */
function parseTimelineUrlParams(
  searchParams: URLSearchParams,
  timezone: string,
): TimelineRange | null {
  const tp = searchParams.get('tp');
  if (!isTimelinePreset(tp)) return null;

  if (tp !== 'custom') {
    return { ...getSpanRange(tp, new Date(), timezone), preset: tp };
  }

  const tfrom = searchParams.get('tfrom');
  const tto = searchParams.get('tto');
  if (!tfrom || !tto || !TIMELINE_YMD_RE.test(tfrom) || !TIMELINE_YMD_RE.test(tto)) return null;

  const fromDay = fromDateInputValue(tfrom);
  const toDay = fromDateInputValue(tto);
  // Reject calendar-rollover garbage (e.g. "2026-13-40") — a real date must
  // round-trip back to the exact same YYYY-MM-DD string.
  if (toDateInputValue(fromDay) !== tfrom || toDateInputValue(toDay) !== tto) return null;
  if (fromDay.getTime() > toDay.getTime()) return null;
  if (daysInPeriod(tfrom, tto) > MAX_TIMELINE_URL_RANGE_DAYS) return null;

  return { from: startOfDay(fromDay), to: endOfDay(toDay), preset: 'custom' };
}

// ── Health Tier Config ────────────────────────────────────────────────────────

interface TierConfig {
  /** Tier id; render the visible label via tierLabel(t, key), never `label` (English fallback). */
  key: HealthTier;
  label: string;
  dotColor: string;
  pillBg: string;
  pillText: string;
  headerBg: string;
  headerText: string;
  rowHover: string;
}

const TIER_CONFIG: Record<HealthTier, TierConfig> = {
  on_track: {
    key: 'on_track',
    label: 'On track',
    dotColor: 'bg-primary-500',
    pillBg: 'bg-primary-50',
    pillText: 'text-primary-700',
    headerBg: 'bg-primary-50',
    headerText: 'text-primary-700',
    rowHover: 'hover:bg-primary-25',
  },
  watch: {
    key: 'watch',
    label: 'Watch',
    dotColor: 'bg-peach',
    pillBg: 'bg-peach/30',
    pillText: 'text-coral-700',
    headerBg: 'bg-peach/30',
    headerText: 'text-coral-700',
    rowHover: 'hover:bg-peach/30',
  },
  at_risk: {
    key: 'at_risk',
    label: 'At risk',
    dotColor: 'bg-coral-400',
    pillBg: 'bg-coral-50',
    pillText: 'text-coral-700',
    headerBg: 'bg-coral-50',
    headerText: 'text-coral-700',
    rowHover: 'hover:bg-coral-50',
  },
  off_track: {
    key: 'off_track',
    label: 'Off track',
    dotColor: 'bg-coral-600',
    pillBg: 'bg-coral-50',
    pillText: 'text-coral-700',
    headerBg: 'bg-coral-50',
    headerText: 'text-coral-700',
    rowHover: 'hover:bg-coral-50',
  },
  paused: {
    key: 'paused',
    label: 'Paused',
    dotColor: 'bg-sage-gray',
    pillBg: 'bg-gray-100',
    pillText: 'text-sage-gray',
    headerBg: 'bg-gray-100',
    headerText: 'text-sage-gray',
    rowHover: 'hover:bg-gray-50',
  },
};

const LIFECYCLE_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: 'bg-primary-50', text: 'text-primary-700', label: 'Active' },
  onboarding: { bg: 'bg-peach/30', text: 'text-coral-700', label: 'Onboarding' },
  churning: { bg: 'bg-coral-50', text: 'text-coral-700', label: 'Deprioritizing' },
  paused: { bg: 'bg-gray-100', text: 'text-sage-gray', label: 'Paused' },
};

// Lifecycle stage → glossary term ID mapping
const LIFECYCLE_TERM_ID: Record<string, string> = {
  active: 'lifecycle-active',
  onboarding: 'lifecycle-onboarding',
  churning: 'lifecycle-churning',
  paused: 'lifecycle-paused',
};

// Editorial text-only lifecycle badge (no filled bg, just uppercase text)
const LifecycleBadge = ({
  stage,
  disableGlossaryOnMobile = false,
}: {
  stage: string;
  /** On mobile, skip the tap-to-open glossary sheet (use inside tappable cards). */
  disableGlossaryOnMobile?: boolean;
}) => {
  const { t } = useTranslation('agency');
  const cfg = LIFECYCLE_CONFIG[stage] ?? LIFECYCLE_CONFIG.active;
  const termId = LIFECYCLE_TERM_ID[stage] ?? 'lifecycle-active';
  return (
    <GlossaryTooltip termId={termId} side="top" disableOnMobile={disableGlossaryOnMobile}>
      <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${cfg.text} cursor-default`}>
        {lifecycleStageLabel(t, stage)}
      </span>
    </GlossaryTooltip>
  );
};

// ── Saved view filter logic ───────────────────────────────────────────────────

/** Presets that read relationship-derived fields (renewal, size, lifecycle). The backend strips those fields
 *  (non-paused lifecycle -> 'active', size -> 'smb'; 'paused' is kept) when `portfolio_client_relationship` is locked. */
const RELATIONSHIP_PRESETS: readonly SavedView[] = ['renewal_this_month', 'enterprise', 'onboarding'];

function applySavedView(
  clients: PortfolioClient[],
  view: SavedView,
  currentUserId: number | null,
): PortfolioClient[] {
  switch (view) {
    case 'all':
      return clients;
    case 'my_clients':
      return currentUserId === null
        ? []
        : clients.filter((c) => c.ownerId === currentUserId);
    case 'at_risk':
      return clients.filter(
        (c) => c.healthOverall === 'at_risk' || c.healthOverall === 'off_track'
      );
    case 'enterprise':
      return clients.filter((c) => c.clientSize === 'enterprise');
    case 'onboarding':
      return clients.filter((c) => c.lifecycleStage === 'onboarding');
    case 'renewal_this_month':
      return clients.filter(
        (c) =>
          c.renewal.daysUntilRenewal !== null && c.renewal.daysUntilRenewal < 30
      );
    case 'paused':
      return clients.filter((c) => c.lifecycleStage === 'paused');
    default:
      return clients;
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Small colored dot + score pill, clickable to open breakdown popover */
const HealthPill = ({
  dimension,
  label,
  tier,
  weeklyScoreHistory,
}: {
  dimension: HealthDimension;
  label: string;
  tier: HealthTier;
  weeklyScoreHistory?: number[];
}) => {
  const { t } = useTranslation('agency');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Map 0–100 score to a readable tier
  const scoreToTier = (score: number): TierConfig => {
    if (score >= 80) return TIER_CONFIG.on_track;
    if (score >= 60) return TIER_CONFIG.watch;
    if (score >= 40) return TIER_CONFIG.at_risk;
    if (score > 0) return TIER_CONFIG.off_track;
    return TIER_CONFIG.paused;
  };

  const dimTier = scoreToTier(dimension.score);

  // Sparkline trend class - returns a Tailwind text-color class so SparklineMini
  // uses currentColor and respects whitelabel CSS variable overrides.
  // Palette mapping:
  //   improving → text-primary-500 (our main green-teal)
  //   declining → text-coral-500   (our semantic red/danger)
  //   neutral   → text-gray-400    (standard neutral)
  const getSparklineClass = (data: number[]): string => {
    if (data.length < 2) return 'text-gray-400';
    const last = data[data.length - 1];
    const first = data[0];
    if (last > first + 5) return 'text-primary-500';  // improving
    if (last < first - 5) return 'text-coral-500';    // declining
    return 'text-gray-400';                            // neutral
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (tier === 'paused') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-gray-200 text-sage-gray tabular-nums">
        <span className="w-1.5 h-1.5 rounded-full bg-sage-gray shrink-0" />
        -
      </span>
    );
  }

  // At-risk / off-track: filled bg for danger signal pop
  const isDanger = tier === 'at_risk' || tier === 'off_track';
  const pillClass = isDanger
    ? `${dimTier.pillBg} ${dimTier.pillText}`
    : `bg-white ring-1 ring-gray-200 ${dimTier.pillText}`;

  // `label` is the canonical English dimension id ('Operational' | 'Brand Equity' | 'Brand' | 'Commercial');
  // everything shown to the user is translated here.
  const dimKey: 'operational' | 'brand_equity' | 'commercial' =
    label === 'Operational' ? 'operational' : label === 'Commercial' ? 'commercial' : 'brand_equity';
  const displayLabel =
    dimKey === 'operational'
      ? t('portfolio_health_operational', { defaultValue: 'Operational' })
      : dimKey === 'commercial'
      ? t('portfolio_health_commercial', { defaultValue: 'Commercial' })
      : t('portfolio_health_brand_equity', { defaultValue: 'Brand Equity' });
  const pillTooltip =
    dimKey === 'operational'
      ? t('portfolio_tooltip_health_operational', { defaultValue: 'Can we execute? Release velocity, scheduling runway, channel health. Score 0–100. Click for full breakdown.' })
      : dimKey === 'commercial'
      ? t('portfolio_tooltip_health_commercial', { defaultValue: 'Is this client profitable and retained? MRR, renewal, satisfaction. Score 0–100. Click for full breakdown.' })
      : t('portfolio_tooltip_health_brand_equity', { defaultValue: 'Are we building the right thing? Consistency, quality, relevance. Score 0–100. Click for full breakdown.' });

  return (
    <div ref={ref} className="relative inline-flex">
      <Tooltip content={!open ? pillTooltip : undefined} side="top" delayDuration={300}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold transition-all cursor-pointer tabular-nums ${pillClass} hover:ring-current border border-transparent outline-none focus-visible:border-primary-600`}
          aria-label={`${displayLabel}: ${dimension.score}`}
          aria-expanded={open}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${dimTier.dotColor} shrink-0`} />
          {dimension.score}
        </button>
      </Tooltip>

      {open && (
        <div
          className="absolute top-full left-0 mt-1.5 z-50 w-60 bg-white rounded-xl shadow-lg border border-gray-100 p-3 space-y-2"
          role="dialog"
          aria-label={t('portfolio_health_breakdown_aria', { label: displayLabel, defaultValue: '{{label}} breakdown' })}
        >
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-gray-700">{displayLabel}</p>
            {/* Info icon - opens GlossaryTooltip for the dimension definition */}
            <GlossaryTooltip
              termId={
                dimKey === 'operational'
                  ? 'health-operational'
                  : dimKey === 'brand_equity'
                  ? 'health-brand'
                  : 'health-commercial'
              }
              side="right"
              frameShape="pill"
            >
              <button
                type="button"
                className="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-300 hover:text-gray-500 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
                aria-label={t('portfolio_health_learn_more_aria', { label: displayLabel, defaultValue: 'Learn more about {{label}}' })}
                onClick={(e) => e.stopPropagation()}
              >
                <i className="hm hm-info" style={{ fontSize: 13 }} aria-hidden="true" />
              </button>
            </GlossaryTooltip>
          </div>
          {dimension.signals.map((sig) => {
            const sigTier = scoreToTier(sig.score);
            return (
              <div key={sig.label}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs text-gray-600">{healthSignalLabel(t, sig)}</span>
                  <span className={`text-xs font-semibold ${sigTier.pillText}`}>{sig.score}</span>
                </div>
                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${sigTier.dotColor}`}
                    style={{ width: `${sig.score}%` }}
                  />
                </div>
              </div>
            );
          })}
          <div className="pt-1 border-t border-gray-100 mt-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700">{t('portfolio_health_overall')}</span>
              <span className={`text-xs font-bold ${dimTier.pillText}`}>{dimension.score}</span>
            </div>
          </div>

          {/* Mini sparkline - 8-week history */}
          {weeklyScoreHistory && weeklyScoreHistory.length >= 2 && (
            <div className="pt-2 border-t border-gray-100">
              <p className="text-[10px] text-gray-400 mb-1">{t('portfolio_health_trend_8w')}</p>
              <SparklineMini
                data={weeklyScoreHistory}
                width={120}
                height={28}
                strokeClassName={getSparklineClass(weeklyScoreHistory)}
              />
            </div>
          )}

          <button
            type="button"
            className="sr-only"
            onClick={() => setOpen(false)}
          >
            {t('close')}
          </button>
        </div>
      )}
    </div>
  );
};

/** Owner avatar circle with hover tooltip */
const OwnerAvatar = ({
  initials,
  name,
  color,
  profilePhotoUrl,
  onClick,
}: {
  initials: string;
  name: string;
  color: string;
  profilePhotoUrl?: string | null;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { t } = useTranslation('agency');
  return (
    <Tooltip content={t('portfolio_tooltip_owner_avatar', { defaultValue: 'Brand lead: {{name}}. Click to filter to their brands.', name })} delayDuration={300}>
      <button
        type="button"
        onClick={onClick}
        className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[9px] font-bold leading-none tracking-[0.02em] shrink-0 ring-1 ring-gray-200 transition-transform hover:scale-110 border border-transparent focus:outline-none focus-visible:border-primary-600 overflow-hidden ${profilePhotoUrl ? '' : `${color} ${avatarTextClass(color)}`}`}
        aria-label={t('portfolio_owner_filter_aria', { name })}
      >
        {profilePhotoUrl ? (
          <img
            src={profilePhotoUrl}
            alt=""
            className="w-full h-full object-cover rounded-full"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <span className="translate-y-px">{initials}</span>
        )}
      </button>
    </Tooltip>
  );
};

/**
 * Client avatar - renders the team's custom icon + color when set,
 * otherwise falls back to the first-letter initial on the auto-assigned
 * palette color. Matches how the workspace edit UI preserves branding.
 */
const ClientAvatar = ({
  initial,
  color,
  size = 'md',
  icon = null,
  customColor = null,
  logoUrl = null,
  name,
}: {
  initial: string;
  color: string;
  size?: 'sm' | 'md';
  /** Custom icon slug (happy-icons) from team.icon */
  icon?: string | null;
  /** Custom hex color (e.g. "#FF5733") from team.color */
  customColor?: string | null;
  /** Uploaded workspace logo URL (takes priority over icon/initial) */
  logoUrl?: string | null;
  /** Brand name — used as the logo's alt text */
  name?: string;
}) => {
  const { t } = useTranslation('agency');
  const sizeClass = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';
  const iconSize = size === 'sm' ? 14 : 16;

  // Uploaded logo takes top priority — matches WorkspaceCard behaviour
  if (logoUrl) {
    return (
      <div
        className={`${sizeClass} rounded-lg overflow-hidden bg-white border border-gray-100 shrink-0 inline-flex items-center justify-center`}
      >
        <img
          src={logoUrl}
          alt={name ?? t('portfolio_brand_logo_alt')}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  // Then custom icon + color (matches workspace edit UI)
  if (icon) {
    const contrastClass = customColor ? getContrastTextColor(customColor) : 'text-white';
    return (
      <div
        className={`${sizeClass} rounded-lg inline-flex items-center justify-center shrink-0 ${contrastClass}`}
        style={{ backgroundColor: customColor ?? undefined }}
        aria-hidden="true"
      >
        <i className={`hm hm-${icon}`} style={{ fontSize: iconSize }} aria-hidden="true" />
      </div>
    );
  }

  // Fallback: initial on auto-assigned palette color
  const initialContrastClass = customColor ? getContrastTextColor(customColor) : avatarTextClass(color);
  return (
    <div
      className={`${sizeClass} text-sm rounded-lg inline-flex items-center justify-center font-bold leading-none tracking-[0.02em] shrink-0 ${color} ${initialContrastClass}`}
      style={customColor ? { backgroundColor: customColor } : undefined}
      aria-hidden="true"
    >
      <span className="translate-y-px">{initial}</span>
    </div>
  );
};

/** Small shield badge indicating this brand has Carte Blanche enabled
 * (posts publish without approval gate). Rendered next to the brand name
 * wherever client cards appear. */
const CarteBlancheBadge = () => {
  const { t } = useTranslation('agency');
  return (
    <Tooltip
      content={t('carte_blanche_badge_tooltip', {
        defaultValue: 'Carte Blanche — posts are released automatically, without review.',
      })}
      side="top"
      delayDuration={300}
    >
      <span
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-soft-mint text-dark-teal text-[9px] font-semibold leading-none shrink-0 cursor-default"
        aria-label={t('brand_settings_carte_blanche_on_toast')}
      >
        <i className="hm hm-shield" style={{ fontSize: 9 }} aria-hidden="true" />
        <span className="uppercase tracking-wider">CB</span>
      </span>
    </Tooltip>
  );
};

/**
 * Status update excerpt with hover popover for full text. Clicking the cell
 * opens the StatusUpdateHistoryModal for that brand so the user can scroll
 * through prior weeks' updates without leaving the view they're in.
 */
const StatusExcerpt = ({
  update,
  formatRelativeDate,
  onClick,
}: {
  update: NonNullable<PortfolioClient['latestStatusUpdate']>;
  formatRelativeDate: (date: string) => string;
  onClick?: () => void;
}) => {
  const { t } = useTranslation('agency');
  const truncated = update.tldr.length > 42 ? update.tldr.slice(0, 42) + '…' : update.tldr;

  const popoverContent = (
    <div className="flex items-start gap-2">
      <span className="text-2xl leading-none shrink-0" aria-hidden="true">{update.vibe}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 leading-snug">{update.tldr}</p>
        <p className="text-xs text-gray-400 mt-2">
          {update.authorName} · {formatRelativeDate(update.createdAt)}
        </p>
        {onClick && (
          <p className="text-[10px] text-primary-600 mt-2 font-medium uppercase tracking-wide">
            {t('portfolio_status_excerpt_cta')}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <TooltipRoot delayDuration={150}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className="flex items-start gap-1.5 text-left border border-transparent focus:outline-none focus-visible:border-primary-600 rounded hover:bg-gray-50 -mx-1 px-1 py-0.5 transition-colors"
          aria-label={t('portfolio_status_excerpt_aria', { tldr: update.tldr })}
        >
          <span className="text-xl leading-none shrink-0 mt-0.5" aria-hidden="true">{update.vibe}</span>
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <span className="text-xs text-gray-700 leading-snug line-clamp-2 text-left">{truncated}</span>
            <span className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium leading-none">
              {update.authorName} · {formatRelativeDate(update.createdAt)}
            </span>
          </div>
        </button>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent
          side="top"
          align="start"
          sideOffset={8}
          avoidCollisions
          collisionPadding={12}
          className="z-[120] w-72 bg-white rounded-xl shadow-xl border border-gray-100 p-4 text-left"
        >
          {popoverContent}
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  );
};

/** Draft count pill (formerly "Pending").
 * For Carte Blanche teams the entire count is drafts in library.
 * For approval-gated teams the tooltip also surfaces the awaiting-approval sub-count. */
const PendingBadge = ({
  count,
  carteBlanche = false,
  awaitingApproval,
}: {
  count: number;
  carteBlanche?: boolean;
  awaitingApproval?: number;
}) => {
  const { t } = useTranslation('agency');
  if (count === 0) return <span className="text-xs text-gray-400 tabular-nums">-</span>;

  const tooltipText = carteBlanche
    ? t('portfolio_tooltip_drafts_cb', { defaultValue_one: '{{count}} draft in library', defaultValue_other: '{{count}} drafts in library', count })
    : t('portfolio_tooltip_drafts_approval', {
        defaultValue_one: '{{count}} draft — {{awaiting}} awaiting review',
        defaultValue_other: '{{count}} drafts — {{awaiting}} awaiting review',
        count,
        awaiting: awaitingApproval ?? 0,
      });

  return (
    <Tooltip content={tooltipText} side="top" delayDuration={300}>
      <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-xs font-bold leading-none tabular-nums bg-peach/60 text-coral-700 cursor-default">
        {count}
      </span>
    </Tooltip>
  );
};

/** Skeleton row for loading state */
const SkeletonRow = () => (
  <div className="flex items-center gap-4 py-3 px-4 border-b border-gray-100 animate-pulse">
    <div className="w-8 h-8 rounded-lg bg-gray-200 shrink-0" />
    <div className="flex-1 space-y-1.5">
      <div className="h-3 bg-gray-200 rounded w-32" />
      <div className="h-2.5 bg-gray-100 rounded w-20" />
    </div>
    <div className="w-6 h-6 rounded-full bg-gray-200 shrink-0" />
    <div className="flex gap-1.5">
      {[0, 1, 2].map((i) => <div key={i} className="w-10 h-5 rounded-full bg-gray-100" />)}
    </div>
    <div className="w-24 h-3 rounded bg-gray-100" />
    <div className="w-40 h-3 rounded bg-gray-100" />
    <div className="w-6 h-5 rounded-full bg-gray-100" />
  </div>
);

// ── Asset Inventory helpers ───────────────────────────────────────────────────

interface AssetStageConfig {
  key: keyof AssetInventory;
  termId: string;
  labelDefault: string;
  dotClass: string;
  labelLocaleKey: string;
}

const ASSET_STAGES: AssetStageConfig[] = [
  {
    key: 'draftsPolishing',
    termId: 'asset-stage-drafts',
    labelLocaleKey: 'portfolio_inventory_stage_drafts',
    labelDefault: 'Drafts polishing',
    dotClass: 'bg-peach',
  },
  {
    key: 'readyToPublish',
    termId: 'asset-stage-ready',
    labelLocaleKey: 'portfolio_inventory_stage_ready',
    labelDefault: 'Ready to publish',
    dotClass: 'bg-primary-500',
  },
  {
    key: 'scheduled',
    termId: 'asset-stage-scheduled',
    labelLocaleKey: 'portfolio_inventory_stage_scheduled',
    labelDefault: 'Scheduled',
    dotClass: 'bg-secondary-500',
  },
  {
    key: 'published',
    termId: 'asset-stage-published',
    labelLocaleKey: 'portfolio_inventory_stage_published',
    labelDefault: 'Published',
    dotClass: 'bg-soft-mint',
  },
  {
    key: 'remixable',
    termId: 'asset-stage-remixable',
    labelLocaleKey: 'portfolio_inventory_stage_remixable',
    labelDefault: 'Remixable',
    dotClass: 'bg-coral-400',
  },
];

// ── Asset Inventory health-zone gauge ────────────────────────────────────────

type HealthZone = 'green' | 'amber' | 'red';

interface StageGauge {
  fillPct: number;
  zone: HealthZone;
  hideBar?: boolean;
}

function getStageGauge(
  key: keyof AssetInventory,
  count: number,
  cadenceWeekly: number,
): StageGauge {
  const cadence = Math.max(1, cadenceWeekly || 3);

  switch (key) {
    case 'draftsPolishing': {
      if (count >= cadence * 2) return { fillPct: 100, zone: 'red' };
      if (count >= cadence)     return { fillPct: Math.min(100, (count / (cadence * 2)) * 100), zone: 'amber' };
      return { fillPct: (count / cadence) * 100, zone: 'green' };
    }
    case 'readyToPublish': {
      if (count === 0) return { fillPct: 0, zone: 'red' };
      const target = cadence;
      if (count > target * 1.5) return { fillPct: 100, zone: 'amber' };
      return { fillPct: Math.min(100, (count / target) * 100), zone: 'green' };
    }
    case 'scheduled': {
      // This count includes pending posts at any future date. The dated RunwayBar
      // above owns coverage; a count-based green gauge would overstate it.
      return { fillPct: 0, zone: 'green', hideBar: true };
    }
    case 'published':
      return { fillPct: 0, zone: 'green', hideBar: true };
    case 'remixable': {
      if (count >= 5) return { fillPct: 100, zone: 'green' };
      if (count >= 1) return { fillPct: (count / 5) * 100, zone: 'amber' };
      return { fillPct: 0, zone: 'green' };
    }
    default:
      return { fillPct: 0, zone: 'green' };
  }
}

const ZONE_BG: Record<HealthZone, string> = {
  green: 'bg-primary-500',
  amber: 'bg-peach',
  red:   'bg-coral-500',
};

/** Asset Inventory drawer panel */
const AssetInventoryPanel = ({
  client,
  locale,
  t,
  onPlanBatch,
}: {
  client: PortfolioClient;
  locale: string;
  t: ReturnType<typeof useTranslation<'agency'>>['t'];
  onPlanBatch: () => void;
}) => {
  const { isLocked } = usePortfolioLocks();
  const openUpsellFromDrawer = useGatingStore((state) => state.openUpsell);
  const inv = client.assetInventory;
  const total = inv.draftsPolishing + inv.readyToPublish + inv.scheduled + inv.published + inv.remixable;
  const lowPipeline = isLowPipeline(client);
  const runsDryDate = formatRunsDryOn(client.runsDryOn, locale);

  // Stage row commentary
  const getCommentary = (key: keyof typeof inv): string | null => {
    if (key === 'draftsPolishing' && inv.draftsPolishing >= 5) {
      return t('portfolio_inventory_commentary_drafts_pile', { defaultValue: 'Drafts piling up — schedule or polish' });
    }
    if (key === 'readyToPublish' && inv.readyToPublish > 0) {
      return t('portfolio_inventory_commentary_ready_to_ship', { defaultValue: 'Ready to ship' });
    }
    if (key === 'readyToPublish' && inv.readyToPublish === 0 && inv.draftsPolishing > 0) {
      return t('portfolio_inventory_commentary_polish_drafts', { defaultValue: 'Polish drafts to fill' });
    }
    // Inventory's scheduled count has no date window. Far-future posts cannot
    // establish near-term runway; that belongs to the dated Runway section.
    if (key === 'scheduled' && inv.scheduled === 0 && inv.readyToPublish + inv.draftsPolishing > 0) {
      return t('portfolio_inventory_commentary_schedule_next_batch', { defaultValue: 'Schedule next batch' });
    }
    if (key === 'scheduled' && inv.scheduled === 0 && client.healthOverall !== 'paused') {
      return t('portfolio_inventory_commentary_empty_calendar', { defaultValue: 'Empty calendar' });
    }
    if (key === 'remixable' && inv.remixable >= 5) {
      return t('portfolio_inventory_commentary_remix_ready', { defaultValue: 'Remix opportunity' });
    }
    return null;
  };

  return (
    <div className="px-6 pt-6 pb-6 border-t border-gray-900/8">
      {/* Overline + rule */}
      <div className="flex items-center gap-1.5 mb-4">
        <GlossaryTooltip termId="metric-asset-inventory" side="right">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium cursor-default">
            {t('portfolio_drawer_section_inventory', { defaultValue: 'Asset Inventory' })}
            <i className="hm hm-info" style={{ fontSize: 10, opacity: 0.5 }} aria-hidden="true" />
          </span>
        </GlossaryTooltip>
      </div>

      {/* Low pipeline: less than 2 weeks of planned + ready content (server SSOT) */}
      {lowPipeline && !isLocked('portfolio_runway') && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 ${
            client.coverDays <= 0 ? 'border-coral-200 bg-coral-50' : 'border-peach bg-peach/30'
          }`}
          role="alert"
        >
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold mb-1 text-coral-700">
            {t('low_pipeline_short', { defaultValue: 'Low pipeline' })}
          </p>
          <p className="text-xs leading-snug text-coral-800">
            {t('low_pipeline_tooltip', {
              defaultValue: 'Less than 2 weeks of planned and ready content. Runs dry around {{date}}.',
              date: runsDryDate,
            })}
          </p>
          <button
            type="button"
            onClick={() =>
              isLocked('portfolio_plan_batch')
                ? openUpsellFromDrawer({ feature: 'portfolio_plan_batch', upsellPlanSlug: null }, true)
                : onPlanBatch()
            }
            {...(isLocked('portfolio_plan_batch')
              ? {
                  'data-testid': 'locked-entry-portfolio_plan_batch',
                  'aria-label': t('common:gating_locked_label', {
                    feature: t('fab_plan_batch', { defaultValue: 'Plan a content batch' }),
                  }),
                }
              : {})}
            className="mt-2 inline-flex min-h-8 items-center gap-1.5 rounded-md border border-coral-300 px-2.5 py-1 text-xs font-semibold text-coral-800 hover:bg-coral-100 focus-visible:border-coral-500 focus-visible:outline-none"
          >
            {t('fab_plan_batch', { defaultValue: 'Plan a content batch' })}
            {isLocked('portfolio_plan_batch') && <LockBadge />}
          </button>
        </div>
      )}

      {/* Hero total count */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <span className="font-wagner text-5xl leading-none tabular-nums tracking-tight text-gray-900">
            {total}
          </span>
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium mt-1">
            {t('portfolio_inventory_total_label', { defaultValue: 'Total assets' })}
          </p>
        </div>
      </div>

      {/* Stage breakdown */}
      <div className="space-y-4">
        {ASSET_STAGES.map((stage) => {
          const count = inv[stage.key];
          const gauge = getStageGauge(stage.key, count, client.cadenceWeekly);
          const commentary = getCommentary(stage.key);

          return (
            <div key={stage.key} className="space-y-1.5">
              {/* Row: dot + label + count */}
              <div className="flex items-center justify-between">
                <GlossaryTooltip termId={stage.termId} side="left">
                  <span className="inline-flex items-center gap-2 cursor-default">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${stage.dotClass}`}
                      aria-hidden="true"
                    />
                    <span className="text-[10px] uppercase tracking-[0.15em] text-gray-600 font-medium">
                      {t(stage.labelLocaleKey, { defaultValue: stage.labelDefault })}
                    </span>
                  </span>
                </GlossaryTooltip>
                <span className="font-wagner text-2xl leading-none tabular-nums text-gray-900">
                  {count}
                </span>
              </div>

              {/* Progress bar */}
              {!gauge.hideBar && !isLocked('portfolio_cadence') && (
                <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${ZONE_BG[gauge.zone]}`}
                    style={{ width: `${gauge.fillPct}%`, opacity: 0.85 }}
                  />
                </div>
              )}

              {/* Commentary caption + optional inline mapped chip — single line keeps
                  row heights symmetric across the inventory list. */}
              <p className="text-[10px] text-gray-400 leading-none min-h-[12px]">
                {commentary ?? '\u00A0'}
                {stage.key === 'scheduled' && (client.calendarMappedCount ?? 0) > 0 && (
                  <>
                    {commentary && <span className="mx-1 text-gray-300">·</span>}
                    <span className="text-gray-500 font-medium tabular-nums">
                      +{client.calendarMappedCount} {t('portfolio_inventory_mapped_suffix', { defaultValue: 'mapped on calendar' })}
                    </span>
                  </>
                )}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Brand Settings Panel ──────────────────────────────────────────────────────

/** Inline confirmation toast shown inside the drawer (delete, save, etc.) */
const DrawerToast = ({ message, visible }: { message: string; visible: boolean }) => (
  <div
    aria-live="polite"
    aria-atomic="true"
    className={`fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+18px+var(--floating-stack-offset))] lg:bottom-24 left-1/2 -translate-x-1/2 z-[200] transition-all duration-300 pointer-events-none
      ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
  >
    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gray-900 text-white text-xs font-medium shadow-lg">
      <i className="hm hm-tick" style={{ fontSize: 11 }} aria-hidden="true" />
      {message}
    </span>
  </div>
);

/**
 * Latest Status Update section in the brand overview drawer.
 * Renders all fields the user filled in via the compose modal so the weekly
 * pulse is actually surfaced back: TL;DR + vibe + author/date, output (n/N
 * posts), top performer, plus the four narrative blocks (wins / blockers /
 * next focus / strategic note) when populated.
 */
const StatusUpdateDrawerSection = ({
  update,
  t,
  formatRelativeDate,
  onViewHistory,
}: {
  update: NonNullable<PortfolioClient['latestStatusUpdate']>;
  t: ReturnType<typeof useTranslation<'agency'>>['t'];
  formatRelativeDate: (iso: string) => string;
  /** Optional: render "View all updates" link that opens the history modal */
  onViewHistory?: () => void;
}) => {
  const hasOutput = (update.postsPublished ?? 0) > 0 || (update.postsPlanned ?? 0) > 0;
  const narrative: { key: string; label: string; value: string | null | undefined }[] = [
    {
      key: 'wins',
      label: t('portfolio_drawer_status_wins', { defaultValue: 'Wins' }),
      value: update.wins,
    },
    {
      key: 'blockers',
      label: t('portfolio_drawer_status_blockers', { defaultValue: 'Blockers' }),
      value: update.blockers,
    },
    {
      key: 'nextFocus',
      label: t('portfolio_drawer_status_next_focus', { defaultValue: 'Next focus' }),
      value: update.nextFocus,
    },
    {
      key: 'strategicNote',
      label: t('portfolio_drawer_status_strategic', { defaultValue: 'Strategic note' }),
      value: update.strategicNote,
    },
  ];

  return (
    <div className="px-6 py-5 border-b border-gray-900/8">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium">
          {t('portfolio_drawer_section_status', { defaultValue: 'Latest Status Update' })}
        </p>
        {onViewHistory && (
          <button
            type="button"
            onClick={onViewHistory}
            className="text-[11px] text-primary-600 font-medium hover:text-primary-700 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600 rounded"
          >
            {t('portfolio_drawer_view_status_history', { defaultValue: 'View history →' })}
          </button>
        )}
      </div>

      {/* Vibe + TL;DR inline */}
      <div className="flex items-start gap-3 mb-3">
        <span className="text-3xl leading-none shrink-0 mt-0.5" aria-hidden="true">
          {update.vibe}
        </span>
        {update.tldr.length <= 80 ? (
          <p className="font-wagner text-xl leading-snug text-gray-900">{update.tldr}</p>
        ) : (
          <p className="text-sm italic text-gray-700 leading-relaxed">{update.tldr}</p>
        )}
      </div>

      {/* Output: n / N posts */}
      {hasOutput && (
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-baseline gap-1 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
            <span className="text-sm font-bold tabular-nums">{update.postsPublished ?? 0}</span>
            <span className="text-gray-400">/</span>
            <span className="text-sm font-bold tabular-nums">{update.postsPlanned ?? 0}</span>
            <span className="ml-0.5 text-[10px] uppercase tracking-wide text-gray-500">
              {t('portfolio_drawer_status_output_posts', { defaultValue: 'posts' })}
            </span>
          </span>
        </div>
      )}

      {/* Top performer */}
      {update.topPost && (
        <div className="flex items-center gap-2.5 mb-3 p-2 rounded-lg bg-gray-50 border border-gray-100">
          {update.topPost.thumbnailUrl ? (
            <img
              src={update.topPost.thumbnailUrl}
              alt=""
              className="w-10 h-10 rounded-md object-cover shrink-0 ring-1 ring-gray-200"
              aria-hidden="true"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-md bg-gradient-to-br from-primary-400 to-primary-600 shrink-0 flex items-center justify-center"
              aria-hidden="true"
            >
              <i className="hm hm-star text-white" style={{ fontSize: 16 }} aria-hidden="true" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium leading-none mb-0.5">
              {t('portfolio_drawer_status_top_performer', { defaultValue: 'Top performer' })}
            </p>
            <p className="text-xs text-gray-700 leading-snug line-clamp-2">{update.topPost.caption}</p>
          </div>
        </div>
      )}

      {/* Narrative blocks */}
      {narrative.some((n) => n.value && n.value.trim()) && (
        <div className="space-y-2.5 mb-3">
          {narrative
            .filter((n) => n.value && n.value.trim())
            .map((n) => (
              <div key={n.key}>
                <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium mb-0.5">
                  {n.label}
                </p>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{n.value}</p>
              </div>
            ))}
        </div>
      )}

      {/* Author + date */}
      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
        {update.authorName}
        {' · '}
        {formatRelativeDate(update.createdAt)}
      </p>
    </div>
  );
};

/** Brand settings section rendered inside MockDrawerOverlay */
const BrandSettingsPanel = ({
  client,
  t,
  onEnterWorkspace,
  onEditBrand,
}: {
  client: PortfolioClient;
  t: ReturnType<typeof useTranslation<'agency'>>['t'];
  /** Enter God Mode then navigate to a specific route inside the client workspace */
  onEnterWorkspace: (destination: string) => void;
  /** Open the in-place Edit Brand modal (name / logo / icon / color / active) */
  onEditBrand: () => void;
}) => {
  const queryClient = useQueryClient();
  const { teamId: effectiveTeamId } = useEffectiveTeamId();
  const { refreshUser } = useAuth();
  const { canManageAgencyGovernance } = useAgencyGovernance();
  const canChangeCarteBlanche = canManageAgencyGovernance;

  // Fetch live Carte Blanche setting for this team
  const { data: carteBlancheData } = useQuery({
    queryKey: ['agency', 'workspace', client.id, 'carte-blanche'],
    queryFn: () => agencyService.getWorkspaceCarteBlanche(client.id),
    staleTime: 30_000,
  });
  const carteBlancheOn = carteBlancheData?.carteBlanche ?? false;

  const [toast, setToast] = useState<{ message: string; visible: boolean }>({ message: '', visible: false });

  const showToast = (message: string) => {
    setToast({ message, visible: true });
    setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 2400);
  };

  const { isLocked } = usePortfolioLocks();

  const carteBlancheMutation = useMutation({
    mutationFn: (next: boolean) => agencyService.updateWorkspaceCarteBlanche(client.id, next),
    onSuccess: (_, next) => {
      void queryClient.invalidateQueries({ queryKey: ['agency', 'workspace', client.id, 'carte-blanche'] });
      // Carte blanche flips approval gating for this client's whole pipeline
      invalidateReleaseState(queryClient, { teamId: effectiveTeamId, alsoTeamIds: [client.id] });
      // Other /me consumers (creator / variation restrictions) must not keep the old carte-blanche value
      void refreshUser();
      showToast(
        next
          ? t('brand_settings_carte_blanche_on_toast', { defaultValue: 'Carte Blanche enabled' })
          : t('brand_settings_carte_blanche_off_toast', { defaultValue: 'Carte Blanche disabled' }),
      );
    },
    onError: (err: unknown) => {
      // Server is the source of truth (PLAN 2.7: admin-only, reviewer required to switch off).
      // Message is already translated server-side; refetch so the toggle reflects server state.
      void queryClient.invalidateQueries({ queryKey: ['agency', 'workspace', client.id, 'carte-blanche'] });
      invalidateReleaseState(queryClient, { teamId: effectiveTeamId, alsoTeamIds: [client.id] });
      void refreshUser();
      showToast(
        t('brand_settings_carte_blanche_error', {
          defaultValue: 'Could not update: {{msg}}',
          msg: formatApiError(err).message,
        }),
      );
    },
  });

  // Satisfaction signal: agency lead's qualitative read on the client. Drives
  // the commercial-health bonus on the backend, persisted to team.satisfactionSignal.
  const satisfactionMutation = useMutation({
    mutationFn: (next: 'STRONG' | 'NEUTRAL' | 'TENSE' | null) =>
      agencyService.updateWorkspace(client.id, { satisfactionSignal: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agency', 'portfolio'] });
      showToast(
        t('brand_settings_satisfaction_saved', { defaultValue: 'Satisfaction updated' }),
      );
    },
    onError: (err: Error) => {
      showToast(
        t('brand_settings_satisfaction_error', {
          defaultValue: 'Could not update: {{msg}}',
          msg: err.message,
        }),
      );
    },
  });

  const handleCarteBlancheToggle = () => {
    if (!canChangeCarteBlanche || carteBlancheMutation.isPending) return;
    carteBlancheMutation.mutate(!carteBlancheOn);
  };

  // Channel health locked: keep the connected count, drop the expiring / broken readings
  const channelHealthLocked = isLocked('portfolio_channel_health');
  const hasChannelIssue = !channelHealthLocked && (client.channelHealth.broken > 0 || client.channelHealth.expiring > 0);

  const channelSummary = (() => {
    const parts: string[] = [];
    parts.push(
      t('brand_settings_channels_total', {
        defaultValue: '{{count}} Connected',
        count: client.channelHealth.total,
      })
    );
    if (!channelHealthLocked && client.channelHealth.expiring > 0) {
      parts.push(
        t('brand_settings_channels_expiring', {
          defaultValue: '{{count}} Expiring',
          count: client.channelHealth.expiring,
        })
      );
    }
    if (!channelHealthLocked && client.channelHealth.broken > 0) {
      parts.push(
        t('brand_settings_channels_broken', {
          defaultValue: '{{count}} Broken',
          count: client.channelHealth.broken,
        })
      );
    }
    return parts.join(' · ');
  })();

  const lifecycleLabel =
    client.lifecycleStage === 'active'
      ? t('portfolio_lifecycle_active', { defaultValue: 'Active' })
      : client.lifecycleStage === 'onboarding'
      ? t('portfolio_lifecycle_onboarding', { defaultValue: 'Onboarding' })
      : client.lifecycleStage === 'churning'
      ? t('portfolio_lifecycle_churning', { defaultValue: 'Deprioritizing' })
      : t('portfolio_lifecycle_paused', { defaultValue: 'Paused' });

  return (
    <>
      <DrawerToast message={toast.message} visible={toast.visible} />

      <div className="px-6 pt-6 pb-6 border-t border-gray-900/8">
        {/* Section overline */}
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-medium">
            {t('brand_settings_overline', { defaultValue: 'Brand Settings' })}
          </p>
        </div>

        <div className="mb-4" />

        <div className="space-y-0 divide-y divide-gray-900/6">

          {/* ── Row 1: Carte Blanche ── */}
          {isLocked('release_carte_blanche') && (
            <div className="py-3">
              <LockedTeaserRow feature="release_carte_blanche" icon={ICONS.UNLOCK} className="rounded-lg" />
            </div>
          )}
          {!isLocked('release_carte_blanche') && (
          <div className="flex items-center justify-between py-3 gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-semibold leading-none mb-0.5">
                {t('brand_settings_carte_blanche_label', { defaultValue: 'Carte Blanche' })}
              </p>
              <p className="text-[11px] text-gray-400 leading-snug">
                {t('brand_settings_carte_blanche_caption', { defaultValue: 'Skip the release workflow. Brand lead publishes directly.' })}
              </p>
            </div>
            {/* Toggle button */}
            <Tooltip
              disabled={canChangeCarteBlanche}
              content={t('carte_blanche_admin_only_hint', {
                defaultValue: 'Only the brand portfolio owner or an admin can change this',
              })}
            >
              <span>
                <Toggle
                  checked={carteBlancheOn}
                  onChange={handleCarteBlancheToggle}
                  disabled={!canChangeCarteBlanche || carteBlancheMutation.isPending}
                  label={t('brand_settings_carte_blanche_aria', { defaultValue: 'Toggle Carte Blanche' })}
                />
              </span>
            </Tooltip>
          </div>
          )}

          {/* ── Row 2: Connected channels ── */}
          <div className="flex items-center justify-between py-3 gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-semibold leading-none mb-0.5">
                {t('brand_settings_channels_label', { defaultValue: 'Channels' })}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {hasChannelIssue && (
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full shrink-0 bg-coral-400"
                    aria-hidden="true"
                  />
                )}
                <p className="text-[11px] text-gray-400 leading-snug">{channelSummary}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onEnterWorkspace('/settings/channels')}
              className="shrink-0 text-[11px] text-primary-600 font-medium hover:text-primary-700 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600 rounded"
            >
              {t('brand_settings_manage_link', { defaultValue: 'Manage →' })}
            </button>
          </div>

          {/* ── Row 3: Brand info ── */}
          <div className="flex items-center justify-between py-3 gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-semibold leading-none mb-0.5">
                {t('brand_settings_brand_info_label', { defaultValue: 'Brand Info' })}
              </p>
              <p className="text-[11px] text-gray-400 leading-snug truncate">
                {client.name}
                <span className="text-gray-300 mx-1" aria-hidden="true">·</span>
                {lifecycleLabel}
              </p>
            </div>
            <button
              type="button"
              onClick={onEditBrand}
              className="shrink-0 text-[11px] text-primary-600 font-medium hover:text-primary-700 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600 rounded"
            >
              {t('brand_settings_edit_link', { defaultValue: 'Edit →' })}
            </button>
          </div>

          {/* ── Row 4: Satisfaction signal ───────────────────────────
               Agency lead's qualitative read. Persists to team.satisfactionSignal,
               which feeds into the commercial-health bonus calculation. */}
          {isLocked('portfolio_sentiment') && (
            <div className="py-3">
              <LockedTeaserRow feature="portfolio_sentiment" icon={ICONS.HEART} className="rounded-lg" />
            </div>
          )}
          {!isLocked('portfolio_sentiment') && (
          <div className="flex items-center justify-between py-3 gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-semibold leading-none mb-0.5">
                {t('brand_settings_satisfaction_label', { defaultValue: 'Client satisfaction' })}
              </p>
              <p className="text-[11px] text-gray-400 leading-snug">
                {t('brand_settings_satisfaction_caption', {
                  defaultValue: 'Your gut read on the relationship. Influences health.',
                })}
              </p>
            </div>
            <div
              className="flex items-center gap-1 shrink-0"
              role="radiogroup"
              aria-label={t('brand_settings_satisfaction_label', { defaultValue: 'Client satisfaction' })}
            >
              {([
                { value: 'STRONG' as const, emoji: '😀', label: t('satisfaction_strong', { defaultValue: 'Strong' }) },
                { value: 'NEUTRAL' as const, emoji: '😐', label: t('satisfaction_neutral', { defaultValue: 'Neutral' }) },
                { value: 'TENSE' as const, emoji: '😬', label: t('satisfaction_tense', { defaultValue: 'Tense' }) },
              ]).map((opt) => {
                const isSelected = (client.satisfactionSignal ?? null) === opt.value;
                return (
                  <Tooltip key={opt.value} content={opt.label} side="top" delayDuration={300}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-label={opt.label}
                      disabled={satisfactionMutation.isPending}
                      onClick={() =>
                        satisfactionMutation.mutate(isSelected ? null : opt.value)
                      }
                      className={`w-8 h-8 rounded-md inline-flex items-center justify-center text-base transition-all border border-transparent outline-none focus-visible:border-primary-600 ${
                        isSelected
                          ? 'bg-primary-50 ring-1 ring-primary-300'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <span aria-hidden="true">{opt.emoji}</span>
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>
          )}

          {/* ── Danger zone: delete workspace ── */}
          {canManageAgencyGovernance && <DeleteBrandRow client={client} t={t} />}

        </div>
      </div>
    </>
  );
};

/** Danger-zone delete row - shown at the bottom of Brand Settings. */
const DeleteBrandRow = ({
  client,
  t,
}: {
  client: PortfolioClient;
  t: ReturnType<typeof useTranslation<'agency'>>['t'];
}) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: () => agencyService.deleteWorkspace(client.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agency', 'portfolio'] });
      showToast(t('brand_settings_deleted_toast', {
        defaultValue: '{{name}} deleted',
        name: client.name,
      }), 'success');
      setDialogOpen(false);
    },
    onError: (err: Error) => {
      showToast(t('brand_settings_delete_error', {
        defaultValue: 'Delete failed: {{msg}}',
        msg: err.message,
      }), 'error');
    },
  });

  return (
    <>
      <div className="flex items-center justify-between py-3 gap-3 border-t border-coral-100 mt-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] text-coral-600 font-semibold leading-none mb-0.5">
            {t('brand_settings_danger_label', { defaultValue: 'Delete brand' })}
          </p>
          <p className="text-[11px] text-gray-400 leading-snug">
            {t('brand_settings_danger_caption', {
              defaultValue: 'This removes the brand from your portfolio and cancels its scheduled posts. To restore it later, contact support.',
            })}
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 text-[11px] text-coral-600 font-medium hover:text-coral-700 transition-colors border border-transparent focus:outline-none focus-visible:border-coral-500 rounded"
          onClick={() => setDialogOpen(true)}
        >
          {t('brand_settings_danger_delete', { defaultValue: 'Delete →' })}
        </button>
      </div>
      <ConfirmDialog
        open={dialogOpen}
        onOpenChange={(open) => !deleteMutation.isPending && setDialogOpen(open)}
        onConfirm={() => deleteMutation.mutate()}
        title={t('brand_settings_delete_title', {
          defaultValue: 'Delete {{name}}?',
          name: client.name,
        })}
        description={t('brand_settings_delete_desc', {
          name: client.name,
          defaultValue: "{{name}}'s scheduled posts will be cancelled and won't publish. The brand is removed from your portfolio. If you change your mind, contact support and we can restore it.",
        })}
        confirmText={t('brand_settings_danger_delete_confirm', { defaultValue: 'Delete brand' })}
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </>
  );
};

/** Brand overview drawer — full-detail panel for a selected workspace */
const MockDrawerOverlay = ({
  client,
  onClose,
  onShowStatusHistory,
  onPlanBatch,
}: {
  client: PortfolioClient;
  onClose: () => void;
  /** Optional: when set, the drawer's status section shows a "View history" link */
  onShowStatusHistory?: (client: PortfolioClient) => void;
  onPlanBatch: (client: PortfolioClient) => void;
}) => {
  const { t, i18n } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const { formatRelativeDate } = useTimezone();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const tier = TIER_CONFIG[client.healthOverall];
  const lifecycle = LIFECYCLE_CONFIG[client.lifecycleStage];
  const lifecycleTermId = LIFECYCLE_TERM_ID[client.lifecycleStage] ?? 'lifecycle-active';

  // Edit-brand modal state (per-drawer instance)
  const [editBrandOpen, setEditBrandOpen] = useState(false);

  // Animation state: starts false, flips to true after mount for enter animation;
  // flips back to false on close, then parent's onClose fires after the exit animation.
  const [visible, setVisible] = useState(false);

  // Enter God Mode mutation - impersonate the client workspace
  const enterGodModeMutation = useMutation({
    mutationKey: ['enter-god-mode'],
    mutationFn: () => agencyService.enterGodMode(client.id),
    onSuccess: async (data, _, __) => {
      setAccessToken(data.accessToken);
      await refreshUser();
      onClose();
      navigate('/');
    },
    onError: (err: Error) => {
      showToast(
        t('portfolio_enter_workspace_error', {
          defaultValue: 'Could not enter brand: {{msg}}',
          msg: err.message,
        }),
        'error',
      );
    },
  });

  // Navigate into God Mode then jump to a specific section
  const enterAndNavigate = useCallback(
    async (destination: string) => {
      try {
        const data = await enterGodModeMutation.mutateAsync();
        setAccessToken(data.accessToken);
        await refreshUser();
        onClose();
        navigate(destination);
      } catch {
        // error handled in onError
      }
    },
    [enterGodModeMutation, navigate, onClose, refreshUser],
  );

  useEffect(() => {
    // Next-frame flip to trigger CSS transition
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    // Wait for exit animation before unmounting
    window.setTimeout(() => onClose(), 260);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) handleClose();
    };
    document.addEventListener('keydown', handler);
    const unlockScroll = lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', handler);
      unlockScroll();
    };
  }, [handleClose]);

  // Helper: score → tier config
  const scoreToTier = (s: number): TierConfig => {
    if (s >= 80) return TIER_CONFIG.on_track;
    if (s >= 60) return TIER_CONFIG.watch;
    if (s >= 40) return TIER_CONFIG.at_risk;
    if (s > 0) return TIER_CONFIG.off_track;
    return TIER_CONFIG.paused;
  };

  // Has any risk signal worth surfacing
  const hasRiskChips =
    (!isLocked('portfolio_channel_health') && (client.channelHealth.broken > 0 || client.channelHealth.expiring > 0)) ||
    (!isLocked('portfolio_client_relationship') &&
      (client.renewal.atRisk ||
        (client.renewal.daysUntilRenewal !== null && client.renewal.daysUntilRenewal < 30))) ||
    (!isLocked('portfolio_velocity') && client.velocity.trendPct < -25) ||
    (!isLocked('portfolio_runway') && isLowPipeline(client));

  return (
    <div
      className="fixed left-0 right-0 top-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:bottom-0 z-50 flex items-end sm:items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={t('portfolio_drawer_aria', { name: client.name })}
    >
      {/* Backdrop - fades */}
      <div
        className={`absolute inset-0 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ease-out ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer panel - slides from right on desktop, up from bottom on mobile */}
      <div
        className={`relative bg-white w-full sm:w-[440px] h-full rounded-t-2xl sm:rounded-none shadow-2xl flex flex-col overflow-hidden
          transition-transform duration-300 ease-out
          ${visible
            ? 'translate-y-0 sm:translate-x-0'
            : 'translate-y-full sm:translate-y-0 sm:translate-x-full'
          }`}
      >

        {/* Mobile drag handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden shrink-0">
          <div className="w-8 h-1 rounded-full bg-gray-300" aria-hidden="true" />
        </div>

        {/* ── HEADER ────────────────────────────────────────────────── */}
        <div className="shrink-0 px-6 pt-5 pb-4 border-b border-gray-900/8">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="shrink-0 mt-1">
              <ClientAvatar initial={client.avatarInitial} color={client.avatarColor} icon={client.icon} customColor={client.customColor} logoUrl={client.logoUrl} name={client.name} size="md" />
            </div>

            {/* Name block */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium mb-1">
                {t('portfolio_drawer_overline', { defaultValue: 'Brand Overview' })}
              </p>
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="font-wagner text-3xl leading-tight tracking-tight text-gray-900 truncate">
                  {client.name}
                </h2>
                {client.carteBlanche && <CarteBlancheBadge />}
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {/* Header segments joined with one separator between visible ones (no stray leading dot) */}
                {[
                  // Lifecycle badge - glossary-wired. Under the relationship lock only 'paused' (operational) stays
                  (!isLocked('portfolio_client_relationship') || client.lifecycleStage === 'paused') && (
                    <GlossaryTooltip key="lifecycle" termId={lifecycleTermId} side="bottom">
                      <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.15em] font-semibold ${lifecycle.text} cursor-default`}>
                        {lifecycleStageLabel(t, client.lifecycleStage)}
                      </span>
                    </GlossaryTooltip>
                  ),
                  client.isActive === false && (
                    <span key="inactive" className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-semibold text-gray-600">
                      <i className="hm hm-pause" style={{ fontSize: 9 }} aria-hidden="true" />
                      {client.hiddenByPlan ? t('brand_space_badge_paused_by_plan') : t('brand_space_badge_paused')}
                    </span>
                  ),
                  // Health overall score - glossary-wired
                  !isLocked('portfolio_health_score') && (
                    <GlossaryTooltip key="health" termId="metric-health-score" side="bottom">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold ${tier.pillText} cursor-default`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${tier.dotColor} shrink-0`} aria-hidden="true" />
                        {tierLabel(t, client.healthOverall)}
                      </span>
                    </GlossaryTooltip>
                  ),
                  <span key="owner" className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-medium">
                    {client.ownerName}
                  </span>,
                ]
                  .filter(Boolean)
                  .flatMap((node, index) =>
                    index === 0
                      ? [node]
                      : [<span key={`sep-${index}`} className="text-gray-300 text-xs" aria-hidden="true">·</span>, node],
                  )}

                {!isLocked('portfolio_channel_health') && <ChannelHealthDot channelHealth={client.channelHealth} />}
              </div>
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={handleClose}
              className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 border border-transparent focus:outline-none focus-visible:border-primary-600 transition-colors"
              aria-label={t('close')}
            >
              <Icon name={ICONS.CLOSE} size="sm" />
            </button>
          </div>

          {/* Lifecycle-specific badge (renewal cliff warning etc.) */}
          {!isLocked('portfolio_client_relationship') &&
            (client.renewal.atRisk || (client.renewal.daysUntilRenewal !== null && client.renewal.daysUntilRenewal < 60)) && (
            <div className="mt-3 flex items-center gap-1.5">
              <RenewalCliffBadge renewal={client.renewal} />
            </div>
          )}
        </div>

        {/* ── SCROLLABLE BODY ───────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* Risk chips */}
          {hasRiskChips && !isLocked('portfolio_health_score') && (
            <div className="px-6 pt-4">
              <RiskRadarChips client={client} />
            </div>
          )}

          {/* ── Alert cards ───────────────────────────────────────── */}
          {((client.stalePendingCount ?? 0) > 0 || (!isLocked('portfolio_runway') && client.runwayDays === 0 && client.healthOverall !== 'paused')) && (
            <div className="px-6 pt-4 pb-2 space-y-2.5">
              {(client.stalePendingCount ?? 0) > 0 && (
                <div className="flex items-start gap-3 bg-coral-50 border border-coral-200 rounded-xl px-4 py-3">
                  <i className="hm hm-stop-watch text-coral-500 mt-0.5 flex-shrink-0" style={{ fontSize: 16 }} aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-coral-800">
                      {t('drawer_stale_alert_title', {
                        defaultValue_one: '{{count}} draft sitting unscheduled (>48h)',
                        defaultValue_other: '{{count}} drafts sitting unscheduled (>48h)',
                        count: client.stalePendingCount,
                      })}
                    </p>
                    <p className="text-xs text-coral-600 mt-0.5">
                      {t('drawer_stale_alert_desc', {
                        defaultValue: "These drafts haven't moved to the calendar. Schedule them or polish further.",
                      })}
                    </p>
                    <button
                      type="button"
                      className="text-xs font-medium underline underline-offset-2 mt-1 text-coral-700 hover:text-coral-900 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        void enterAndNavigate('/creator?status=UNSCHEDULED');
                      }}
                    >
                      {t('drawer_stale_alert_cta', { defaultValue: 'View drafts' })}
                    </button>
                  </div>
                </div>
              )}
              {!isLocked('portfolio_runway') && client.runwayDays === 0 && client.healthOverall !== 'paused' && (
                <div className="flex items-start gap-3 bg-peach/30 border border-peach rounded-xl px-4 py-3">
                  <i className="hm hm-calendar text-coral-600 mt-0.5 flex-shrink-0" style={{ fontSize: 16 }} aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-coral-800">
                      {t('drawer_no_scheduled_title', { defaultValue: 'Nothing scheduled' })}
                    </p>
                    <p className="text-xs text-coral-700 mt-0.5">
                      {t('drawer_no_scheduled_warning', {
                        defaultValue: 'No posts scheduled in the next 7 days',
                      })}
                    </p>
                    <button
                      type="button"
                      className="text-xs font-medium underline underline-offset-2 mt-1 text-coral-700 hover:text-coral-800 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        void enterAndNavigate('/calendar');
                      }}
                    >
                      {t('drawer_schedule_cta', { defaultValue: 'Go to calendar' })}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Pipeline ──────────────────────────────────────────── */}
          <div className="px-6 py-5 border-b border-gray-900/8">
            <div className="flex items-center justify-between mb-3">
              <GlossaryTooltip termId="metric-pipeline" side="right">
                <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium cursor-default">
                  {t('portfolio_drawer_section_pipeline', { defaultValue: 'Pipeline' })}
                  <i className="hm hm-info" style={{ fontSize: 10, opacity: 0.5 }} aria-hidden="true" />
                </p>
              </GlossaryTooltip>
              <span className="text-[11px] text-gray-400 tabular-nums">
                {client.pipeline.pending +
                  (client.pipeline.carteBlanche ? 0 : client.pipeline.changesRequested + client.pipeline.approved) +
                  client.pipeline.scheduled}
                {' '}
                {t('portfolio_drawer_pipeline_total_label', { defaultValue: 'total' })}
              </span>
            </div>
            <PipelineBar pipeline={client.pipeline} fullWidth />
            {/* Labeled legend grid — "Changes"/"Approved" are approval-only stages
                and do not exist for Carte Blanche teams (mirrors PipelineBar's
                FUNNEL_SEGMENTS.approvalOnly filtering). */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3">
              {([
                { count: client.pipeline.pending,          label: t('pipeline_label_pending',   { defaultValue: 'Drafts' }),    dot: 'bg-gray-400',      text: 'text-gray-700', approvalOnly: false },
                { count: client.pipeline.changesRequested, label: t('pipeline_label_changes',   { defaultValue: 'Changes' }),   dot: 'bg-coral-400',     text: 'text-coral-700', approvalOnly: true },
                { count: client.pipeline.approved,         label: t('pipeline_label_approved',  { defaultValue: 'Released' }),  dot: 'bg-primary-500',   text: 'text-primary-700', approvalOnly: true },
                { count: client.pipeline.scheduled,        label: t('pipeline_label_scheduled', { defaultValue: 'Scheduled' }), dot: 'bg-secondary-400', text: 'text-gray-700', approvalOnly: false },
              ] as const)
                .filter((seg) => !seg.approvalOnly || !client.pipeline.carteBlanche)
                .map((seg) => (
                  <div key={seg.label} className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${seg.dot}`} aria-hidden="true" />
                    <span className="text-[11px] text-gray-500">{seg.label}</span>
                    <span className={`ml-auto text-[11px] font-semibold tabular-nums ${seg.text}`}>{seg.count}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* ── This Week stats grid ──────────────────────────────── */}
          <div className="px-6 py-5 border-b border-gray-900/8">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium mb-3">
              {t('portfolio_drawer_section_this_week', { defaultValue: 'Pipeline' })}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {/* "Changes"/"Approved" tiles are approval-only stages — hidden for
                  Carte Blanche teams (mirrors the pipeline legend grid above and
                  PipelineBar's FUNNEL_SEGMENTS.approvalOnly filtering). */}
              {([
                { value: client.pipeline.pending,                  label: t('pipeline_drafts',            { defaultValue: 'Drafts' }),           accent: 'warning' as const,   approvalOnly: false },
                { value: client.pipeline.changesRequested,         label: t('pipeline_changes_requested', { defaultValue: 'Changes' }),          accent: 'coral' as const,     approvalOnly: true },
                { value: client.pipeline.approved,                 label: t('status_approved',            { defaultValue: 'Released' }),         accent: 'primary' as const,   approvalOnly: true },
                { value: client.pipeline.scheduled,                label: t('status_scheduled',           { defaultValue: 'Scheduled' }),        accent: 'secondary' as const, approvalOnly: false },
                { value: client.pipeline.publishedThisWeek ?? 0,   label: t('status_published',           { defaultValue: 'Published' }),        accent: 'mint' as const,      approvalOnly: false },
                { value: client.pipeline.failed ?? 0,              label: t('status_failed',              { defaultValue: 'Failed' }),           accent: 'danger' as const,    approvalOnly: false },
                { value: client.pipeline.partial ?? 0,             label: t('status_partial',             { defaultValue: 'Partial success' }),  accent: 'partial' as const,   approvalOnly: false },
                { value: client.pipeline.missed ?? 0,              label: t('status_missed',              { defaultValue: 'Missed' }),           accent: 'missed' as const,    approvalOnly: false },
              ]).filter((s) => !s.approvalOnly || !client.pipeline.carteBlanche).map((s) => (
                <div
                  key={s.accent}
                  className={`relative bg-white rounded-xl p-3 border border-gray-100 border-l-4 ${
                    s.accent === 'warning'   ? 'border-l-amber-400' :
                    s.accent === 'coral'     ? 'border-l-coral-500' :
                    s.accent === 'primary'   ? 'border-l-primary-500' :
                    s.accent === 'secondary' ? 'border-l-secondary-400' :
                    s.accent === 'mint'      ? 'border-l-primary-400' :
                    s.accent === 'partial'   ? 'border-l-amber-500' :
                    s.accent === 'missed'    ? 'border-l-slate-400' :
                    'border-l-coral-500'
                  } ${s.value === 0 ? 'opacity-60' : ''}`}
                >
                  <div className={`text-2xl font-bold leading-none ${
                    s.accent === 'warning'   ? 'text-coral-700' :
                    s.accent === 'coral'     ? 'text-coral-700' :
                    s.accent === 'primary'   ? 'text-dark-teal' :
                    s.accent === 'secondary' ? 'text-gray-700' :
                    s.accent === 'mint'      ? 'text-primary-600' :
                    s.accent === 'partial'   ? 'text-amber-700' :
                    s.accent === 'missed'    ? 'text-slate-600' :
                    'text-coral-700'
                  }`}>
                    {s.value}
                  </div>
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mt-1.5 leading-tight">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Avg time to approve KPI ───────────────────────────── */}
          {/* Approval-workflow metric — never applies to Carte Blanche teams
              (no approval gate to time), regardless of what the backend sends. */}
          {!client.carteBlanche &&
            client.avgTimeToApproveHours !== undefined && client.avgTimeToApproveHours !== null && (
            <div className="px-6 py-5 border-b border-gray-900/8">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium mb-3">
                {t('drawer_avg_approve_time', { defaultValue: 'Avg. release time (30d)' })}
              </p>
              <div className="flex items-end gap-2">
                <span className="font-wagner text-4xl leading-none tabular-nums text-gray-900">
                  {client.avgTimeToApproveHours}
                </span>
                <span className="text-sm font-medium text-gray-400 pb-1">
                  {t('drawer_avg_approve_unit', { defaultValue: 'hours' })}
                </span>
                <i className="hm hm-stop-watch text-gray-300 ml-auto pb-1" style={{ fontSize: 18 }} aria-hidden="true" />
              </div>
            </div>
          )}

          {/* ── Overview counts grid ──────────────────────────────── */}
          <div className="px-6 py-5 border-b border-gray-900/8">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium mb-3">
              {t('portfolio_drawer_section_overview', { defaultValue: 'Overview' })}
            </p>
            {/* 2-col on mobile (375px fits well), 4-col on sm+
                Dividers: always right-border on col 0; bottom-border on row 0 on mobile only */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-0">
              {([
                { value: client.totalPosts ?? 0,     label: t('total_posts',     { defaultValue: 'Posts' }),     pos: 0 },
                { value: client.totalChannels ?? 0,  label: t('total_channels',  { defaultValue: 'Channels' }),  pos: 1 },
                { value: client.totalMembers ?? 0,   label: t('total_members',   { defaultValue: 'Members' }),   pos: 2 },
                { value: client.totalCalendars ?? 0, label: t('total_calendars', { defaultValue: 'Calendars' }), pos: 3 },
              ] as const).map((s) => {
                // col 0 (pos 0,2) always has right divider; col 1 (pos 1,3) never does
                const rightBorder = s.pos % 2 === 0 ? 'border-r border-gray-900/6' : '';
                // row 0 (pos 0,1) has bottom divider on mobile; hidden on sm+
                const bottomBorder = s.pos < 2 ? 'border-b border-gray-900/6 sm:border-b-0' : '';
                // On sm+: all but last get right border
                const smRightBorder = s.pos < 3 ? 'sm:border-r sm:border-gray-900/6' : 'sm:border-r-0';
                return (
                  <div key={s.label} className={`flex flex-col items-center text-center py-2 ${rightBorder} ${bottomBorder} ${smRightBorder}`}>
                    <span className="font-wagner text-2xl leading-none tabular-nums text-gray-800">{s.value}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mt-1 font-medium">{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Health Dimensions ─────────────────────────────────── */}
          {!isLocked('portfolio_health_score') && (
          <div className="px-6 py-5 space-y-8 border-b border-gray-900/8">
            {[
              { label: 'Operational', overline: t('portfolio_health_operational', { defaultValue: 'Operational' }), dim: client.healthOperational, termId: 'health-operational' },
              { label: 'Brand Equity', overline: t('portfolio_health_brand_equity', { defaultValue: 'Brand Equity' }), dim: client.healthBrandEquity, termId: 'health-brand' },
              { label: 'Commercial', overline: t('portfolio_health_commercial', { defaultValue: 'Commercial' }), dim: client.healthCommercial, termId: 'health-commercial' },
            ].map(({ overline, dim, termId }) => {
              const t2 = scoreToTier(dim.score);
              return (
                <div key={termId}>
                  {/* Overline + info icon */}
                  <div className="flex items-center gap-1.5 mb-3">
                    <GlossaryTooltip termId={termId} side="right">
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium cursor-default">
                        {overline}
                        <i className="hm hm-info" style={{ fontSize: 10, opacity: 0.5 }} aria-hidden="true" />
                      </span>
                    </GlossaryTooltip>
                  </div>

                  {/* Score hero */}
                  <div className="flex items-end justify-between mb-3">
                    <span className={`font-wagner text-5xl leading-none tabular-nums tracking-tight ${t2.pillText}`}>
                      {dim.score}
                    </span>
                    <span className={`text-[11px] uppercase tracking-[0.15em] font-semibold ${t2.pillText}`}>
                      {tierLabel(t, t2.key)}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-3">
                    <div
                      className={`h-full rounded-full transition-all ${t2.dotColor}`}
                      style={{ width: `${dim.score}%` }}
                    />
                  </div>

                  {/* Signals */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {dim.signals.map((sig) => {
                      const st = scoreToTier(sig.score);
                      return (
                        <div key={sig.label} className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 truncate">{healthSignalLabel(t, sig)}</span>
                          <span className={`text-xs font-semibold tabular-nums ${st.pillText} ml-2 shrink-0`}>{sig.score}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          )}

          {/* ── Status update (history stays readable when composing is locked) ── */}
          {client.latestStatusUpdate && (
            <StatusUpdateDrawerSection
              update={client.latestStatusUpdate}
              t={t}
              formatRelativeDate={formatRelativeDate}
              onViewHistory={onShowStatusHistory ? () => onShowStatusHistory(client) : undefined}
            />
          )}

          {/* ── Runway ────────────────────────────────────────────── */}
          {!isLocked('portfolio_runway') && (
          <div className="px-6 py-5 space-y-2 border-b border-gray-900/8">
            <GlossaryTooltip termId="metric-runway" side="right">
              <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium cursor-default">
                {t('portfolio_drawer_section_runway', { defaultValue: 'Runway' })}
                <i className="hm hm-info" style={{ fontSize: 10, opacity: 0.5 }} aria-hidden="true" />
              </p>
            </GlossaryTooltip>
            {client.lifecycleStage === 'paused' ? (
              <span className="text-xs text-gray-400 italic">{tierLabel(t, 'paused')}</span>
            ) : (
              <RunwayBar
                {...pipelineBarDays(client)}
                fullWidth
              />
            )}
          </div>
          )}

          {/* ── Recent Activity ───────────────────────────────────── */}
          {/* Approval-action feed (APPROVED/CHANGES_REQUESTED/REJECTED/PENDING) —
              never applies to Carte Blanche teams (no approval workflow). */}
          {!client.carteBlanche && client.recentActivity && client.recentActivity.length > 0 && (
            <div className="px-6 py-5 border-b border-gray-900/8">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-medium mb-3">
                {t('drawer_activity_feed', { defaultValue: 'Recent activity' })}
              </p>
              <div className="relative">
                <div className="absolute left-[13px] top-0 bottom-0 w-px bg-gray-200" aria-hidden="true" />
                <div className="space-y-0">
                  {client.recentActivity.slice(0, 5).map((item) => {
                    const dotColor =
                      item.actionType === 'APPROVED' ? 'bg-primary-500' :
                      item.actionType === 'CHANGES_REQUESTED' ? 'bg-coral-500' :
                      item.actionType === 'REJECTED' ? 'bg-coral-600' :
                      'bg-peach';
                    const avatarBg =
                      item.actionType === 'APPROVED' ? 'bg-soft-mint text-dark-teal' :
                      item.actionType === 'CHANGES_REQUESTED' ? 'bg-coral-50 text-coral-700' :
                      item.actionType === 'REJECTED' ? 'bg-coral-100 text-coral-800' :
                      'bg-peach/50 text-coral-800';
                    const verb =
                      item.actionType === 'APPROVED' ? t('portfolio_activity_approved') :
                      item.actionType === 'CHANGES_REQUESTED' ? t('portfolio_activity_requested_changes') :
                      item.actionType === 'REJECTED' ? t('portfolio_activity_rejected') :
                      t('portfolio_activity_submitted');
                    return (
                      <div key={item.id} className="relative flex items-start gap-3 pl-2 py-2.5">
                        <div className={`relative z-[1] w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ring-2 ring-white ${avatarBg}`}>
                          {item.actorName.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 flex-wrap leading-snug">
                            <span className="text-xs font-semibold text-gray-800">{item.actorName}</span>
                            <span className="text-xs text-gray-500">{verb}</span>
                            <span className="text-xs text-gray-500 italic truncate max-w-[130px]">&ldquo;{item.postTitle ?? t('common:untitled_post')}&rdquo;</span>
                          </div>
                          {item.comment && (
                            <p className="text-[11px] text-gray-400 italic truncate mt-0.5">{item.comment}</p>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400 shrink-0 whitespace-nowrap">
                          {formatRelativeDate(item.occurredAt)}
                        </span>
                        <span className={`absolute left-[10px] top-[14px] w-2 h-2 rounded-full border-2 border-white z-[2] ${dotColor}`} aria-hidden="true" />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Asset Inventory ───────────────────────────────────── */}
          <AssetInventoryPanel
            client={client}
            locale={i18n.language}
            t={t}
            onPlanBatch={() => onPlanBatch(client)}
          />

          {/* ── Brand Settings ────────────────────────────────────── */}
          <BrandSettingsPanel
            client={client}
            t={t}
            onEnterWorkspace={enterAndNavigate}
            onEditBrand={() => setEditBrandOpen(true)}
          />

        </div>

        {/* ── STICKY FOOTER ─────────────────────────────────────────── */}
        <div className="shrink-0 border-t border-gray-900/8 px-6 py-4 flex items-center gap-3 bg-white">
          {client.ownerName && client.ownerInitials ? (
            <>
              <OwnerAvatar
                initials={client.ownerInitials}
                name={client.ownerName}
                color={client.ownerAvatarColor ?? 'bg-gray-300'}
                profilePhotoUrl={client.ownerProfilePhotoUrl}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 leading-tight truncate">{client.ownerName}</p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
                  {t('portfolio_drawer_assigned_lead', { defaultValue: 'Brand lead' })}
                </p>
              </div>
            </>
          ) : (
            <>
              <span
                className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-semibold text-gray-400 bg-gray-100 ring-1 ring-gray-200 shrink-0"
                aria-hidden="true"
              >
                <i className="hm hm-user" style={{ fontSize: 10 }} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-400 italic leading-tight truncate">
                  {t('portfolio_drawer_no_lead', { defaultValue: 'Unassigned' })}
                </p>
                <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
                  {t('portfolio_drawer_assigned_lead', { defaultValue: 'Brand lead' })}
                </p>
              </div>
            </>
          )}
          <Button
            variant="secondary"
            size="sm"
            loading={enterGodModeMutation.isPending}
            onClick={() => enterGodModeMutation.mutate()}
          >
            {t('portfolio_drawer_enter_workspace', { defaultValue: 'Enter brand' })}
          </Button>
        </div>
      </div>

      {/* Edit Brand modal - opens via Brand Info → Edit → in Brand Settings */}
      <EditBrandSpaceModal
        client={editBrandOpen ? client : null}
        onClose={() => setEditBrandOpen(false)}
      />
    </div>
  );
};

// ── KPI Strip ─────────────────────────────────────────────────────────────────

const KpiStrip = ({ clients, isLoading }: { clients: PortfolioClient[]; isLoading: boolean }) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const healthLocked = isLocked('portfolio_health_score');
  const counts = useMemo(() => {
    const tiers = { on_track: 0, watch: 0, at_risk: 0, off_track: 0 };
    let postsThisWeek = 0;
    clients.forEach((c) => {
      if (c.healthOverall !== 'paused') {
        (tiers as Record<string, number>)[c.healthOverall] = ((tiers as Record<string, number>)[c.healthOverall] ?? 0) + 1;
      }
      postsThisWeek += c.postsThisWeek;
    });
    return { total: clients.length, ...tiers, postsThisWeek };
  }, [clients]);

  // Map KPI tile index to glossary term ID (null = use plain Tooltip)
  const allTiles: Array<{
    label: string;
    tooltipKey: string;
    tooltipDefault: string;
    value: number;
    numColor: string;
    glossaryTermId?: string;
  }> = [
    { label: t('portfolio_kpi_total', { defaultValue: 'Brands' }), tooltipKey: 'portfolio_tooltip_kpi_total', tooltipDefault: 'All brands in your portfolio.', value: counts.total, numColor: 'text-gray-800' },
    { label: t('portfolio_kpi_on_track', { defaultValue: 'On track' }), tooltipKey: 'portfolio_tooltip_kpi_on_track', tooltipDefault: 'Clients with health score 80 or above. Everything humming.', value: counts.on_track, numColor: 'text-primary-700', glossaryTermId: 'tier-on-track' },
    { label: t('portfolio_kpi_watch', { defaultValue: 'Watch' }), tooltipKey: 'portfolio_tooltip_kpi_watch', tooltipDefault: 'Health 60–79. Not broken, but keep an eye.', value: counts.watch, numColor: 'text-coral-700', glossaryTermId: 'tier-watch' },
    { label: t('portfolio_kpi_at_risk', { defaultValue: 'At risk' }), tooltipKey: 'portfolio_tooltip_kpi_at_risk', tooltipDefault: 'Health 40–59. Clear signals something needs attention.', value: counts.at_risk, numColor: 'text-coral-700', glossaryTermId: 'tier-at-risk' },
    { label: t('portfolio_kpi_off_track', { defaultValue: 'Off track' }), tooltipKey: 'portfolio_tooltip_kpi_off_track', tooltipDefault: 'Health below 40. Urgent - the client needs you this week.', value: counts.off_track, numColor: 'text-coral-700', glossaryTermId: 'tier-off-track' },
    { label: t('portfolio_kpi_scheduled', { defaultValue: 'This week' }), tooltipKey: 'portfolio_tooltip_kpi_scheduled', tooltipDefault: 'Scheduled or published posts across the portfolio in the current week.', value: counts.postsThisWeek, numColor: 'text-primary-700' },
  ];
  // Health locked: the tier tiles are health aggregates, keep only Brands + This week
  const tiles = healthLocked ? [allTiles[0], allTiles[allTiles.length - 1]] : allTiles;

  return (
    <div
      className={`grid ${healthLocked ? 'grid-cols-2 sm:grid-cols-2' : 'grid-cols-3 sm:grid-cols-6'} bg-white border-b border-gray-900/10 portfolio-section px-4 sm:px-6`}
      aria-label={t('portfolio_kpis_aria')}
    >
      {tiles.map((tile, i) => {
        const showBorderR =
          i < tiles.length - 1 &&
          (healthLocked ? true : (i + 1) % 3 !== 0);

        const inner = (
          <div className="flex flex-col items-center text-center gap-1">
            {isLoading ? (
              <div className="w-10 h-9 bg-gray-200 rounded animate-pulse" />
            ) : (
              <span className={`font-wagner text-3xl sm:text-5xl leading-none tabular-nums tracking-tight ${tile.value === 0 ? 'text-gray-400' : tile.numColor}`}>{tile.value}</span>
            )}
            <span className="text-[9px] sm:text-[11px] uppercase tracking-wide sm:tracking-[0.15em] text-gray-500 leading-tight font-medium px-0.5">
              {tile.label}
            </span>
          </div>
        );

        const wrapped = tile.glossaryTermId ? (
          <GlossaryTooltip termId={tile.glossaryTermId} side="bottom">
            {inner}
          </GlossaryTooltip>
        ) : (
          <Tooltip content={t(tile.tooltipKey, { defaultValue: tile.tooltipDefault })} side="bottom" delayDuration={300}>
            {inner}
          </Tooltip>
        );

        return (
          <div
            key={tile.label}
            className={`px-2 sm:px-5 py-2 sm:py-0 min-h-[80px] sm:min-h-[88px] flex flex-col items-center justify-center cursor-default
              ${showBorderR ? 'border-r border-gray-900/8' : ''}
              ${i < tiles.length - 1 ? 'sm:border-r sm:border-gray-900/8' : 'sm:border-r-0'}
              ${i >= 3 ? 'border-t border-gray-900/8 sm:border-t-0' : ''}`}
          >
            {wrapped}
          </div>
        );
      })}
    </div>
  );
};

// ── Saved Views Dropdown ──────────────────────────────────────────────────────

interface LockedActionButtonProps {
  /** Portfolio feature key (agency package), e.g. 'portfolio_compare' */
  feature: string;
  label: string;
  iconClass: string;
}

/** A portfolio action the agency package lacks: stays in place with a lock badge, click opens the upsell. */
const LockedActionButton = ({ feature, label, iconClass }: LockedActionButtonProps) => {
  const { t } = useTranslation('agency');
  const openUpsell = useGatingStore((state) => state.openUpsell);
  return (
    <button
      type="button"
      data-testid={`locked-entry-${feature}`}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus-visible:border-primary-600"
      onClick={() => openUpsell({ feature, upsellPlanSlug: null }, true)}
      aria-label={t('common:gating_locked_label', { feature: label })}
      title={label}
    >
      <i className={`hm ${iconClass}`} style={{ fontSize: 14 }} aria-hidden="true" />
      <span className="hidden sm:inline">{label}</span>
      <LockBadge />
    </button>
  );
};

interface SavedViewsDropdownProps {
  savedView: SavedView;
  onSelect: (view: SavedView) => void;
  onSaveStub: () => void;
  customViews: PortfolioViewRecord[];
  activeCustomViewId: number | null;
  onSelectCustomView: (view: PortfolioViewRecord) => void;
  onDeleteCustomView: (view: PortfolioViewRecord) => void;
  /** The agency package lacks saved views: no custom views, no save action */
  viewsLocked?: boolean;
}

const SavedViewsDropdown = ({
  savedView,
  onSelect,
  onSaveStub,
  customViews,
  activeCustomViewId,
  onSelectCustomView,
  onDeleteCustomView,
  viewsLocked = false,
}: SavedViewsDropdownProps) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const relationshipLocked = isLocked('portfolio_client_relationship');
  const healthLocked = isLocked('portfolio_health_score');
  const openUpsellFromView = useGatingStore((state) => state.openUpsell);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentConfig = SAVED_VIEW_CONFIGS.find((v) => v.id === savedView);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors duration-150 border border-transparent focus:outline-none focus-visible:border-primary-600
          ${savedView !== 'all'
            ? 'bg-primary-25 text-primary-700'
            : 'text-gray-700 hover:bg-gray-50'
          }`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t('portfolio_saved_view_select_aria')}
        title={t('portfolio_saved_view_select_aria')}
      >
        <i className="hm hm-list" style={{ fontSize: 13 }} aria-hidden="true" />
        <span className="hidden sm:inline">
          {t(currentConfig?.labelKey ?? 'portfolio_view_all', {
            defaultValue: currentConfig?.defaultLabel ?? 'All Brands',
          })}
        </span>
        <SelectChevron className="ml-0.5 shrink-0" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-30 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 overflow-hidden">
          {SAVED_VIEW_CONFIGS.filter((cfg) => !(RELATIONSHIP_PRESETS.includes(cfg.id) && relationshipLocked) && !(cfg.id === 'at_risk' && healthLocked)).map((cfg) => {
            const savedViewTooltipDefaults: Record<SavedView, string> = {
              all: 'Everyone in your portfolio.',
              my_clients: 'Clients where you are the assigned lead.',
              at_risk: 'Clients flagged At risk or Off track.',
              enterprise: 'Your highest-tier retainers.',
              onboarding: 'Newly-signed clients still ramping up.',
              renewal_this_month: 'Retainers renewing within 30 days.',
              paused: 'Clients on hold or paused.',
            };
            return (
            <Tooltip key={cfg.id} content={t(`portfolio_tooltip_view_preset_${cfg.id}`, { defaultValue: savedViewTooltipDefaults[cfg.id] })} side="right" delayDuration={300}>
              <button
                type="button"
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors relative
                  ${savedView === cfg.id
                    ? 'text-primary-700 font-medium bg-primary-25 border-l-[3px] border-primary-500 pl-[9px]'
                    : 'text-gray-700 border-l-[3px] border-transparent pl-[9px]'
                  }`}
                onClick={() => {
                  onSelect(cfg.id);
                  setOpen(false);
                }}
              >
                {t(cfg.labelKey, { defaultValue: cfg.defaultLabel })}
              </button>
            </Tooltip>
            );
          })}

          {customViews.length > 0 && (
            <>
              <div className="border-t border-gray-100 mt-1 pt-1 pb-1">
                <p className="px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
                  {t('portfolio_my_saved_views', { defaultValue: 'My saved views' })}
                </p>
                {customViews.map((v) => {
                  const isActive = activeCustomViewId === v.id;
                  return (
                    <div
                      key={v.id}
                      className={`group relative flex items-center border-l-[3px] ${
                        isActive
                          ? 'border-primary-500 bg-primary-25'
                          : 'border-transparent'
                      }`}
                    >
                      <button
                        type="button"
                        className={`flex-1 text-left pl-[9px] pr-2 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors
                          ${isActive ? 'text-primary-700 font-medium' : 'text-gray-700'}`}
                        onClick={() => {
                          onSelectCustomView(v);
                          setOpen(false);
                        }}
                        title={v.name}
                      >
                        <span className="truncate">{v.name}</span>
                      </button>
                      <button
                        type="button"
                        className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 focus:opacity-100 inline-flex items-center justify-center p-1.5 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] mr-1 text-gray-400 hover:text-coral-600 rounded transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteCustomView(v);
                        }}
                        aria-label={t('portfolio_delete_view_aria', { name: v.name })}
                      >
                        <i className="hm hm-cross" style={{ fontSize: 11 }} aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm text-gray-700 flex items-center gap-2 hover:bg-gray-50 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
              {...(viewsLocked
                ? {
                    'data-testid': 'locked-entry-portfolio_saved_views',
                    'aria-label': t('common:gating_locked_label', {
                      feature: t('portfolio_save_view', { defaultValue: 'Save current filter as view…' }),
                    }),
                  }
                : {})}
              onClick={() => {
                setOpen(false);
                if (viewsLocked) {
                  openUpsellFromView({ feature: 'portfolio_saved_views', upsellPlanSlug: null }, true);
                } else {
                  onSaveStub();
                }
              }}
            >
              <i className="hm hm-plus-gap text-gray-500 shrink-0" style={{ fontSize: 12 }} aria-hidden="true" />
              {t('portfolio_save_view', { defaultValue: 'Save current filter as view…' })}
              {viewsLocked && <LockBadge className="ml-auto" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Runway + velocity cell (each independently lockable) ─────────────────────

/**
 * Runway bar with the velocity trend arrow in its trailing slot. A runway lock hides only the bar (and its
 * low-pipeline label); a velocity lock hides only the arrow. With the runway locked the arrow stands alone.
 */
const RunwayVelocity = ({
  client,
  layout,
  velocityPeriod,
}: {
  client: PortfolioClient;
  layout: 'table' | 'mobile' | 'board';
  velocityPeriod?: 'week' | 'month';
}) => {
  const { isLocked } = usePortfolioLocks();
  const runwayLocked = isLocked('portfolio_runway');
  const velocityLocked = isLocked('portfolio_velocity');
  const velocity = velocityLocked ? undefined : (
    <VelocityTrendArrow
      velocity={client.velocity}
      period={layout === 'table' ? velocityPeriod : undefined}
      disableGlossaryOnMobile={layout === 'mobile'}
    />
  );
  // The board never showed velocity on its own: with the runway locked there is nothing to render
  if (runwayLocked) return layout === 'board' ? null : (velocity ?? null);
  return (
    <>
      <RunwayBar
        {...pipelineBarDays(client)}
        hideCaption={layout !== 'board' && isLowPipeline(client)}
        compact={layout !== 'mobile'}
        fullWidth={layout === 'mobile'}
        disableGlossaryOnMobile={layout !== 'table'}
        trailingSlot={layout === 'board' ? undefined : velocity}
      />
      {layout !== 'board' && <LowPipelineLabel client={client} />}
    </>
  );
};

// ── Grid View ─────────────────────────────────────────────────────────────────

const MAX_COMPARE = 3;

const GridView = ({
  clients,
  onRowClick,
  onShowStatusHistory,
  formatRelativeDate,
  isLoading,
  compareMode,
  selectedIds,
  onToggleSelect,
  visibleBadges,
  matchContextMap,
  velocityPeriod,
  onVelocityPeriodChange,
  onClearFilters,
}: {
  clients: PortfolioClient[];
  onRowClick: (client: PortfolioClient) => void;
  /** Click on the Status column → open the status-update history modal */
  onShowStatusHistory: (client: PortfolioClient) => void;
  formatRelativeDate: (date: string) => string;
  isLoading: boolean;
  compareMode: boolean;
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  visibleBadges: BadgeId[];
  /** Deep-search match context per brand id (e.g., "matched in post: ..."). */
  matchContextMap: Map<number, string>;
  /** Drives the trend arrow: 'week' (default) or 'month'. */
  velocityPeriod: 'week' | 'month';
  /** User-flipped period toggle in the Runway column header. */
  onVelocityPeriodChange: (period: 'week' | 'month') => void;
  /** Resets all active filters (empty-state "Clear filters" button). */
  onClearFilters: () => void;
}) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const healthLocked = isLocked('portfolio_health_score');
  const runwayLocked = isLocked('portfolio_runway');
  const velocityLocked = isLocked('portfolio_velocity');
  const showRunwayColumn = !runwayLocked || !velocityLocked;
  // Badge visibility after the package locks (the empty-state check uses the same flags)
  const showChannelHealth = visibleBadges.includes('channel_health') && !isLocked('portfolio_channel_health');
  const showReviewCliff = visibleBadges.includes('review_cliff') && !isLocked('portfolio_client_relationship');
  // Lifecycle stage is stripped to 'active' under the relationship lock: no chip then
  const relationshipLocked = isLocked('portfolio_client_relationship');
  const showLifecycleFor = (c: PortfolioClient): boolean =>
    visibleBadges.includes('lifecycle') && (!relationshipLocked || c.lifecycleStage === 'paused');
  const handleRowAction = (client: PortfolioClient) => {
    if (compareMode) {
      onToggleSelect(client.id);
    } else {
      onRowClick(client);
    }
  };
  if (isLoading) {
    return (
      <div>
        {[0, 1, 2, 3, 4].map((i) => <SkeletonRow key={i} />)}
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 md:py-16 px-6 text-center">
        <i className="hm hm-group text-gray-300 mb-4" style={{ fontSize: 48 }} aria-hidden="true" />
        <h3 className="text-base font-semibold text-gray-900 mb-1">{t('portfolio_filter_empty_title')}</h3>
        <p className="text-sm text-gray-500 max-w-md mb-4">{t('portfolio_filter_empty_desc')}</p>
        <Button variant="outline" size="sm" onClick={onClearFilters}>
          {t('inbox_clear_filters')}
        </Button>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto px-4 sm:px-6">
      <table className="w-full min-w-[900px] text-sm hidden sm:table [&_td]:align-middle" role="grid">
        <thead>
          <tr className="border-b border-gray-900/10">
            {compareMode && <th className="w-10 px-3 py-2.5" aria-label={t('compare_select_column', { defaultValue: 'Select for compare' })} />}
            <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-[0.15em] px-4 py-2.5 w-44">
              {t('portfolio_col_client', { defaultValue: 'Brand' })}
            </th>
            <th className="text-center text-[10px] font-medium text-gray-400 uppercase tracking-[0.15em] px-3 py-2.5 w-12">
              {t('portfolio_col_lead', { defaultValue: 'Lead' })}
            </th>
            {!healthLocked && (
              <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-[0.15em] px-3 py-2.5 w-44">
                {t('portfolio_col_health', { defaultValue: 'Health' })}
              </th>
            )}
            <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-[0.15em] pl-3 pr-5 py-2.5 w-48">
              {t('portfolio_col_pipeline', { defaultValue: 'Pipeline' })}
            </th>
            {showRunwayColumn && (
            <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-[0.15em] pl-5 pr-3 py-2.5 w-48">
              <span className="inline-flex items-center gap-1.5 normal-case tracking-normal">
                <span className="uppercase tracking-[0.15em]">
                  {runwayLocked
                    ? t('compare_section_velocity', { defaultValue: 'Velocity' })
                    : t('portfolio_col_runway', { defaultValue: 'Runway' })}
                </span>
                {!velocityLocked && (
                <span
                  className="inline-flex items-stretch rounded-md border border-gray-200 bg-white text-[9px] tracking-normal h-5 overflow-hidden"
                  role="group"
                  aria-label={t('portfolio_velocity_period_aria', { defaultValue: 'Velocity comparison period' })}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onVelocityPeriodChange('week'); }}
                    aria-pressed={velocityPeriod === 'week'}
                    className={`px-1.5 inline-flex items-center justify-center leading-none transition-colors ${
                      velocityPeriod === 'week'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    W
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onVelocityPeriodChange('month'); }}
                    aria-pressed={velocityPeriod === 'month'}
                    className={`px-1.5 inline-flex items-center justify-center leading-none transition-colors ${
                      velocityPeriod === 'month'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    M
                  </button>
                </span>
                )}
              </span>
            </th>
            )}
            <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-[0.15em] px-3 py-2.5 w-48">
              {t('portfolio_col_status', { defaultValue: 'Status update' })}
            </th>
            <th className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-[0.15em] px-3 py-2.5 w-36">
              {t('portfolio_col_tags', { defaultValue: 'Tags' })}
            </th>
            <th className="text-center text-[10px] font-medium text-gray-400 uppercase tracking-[0.15em] px-3 py-2.5 w-16">
              {t('portfolio_col_drafts', { defaultValue: 'Drafts' })}
            </th>
            {!compareMode && <th className="w-8" />}
          </tr>
        </thead>
        <tbody>
          {clients.map((client, rowIndex) => {
            const isPaused = client.healthOverall === 'paused';
            const isSelected = selectedIds.has(client.id);
            const isDisabled = compareMode && !isSelected && selectedIds.size >= MAX_COMPARE;

            return (
              <tr
                key={client.id}
                style={{ '--row-i': rowIndex } as React.CSSProperties}
                className={`border-b border-gray-900/6 transition-colors duration-150 group cursor-pointer portfolio-row
                  ${compareMode
                    ? isSelected
                      ? 'bg-primary-25 ring-1 ring-inset ring-primary-300'
                      : isDisabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:bg-gray-50/60'
                    : 'hover:bg-gray-50/60'
                  }
                  ${isPaused && !compareMode ? 'opacity-60' : ''}
                `}
                onClick={() => !isDisabled && handleRowAction(client)}
                onKeyDown={(e) => {
                  // Ignore keys bubbling from nested controls (health score button, menus, checkbox)
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!isDisabled) handleRowAction(client);
                  }
                }}
                tabIndex={isDisabled ? -1 : 0}
                role="row"
                aria-label={`${healthLocked ? client.name : `${client.name}: ${tierLabel(t, client.healthOverall)}`}${compareMode && isSelected ? `, ${t('portfolio_row_selected')}` : ''}`}
                aria-selected={compareMode ? isSelected : undefined}
              >
                {/* Checkbox (compare mode) */}
                {compareMode && (
                  <td className="pl-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelect(client.id)}
                      disabled={isDisabled}
                      aria-label={client.name}
                      inline
                    />
                  </td>
                )}

                {/* Brand */}
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2.5">
                    <ClientAvatar initial={client.avatarInitial} color={client.avatarColor} icon={client.icon} customColor={client.customColor} logoUrl={client.logoUrl} name={client.name} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-base font-semibold text-gray-900 truncate leading-snug">{client.name}</span>
                        {client.carteBlanche && <CarteBlancheBadge />}
                      </div>
                      {matchContextMap.get(client.id) && (
                        <p
                          className="text-[10px] text-primary-600 italic truncate mt-0.5"
                          title={matchContextMap.get(client.id)}
                        >
                          {matchContextMap.get(client.id)}
                        </p>
                      )}
                    </div>
                  </div>
                </td>

                {/* Brand lead */}
                <td className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center">
                    {client.ownerName && client.ownerInitials ? (
                      <OwnerAvatar
                        initials={client.ownerInitials}
                        name={client.ownerName}
                        color={client.ownerAvatarColor ?? 'bg-gray-300'}
                        profilePhotoUrl={client.ownerProfilePhotoUrl}
                        onClick={(e) => { e.stopPropagation(); }}
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-200 ring-1 ring-gray-300" title={t('portfolio_brand_lead_not_set')} aria-label={t('portfolio_brand_lead_not_set')} />
                    )}
                  </div>
                </td>

                {/* Health */}
                {!healthLocked && (
                <td className="px-3 py-4 w-48" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-start gap-2">
                    {[
                      { dim: client.healthOperational, label: 'Operational', short: t('portfolio_health_short_operational', { defaultValue: 'Ops' }) },
                      { dim: client.healthBrandEquity, label: 'Brand Equity', short: t('portfolio_health_short_brand_equity', { defaultValue: 'Brand' }) },
                      { dim: client.healthCommercial, label: 'Commercial', short: t('portfolio_health_short_commercial', { defaultValue: 'Comm' }) },
                    ].map((d) => (
                      <div key={d.label} className="flex flex-col items-center gap-1">
                        <HealthPill
                          dimension={d.dim}
                          label={d.label}
                          tier={client.healthOverall}
                          weeklyScoreHistory={client.weeklyScoreHistory}
                        />
                        <span className="text-[9px] uppercase tracking-[0.1em] text-gray-400 font-medium leading-none">
                          {d.short}
                        </span>
                      </div>
                    ))}
                  </div>
                </td>
                )}

                {/* Pipeline */}
                <td className="pl-3 pr-5 py-4 w-48 align-middle" onClick={(e) => e.stopPropagation()}>
                  {isPaused ? (
                    <span className="text-xs text-gray-400 italic">{tierLabel(t, 'paused')}</span>
                  ) : (
                    <PipelineBar pipeline={client.pipeline} fullWidth />
                  )}
                </td>

                {/* Runway (+ velocity) */}
                {showRunwayColumn && (
                <td className="pl-5 pr-3 py-4 w-48 align-middle" onClick={(e) => e.stopPropagation()}>
                  {isPaused ? (
                    <span className="text-xs text-gray-400 italic">{tierLabel(t, 'paused')}</span>
                  ) : (
                    <RunwayVelocity client={client} layout="table" velocityPeriod={velocityPeriod} />
                  )}
                </td>
                )}

                {/* Status update */}
                <td className="px-3 py-4 max-w-[220px]" onClick={(e) => e.stopPropagation()}>
                  {client.latestStatusUpdate ? (
                    <StatusExcerpt
                      update={client.latestStatusUpdate}
                      formatRelativeDate={formatRelativeDate}
                      onClick={() => onShowStatusHistory(client)}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => onShowStatusHistory(client)}
                      className="text-xs text-gray-300 italic hover:text-gray-500 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600 rounded"
                    >
                      {t('portfolio_status_no_update_view_history', { defaultValue: 'No update — view history' })}
                    </button>
                  )}
                </td>

                {/* Badges */}
                <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
                  <div className="space-y-1.5">
                    {/* Line 1: lifecycle stage */}
                    {showLifecycleFor(client) && (
                      <div className="flex items-center">
                        <LifecycleBadge stage={client.lifecycleStage} />
                      </div>
                    )}
                    {/* Line 2: channel health, renewal cliff, tier */}
                    {(showChannelHealth || showReviewCliff) && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {showChannelHealth && <ChannelHealthDot channelHealth={client.channelHealth} />}
                        {showReviewCliff && <RenewalCliffBadge renewal={client.renewal} />}
                      </div>
                    )}
                    {/* Empty state */}
                    {!showLifecycleFor(client) && !showChannelHealth && !showReviewCliff && (
                      <span className="text-xs text-gray-300">-</span>
                    )}
                  </div>
                </td>

                {/* Drafts */}
                <td className="px-3 py-4 text-center">
                  <PendingBadge
                    count={client.pendingCount}
                    carteBlanche={client.carteBlanche}
                    awaitingApproval={client.pipeline.awaitingApproval}
                  />
                </td>

                {/* Chevron (normal mode only) */}
                {!compareMode && (
                  <td className="pr-3 py-4">
                    <div className="flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
                        <Icon name={ICONS.ANGLE_RIGHT} size="sm" color="text-gray-500" ariaHidden />
                      </span>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Mobile: structured cards (mirrors Creator's PostTable mobile pattern —
          info bar / metrics / status line / badges as discrete rows). */}
      <div className="sm:hidden space-y-3 p-3">
        {clients.map((client) => {
          const tier = TIER_CONFIG[client.healthOverall];
          const isPaused = client.healthOverall === 'paused';
          const isSelected = selectedIds.has(client.id);
          const isDisabled = compareMode && !isSelected && selectedIds.size >= MAX_COMPARE;

          return (
            <div
              key={client.id}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              aria-disabled={isDisabled || undefined}
              onKeyDown={(e) => {
                // Ignore keys bubbling from nested controls (e.g. compare checkbox)
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!isDisabled) handleRowAction(client);
                }
              }}
              className={`w-full text-left cursor-pointer p-3 rounded-xl border transition-all active:scale-[0.98] space-y-2.5
                ${compareMode
                  ? isSelected
                    ? 'bg-primary-25 border-primary-300 ring-1 ring-primary-300'
                    : isDisabled
                    ? 'opacity-40 cursor-not-allowed bg-white border-gray-100'
                    : 'bg-white border-gray-200'
                  : isPaused
                  ? 'opacity-60 bg-gray-50 border-gray-100'
                  : 'bg-white border-gray-200'
                }
              `}
              onClick={() => !isDisabled && handleRowAction(client)}
              aria-label={`${healthLocked ? client.name : `${client.name}: ${tierLabel(t, client.healthOverall)}`}${compareMode && isSelected ? `, ${t('portfolio_row_selected')}` : ''}`}
            >
              {/* Row 1 — Info bar: [checkbox] avatar + name (carte-blanche) · health pill · chevron */}
              <div className="flex items-center gap-2">
                {compareMode && (
                  <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelect(client.id)}
                      disabled={isDisabled}
                      aria-label={client.name}
                      inline
                    />
                  </span>
                )}
                <ClientAvatar
                  initial={client.avatarInitial}
                  color={client.avatarColor}
                  icon={client.icon}
                  customColor={client.customColor}
                  logoUrl={client.logoUrl}
                  name={client.name}
                  size="sm"
                />
                <div className="flex-1 min-w-0 flex items-center gap-1.5">
                  <span className="font-semibold text-gray-900 text-sm truncate">{client.name}</span>
                  {client.carteBlanche && <CarteBlancheBadge />}
                </div>
                {!healthLocked && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${tier.pillBg} ${tier.pillText}`}>
                    {tierLabel(t, client.healthOverall)}
                  </span>
                )}
                {client.pendingCount > 0 && (
                  <span className="hidden lg:inline-flex shrink-0">
                    <PendingBadge
                      count={client.pendingCount}
                      carteBlanche={client.carteBlanche}
                      awaitingApproval={client.pipeline.awaitingApproval}
                    />
                  </span>
                )}
                {!compareMode && (
                  <Icon name={ICONS.ANGLE_RIGHT} size="xs" color="text-gray-500" className="shrink-0" ariaHidden />
                )}
              </div>

              {/* Row 2 — Pipeline + Runway bars (skipped when paused).
                  Both pass `fullWidth` so they fill the card and match each
                  other's width on mobile. */}
              {!isPaused && (
                <div className="space-y-1.5">
                  <PipelineBar pipeline={client.pipeline} fullWidth disableGlossaryOnMobile showCountLabels />
                  <RunwayVelocity client={client} layout="mobile" />
                </div>
              )}

              {/* Row 3 — Latest status update line. Click opens history modal
                  without triggering the parent card's "open drawer" handler. */}
              {client.latestStatusUpdate && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowStatusHistory(client);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      e.preventDefault();
                      onShowStatusHistory(client);
                    }
                  }}
                  className="block text-xs text-gray-500 leading-snug line-clamp-2 hover:bg-gray-50 -mx-1 px-1 rounded cursor-pointer transition-colors"
                  aria-label={t('portfolio_status_history_view_aria')}
                >
                  <span className="mr-1">{client.latestStatusUpdate.vibe}</span>
                  {client.latestStatusUpdate.tldr}
                </span>
              )}

              {/* Row 4 — Badges (lifecycle / channel-health / cliff). Hidden when no badges turned on. */}
              {(showLifecycleFor(client) || showChannelHealth || showReviewCliff) && (
                <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-gray-100">
                  {showLifecycleFor(client) && (
                    <LifecycleBadge stage={client.lifecycleStage} disableGlossaryOnMobile />
                  )}
                  {showChannelHealth && <ChannelHealthDot channelHealth={client.channelHealth} showLabel />}
                  {showReviewCliff && <RenewalCliffBadge renewal={client.renewal} />}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Board View ────────────────────────────────────────────────────────────────

const BOARD_TIERS: HealthTier[] = ['on_track', 'watch', 'at_risk', 'off_track', 'paused'];

const BoardView = ({
  clients,
  onCardClick,
  isLoading,
}: {
  clients: PortfolioClient[];
  onCardClick: (client: PortfolioClient) => void;
  /** Click on the status line in a card → open status history modal */
  onShowStatusHistory: (client: PortfolioClient) => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation('agency');
  const { isLocked } = usePortfolioLocks();
  const healthLocked = isLocked('portfolio_health_score');
  const grouped = useMemo(() => {
    const map: Record<HealthTier, PortfolioClient[]> = {
      on_track: [],
      watch: [],
      at_risk: [],
      off_track: [],
      paused: [],
    };
    clients.forEach((c) => map[c.healthOverall].push(c));
    return map;
  }, [clients]);

  // Health locked: the tier columns would reveal the health tier, so group by lifecycle instead (Active / Paused)
  const boardColumns = useMemo(
    () =>
      healthLocked
        ? [
            {
              key: 'active',
              cfgKey: 'on_track' as HealthTier,
              label: lifecycleStageLabel(t, 'active'),
              group: clients.filter((c) => c.healthOverall !== 'paused'),
            },
            { key: 'paused', cfgKey: 'paused' as HealthTier, label: tierLabel(t, 'paused'), group: grouped.paused },
          ]
        : BOARD_TIERS.map((tier) => ({
            key: tier as string,
            cfgKey: tier,
            label: tierLabel(t, tier),
            group: grouped[tier],
          })),
    [healthLocked, clients, grouped, t],
  );

  const boardGridCols = `repeat(${boardColumns.length}, minmax(200px, 1fr))`;

  if (isLoading) {
    return (
      <div className="overflow-x-auto px-4 py-4">
        <div className="hidden sm:grid gap-3" style={{ gridTemplateColumns: boardGridCols }}>
          {boardColumns.map(({ key: tier }) => (
            <div key={tier} className="space-y-2">
              <div className="h-7 bg-gray-200 rounded-lg animate-pulse mb-3" />
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ))}
        </div>
        <div className="sm:hidden space-y-3">
          {boardColumns.map(({ key: tier }) => (
            <div key={tier} className="space-y-2">
              <div className="h-7 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-28 bg-gray-100 rounded-xl animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="px-4 sm:px-6 py-4 pb-6 sm:overflow-x-auto"
      role="list"
      aria-label={t('portfolio_board_view_aria')}
    >
      {/* Mobile: tiers stack as labelled rows; cards within each tier swipe horizontally.
          Desktop (sm+): traditional kanban — columns side-by-side, cards stack vertically. */}
      <div
        className="flex flex-col gap-3 sm:grid"
        style={{ gridTemplateColumns: boardGridCols }}
      >
      {boardColumns.map(({ key: tier, cfgKey, label: columnLabel, group }) => {
        const cfg = TIER_CONFIG[cfgKey];
        return (
          <div key={tier} className="flex flex-col min-w-0" role="listitem" aria-label={t('portfolio_board_column_aria', { tier: columnLabel, defaultValue: '{{tier}} column' })}>
            {/* Column header */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl mb-2 ${cfg.headerBg}`}>
              <span className={`w-2 h-2 rounded-full ${cfg.dotColor}`} aria-hidden="true" />
              <span className={`text-xs font-semibold ${cfg.headerText}`}>{columnLabel}</span>
              <span className={`ml-auto text-xs font-bold ${cfg.pillText} bg-white/60 rounded-full px-1.5 py-0.5`}>
                {group.length}
              </span>
            </div>

            {/* Cards: horizontal scroll on mobile, vertical stack on desktop.
                Negative margin lets cards bleed to the screen edge on mobile so
                the user can swipe naturally; cards inside have min-width so 1.5
                are visible at a time hinting "swipe for more". */}
            <div className="flex flex-row gap-2 overflow-x-auto -mx-4 px-4 pb-1 sm:mx-0 sm:px-0 sm:flex-col sm:overflow-visible sm:flex-1 [&>button]:min-w-[220px] sm:[&>button]:min-w-0">
              {group.length === 0 && (
                <div className="flex flex-col items-center justify-center py-6 px-4 border border-dashed border-gray-200 rounded-xl min-w-[160px] sm:min-w-0 sm:py-8">
                  <span className="text-xs text-gray-300">{t('portfolio_board_empty_tier')}</span>
                </div>
              )}
              {group.map((client) => {
                const isPaused = tier === 'paused';
                return (
                  <div
                    key={client.id}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      // Ignore keys bubbling from nested controls (risk chips)
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCardClick(client);
                      }
                    }}
                    className={`w-full text-left cursor-pointer p-3 rounded-lg border bg-white transition-all hover:-translate-y-0.5 active:scale-[0.98] ${isPaused ? 'opacity-60 border-gray-900/6' : 'border-gray-900/6 hover:border-primary-200'} outline-none focus-visible:border-primary-600`}
                    onClick={() => onCardClick(client)}
                    aria-label={healthLocked ? client.name : `${client.name}: ${tierLabel(t, client.healthOverall)}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <ClientAvatar initial={client.avatarInitial} color={client.avatarColor} icon={client.icon} customColor={client.customColor} logoUrl={client.logoUrl} name={client.name} size="sm" />
                      <span className="font-semibold text-gray-900 text-sm truncate flex-1">{client.name}</span>
                      {client.carteBlanche && <CarteBlancheBadge />}
                      {!isLocked('portfolio_channel_health') && <ChannelHealthDot channelHealth={client.channelHealth} />}
                      {client.ownerName && client.ownerInitials ? (
                        <OwnerAvatar
                          initials={client.ownerInitials}
                          name={client.ownerName}
                          color={client.ownerAvatarColor ?? 'bg-gray-300'}
                          profilePhotoUrl={client.ownerProfilePhotoUrl}
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-200 ring-1 ring-gray-300" title={t('portfolio_brand_lead_not_set')} aria-label={t('portfolio_brand_lead_not_set')} />
                      )}
                    </div>

                    {/* Health pills */}
                    {!healthLocked && (
                    <div className="flex items-center gap-1 mb-2" onClick={(e) => e.stopPropagation()}>
                      <HealthPill dimension={client.healthOperational} label="Operational" tier={client.healthOverall} weeklyScoreHistory={client.weeklyScoreHistory} />
                      <HealthPill dimension={client.healthBrandEquity} label="Brand" tier={client.healthOverall} weeklyScoreHistory={client.weeklyScoreHistory} />
                      <HealthPill dimension={client.healthCommercial} label="Commercial" tier={client.healthOverall} weeklyScoreHistory={client.weeklyScoreHistory} />
                    </div>
                    )}

                    <div className="space-y-1.5 mb-1">
                      {isPaused ? (
                        <span className="text-xs text-gray-400 italic">{tierLabel(t, 'paused')}</span>
                      ) : (
                        <>
                          {/* Pipeline row: bar + readout */}
                          <PipelineBar pipeline={client.pipeline} disableGlossaryOnMobile />
                          {/* Runway row: bar + readout - same h-2 bar height, same text-[10px] readout */}
                          <RunwayVelocity client={client} layout="board" />
                        </>
                      )}
                    </div>
                    {(!isLocked('portfolio_client_relationship') || client.lifecycleStage === 'paused') && (
                      <div className="flex items-center justify-end">
                        <LifecycleBadge stage={client.lifecycleStage} disableGlossaryOnMobile />
                      </div>
                    )}

                    {/* Risk chips */}
                    {!healthLocked && (
                      <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                        <RiskRadarChips client={client} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

// ── Timeline View ─────────────────────────────────────────────────────────────

/** Per-day published/scheduled/failed sums, keyed by `YYYY-MM-DD` (day mode)
 *  or by the Monday `YYYY-MM-DD` of the ISO week (week mode). */
interface TimelineBucket {
  /** Posts committed to a calendar day but not yet scheduled/published/failed
   *  ("planned"). Never overlaps published/scheduled/failed. */
  mapped: number;
  published: number;
  scheduled: number;
  /** First-class occurrence outcome (POST-STATUS-MODEL.md) — some channels
   *  published, some failed. Rendered as its own amber dot, never folded
   *  into `failed`. */
  partial: number;
  failed: number;
  /** First-class occurrence outcome — overdue, never attempted. Rendered as
   *  its own gray dot, never folded into `failed`. */
  missed: number;
}

const EMPTY_TIMELINE_BUCKET: TimelineBucket = {
  mapped: 0,
  published: 0,
  scheduled: 0,
  partial: 0,
  failed: 0,
  missed: 0,
};

/** Builds a noon-local `Date` from a `YYYY-MM-DD` key — mirrors
 *  `isoWeekFromYmd` in `useTimelineWindow`. Used to match campaign/holiday
 *  markers against a Timeline column's day(s) without tz drift. */
function ymdToNoonDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 12);
}

const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
function safeHexColor(color: string | null | undefined, fallback: string): string {
  return color && HEX_COLOR_RE.test(color) ? color : fallback;
}

/** Converts a `TimelineNote` (backend/API shape, nullable fields) into the
 *  `CalendarNote` shape `campaignMatchesDay` expects, so the Timeline reuses
 *  the exact same matching logic as the Calendar page. Only `startAt` /
 *  `endAt` / `repeatType` are read by the matcher — the rest are placeholders. */
function noteToCalendarNote(note: TimelineNote): CalendarNote {
  return {
    id: note.id,
    calendarId: 0,
    title: note.title,
    description: note.description ?? undefined,
    color: note.color ?? undefined,
    startAt: note.startAt,
    endAt: note.endAt ?? undefined,
    repeatType: note.repeatType ?? undefined,
    userId: 0,
    createdAt: note.startAt,
    updatedAt: note.startAt,
  };
}

/** One header/body column of the Timeline grid. In day mode each column is a
 *  single calendar day; in week mode (spans > 45 days, i.e. the Quarter
 *  preset) each column aggregates a full Monday-anchored ISO week so a
 *  92-day quarter renders ~13 columns instead of 92. `dayIsos` lists the
 *  underlying day keys (from the timeline API response) to sum per column. */
interface TimelineColumn {
  /** Day key (day mode) or week-start Monday key (week mode), `YYYY-MM-DD`. */
  iso: string;
  label: string;
  isToday: boolean;
  isWeekStart: boolean;
  dayIsos: string[];
  /** ISO calendar week number of the column's Monday (week mode only; `null` in day mode). */
  isoWeek: number | null;
}

/** Builds the Timeline grid's columns for an arbitrary `[from, to]` window,
 *  switching from day-granular to week-aggregated columns once the span
 *  exceeds 45 days. All day/week keys and labels are computed in `timezone`
 *  (the resolved user → team → default cascade), matching the keys the
 *  backend timeline endpoint returns. */
function useTimelineWindow(
  from: Date,
  to: Date,
  timezone: string,
  locale: string,
): { columns: TimelineColumn[]; mode: 'day' | 'week' } {
  return useMemo(() => {
    const ymd = (d: Date) =>
      new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: timezone,
      }).format(d);
    // Labels are formatted from a noon-UTC anchor built out of the tz-anchored
    // y/m/d parts, so they show the calendar day itself regardless of DST.
    const dayLabelFormatter = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
    const weekLabelFormatter = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });

    /** Noon-UTC Date for a `YYYY-MM-DD` key (label/weekday anchor, DST-proof). */
    const ymdToUtcNoon = (dateYmd: string): Date => {
      const [y, m, d] = dateYmd.split('-').map(Number);
      return new Date(Date.UTC(y, m - 1, d, 12));
    };
    /** Adds whole CALENDAR days to a `YYYY-MM-DD` key (never fixed ms). */
    const addDays = (dateYmd: string, days: number): string => {
      const [y, m, d] = dateYmd.split('-').map(Number);
      return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
    };

    /** ISO week number of a `YYYY-MM-DD` string, computed from a noon-local Date
     *  built out of the tz-anchored y/m/d parts (never the raw instant) so the
     *  week number can't drift by the browser's own timezone — mirrors
     *  `noonLocal()` + `getISOWeek()` in statusUpdatePeriod.ts. */
    const isoWeekFromYmd = (dateYmd: string): number => {
      const [y, m, d] = dateYmd.split('-').map(Number);
      return getISOWeek(new Date(y, m - 1, d, 12));
    };

    const todayIso = ymd(new Date());
    const spanDays = daysInPeriod(toDateInputValue(from), toDateInputValue(to));
    const mode: 'day' | 'week' = spanDays > 45 ? 'week' : 'day';
    const columns: TimelineColumn[] = [];

    if (mode === 'day') {
      let cursorIso = ymd(from);
      const endIso = ymd(to);
      // Calendar-day stepping: a DST 23h/25h day can never repeat or skip a key.
      while (cursorIso <= endIso) {
        const anchor = ymdToUtcNoon(cursorIso);
        columns.push({
          iso: cursorIso,
          label: dayLabelFormatter.format(anchor),
          isToday: cursorIso === todayIso,
          isWeekStart: anchor.getUTCDay() === 1,
          dayIsos: [cursorIso],
          isoWeek: null,
        });
        cursorIso = addDays(cursorIso, 1);
      }
    } else {
      const todayWeekIso = ymd(getWeekStart(new Date(), timezone));
      let weekIso = ymd(getWeekStart(from, timezone));
      const lastWeekIso = ymd(getWeekStart(to, timezone));
      while (weekIso <= lastWeekIso) {
        const dayIsos: string[] = [];
        for (let i = 0; i < 7; i++) {
          dayIsos.push(addDays(weekIso, i));
        }
        columns.push({
          iso: weekIso,
          label: weekLabelFormatter.format(ymdToUtcNoon(weekIso)),
          isToday: weekIso === todayWeekIso,
          isWeekStart: true,
          dayIsos,
          isoWeek: isoWeekFromYmd(weekIso),
        });
        weekIso = addDays(weekIso, 7);
      }
    }

    return { columns, mode };
  }, [from, to, timezone, locale]);
}

/** Fewer→More heat scale for PUBLISHED counts. Index 0 is unused (empty/no
 *  post — rendered separately); index = min(publishedCount, 4). */
const PUBLISHED_CELL_COLORS = [
  'bg-transparent',
  'bg-primary-100',
  'bg-primary-200',
  'bg-primary-400',
  'bg-primary-600',
];

const TimelineView = ({
  clients,
  range,
  bucketsByTeam,
  notesByTeam,
  holidaysByTeam,
  onClientClick,
  isLoading,
  isError = false,
}: {
  clients: PortfolioClient[];
  range: TimelineRange;
  bucketsByTeam: Map<number, Map<string, TimelineBucket>>;
  /** Campaign markers ("Campaigns" in the UI, backend model `CalendarNote`), keyed by teamId. */
  notesByTeam: Map<number, TimelineNote[]>;
  /** Public-holiday markers, keyed by teamId. */
  holidaysByTeam: Map<number, TimelineHoliday[]>;
  onClientClick: (client: PortfolioClient) => void;
  isLoading: boolean;
  /** True when the `/portfolio/timeline` fetch failed (e.g. a 400 for an
   *  over-92-day custom range) — renders an inline error row instead of
   *  silently showing an empty grid (every cell would otherwise read as
   *  "no posts"). */
  isError?: boolean;
}) => {
  const { t, i18n } = useTranslation('agency');
  const healthLocked = usePortfolioLocks().isLocked('portfolio_health_score');
  const { timezone } = useTimezone();
  const { columns, mode } = useTimelineWindow(range.from, range.to, timezone, i18n.language);
  // Task #182: shared status-label dictionary for the timeline heatmap
  // (tooltip readout, cell aria-label and legend swatches all reuse the same
  // canonical `status_*` keys, common.json via fallbackNS — never re-derive
  // wording per surface).
  const timelineStatusLabels = {
    mapped: t('status_mapped', { defaultValue: 'Mapped' }),
    scheduled: t('status_scheduled', { defaultValue: 'Scheduled' }),
    published: t('status_published', { defaultValue: 'Published' }),
    partial: t('status_partial', { defaultValue: 'Partial success' }),
    failed: t('status_failed', { defaultValue: 'Failed' }),
    missed: t('status_missed', { defaultValue: 'Missed' }),
  };
  // Week-mode columns pad out to a full Monday-Sunday ISO week, so
  // col.dayIsos can include days outside the user's selected [range.from,
  // range.to] (up to 6 days past the end). Numeric buckets are unaffected
  // (backend only returns in-range days), but campaign/holiday matching
  // below iterates col.dayIsos directly — clip it to the selected range so
  // an ongoing recurring campaign doesn't show a dot on a padding day.
  // Same `en-CA` YYYY-MM-DD formatter as useTimelineWindow's `ymd` so the
  // keys are directly comparable to col.dayIsos.
  const { rangeFromKey, rangeToKey } = useMemo(() => {
    const ymd = (d: Date) =>
      new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: timezone,
      }).format(d);
    return { rangeFromKey: ymd(range.from), rangeToKey: ymd(range.to) };
  }, [range.from, range.to, timezone]);

  if (!isLoading && isError) {
    return (
      <div className="flex items-center justify-center px-4 py-16 text-center">
        <div className="max-w-sm">
          <i className="hm hm-caution text-2xl text-gray-300" aria-hidden="true" />
          <p className="mt-2 text-sm text-gray-500">
            {t('portfolio_timeline_load_error', {
              defaultValue: 'Couldn’t load the timeline for this range. Try a shorter range.',
            })}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    const gridCols = `9rem repeat(${columns.length}, minmax(32px, 1fr))`;
    return (
      <div className="overflow-x-auto px-4 py-4">
        <div className="space-y-1.5">
          {/* Day header row skeleton */}
          <div className="grid gap-0" style={{ gridTemplateColumns: gridCols }}>
            <div /> {/* spacer for brand col */}
            {columns.map((c) => (
              <div key={c.iso} className="h-4 bg-gray-200 rounded animate-pulse mx-1" />
            ))}
          </div>
          {/* Client row skeletons */}
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="grid gap-0 items-center" style={{ gridTemplateColumns: gridCols }}>
              <div className="h-6 bg-gray-200 rounded animate-pulse mr-2" />
              {columns.map((c) => (
                <div key={c.iso} className="h-8 bg-gray-100 rounded animate-pulse mx-0.5" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Flex-row layout — sticky positioning inside CSS-Grid breaks on mobile after
  // scrolling past one screen-width. Flex with a fixed-width sticky brand cell
  // and `flex-1 min-w-[32px]` day cells works reliably across browsers.

  const cellMinWidth = mode === 'week' ? 'min-w-[52px]' : 'min-w-[32px]';

  return (
    <div className="overflow-x-auto pb-4 px-4 sm:px-6">
      <div role="grid" aria-label={t('portfolio_timeline_aria', { defaultValue: 'Timeline view' })} className="min-w-max">
        {/* Day/week header row */}
        <div className="flex items-stretch sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="w-36 shrink-0 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky left-0 bg-white z-30 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.08)]">
            {t('portfolio_col_client', { defaultValue: 'Brand' })}
          </div>
          {columns.map((col) => (
            <div
              key={col.iso}
              className={`flex-1 ${cellMinWidth} text-center py-2 text-xs font-medium ${
                col.isToday
                  ? 'border-l-2 border-primary-400 text-primary-700 bg-primary-25'
                  : mode === 'day' && col.isWeekStart
                  ? 'border-l border-gray-100 text-gray-400'
                  : 'text-gray-400'
              }`}
              aria-label={
                mode === 'week'
                  ? `${t('portfolio_timeline_week_of', { date: col.label })}${col.isoWeek !== null ? ` · ${t('portfolio_timeline_week_number', { week: col.isoWeek })}` : ''}`
                  : col.label
              }
            >
              {mode === 'day' ? (
                <>
                  {col.label.split(' ')[0]}
                  <br />
                  <span className={`font-bold ${col.isToday ? 'text-primary-700' : 'text-gray-600'}`}>
                    {col.label.split(' ')[1]}
                  </span>
                </>
              ) : (
                <>
                  {t('portfolio_timeline_week_of_short')}
                  <br />
                  <span className={`font-bold ${col.isToday ? 'text-primary-700' : 'text-gray-600'}`}>
                    {col.label}
                  </span>
                  {col.isoWeek !== null && (
                    <>
                      <br />
                      <span className="text-[10px] text-gray-400">{t('portfolio_timeline_week_number', { week: col.isoWeek })}</span>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Client rows */}
        {clients.map((client) => {
          const isPaused = client.healthOverall === 'paused';
          const tier = TIER_CONFIG[client.healthOverall];
          const dayMap = bucketsByTeam.get(client.id);
          const notes = isPaused ? [] : notesByTeam.get(client.id) ?? [];
          const holidays = isPaused ? [] : holidaysByTeam.get(client.id) ?? [];

          return (
            <div
              key={client.id}
              className={`flex items-stretch border-b border-gray-100 transition-colors group bg-white ${isPaused ? 'opacity-50' : 'hover:bg-gray-50'}`}
              role="row"
            >
              {/* Brand col — sticky to scroll container's left edge. Fixed 9rem
                  width, shrink-0, z-20 so day cells slide underneath. */}
              <button
                type="button"
                className="w-36 shrink-0 sticky left-0 z-20 bg-white group-hover:bg-gray-50 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.08)] px-4 py-2 flex items-center gap-2 text-left border border-transparent focus:outline-none focus-visible:border-primary-600"
                onClick={() => onClientClick(client)}
                aria-label={t('portfolio_timeline_row_aria', { name: client.name })}
              >
                <ClientAvatar initial={client.avatarInitial} color={client.avatarColor} icon={client.icon} customColor={client.customColor} logoUrl={client.logoUrl} name={client.name} size="sm" />
                <div className="min-w-0">
                  <span className="text-xs font-medium text-gray-800 truncate block">{client.name}</span>
                  {!healthLocked && <span className={`text-[10px] font-semibold ${tier.pillText}`}>{tierLabel(t, client.healthOverall)}</span>}
                </div>
              </button>

              {/* Timeline cells */}
              {columns.map((col) => {
                const bucket: TimelineBucket = col.dayIsos.reduce(
                  (acc, dayIso) => {
                    const b = dayMap?.get(dayIso);
                    if (!b) return acc;
                    return {
                      mapped: acc.mapped + b.mapped,
                      published: acc.published + b.published,
                      scheduled: acc.scheduled + b.scheduled,
                      partial: acc.partial + b.partial,
                      failed: acc.failed + b.failed,
                      missed: acc.missed + b.missed,
                    };
                  },
                  EMPTY_TIMELINE_BUCKET,
                );
                const { mapped, published, scheduled, partial, failed, missed } = bucket;
                const hasPublished = !isPaused && published > 0;
                const hasScheduled = !isPaused && scheduled > 0;
                const hasMapped = !isPaused && mapped > 0;
                // Task #182: Partial and Missed are first-class occurrence
                // outcomes (POST-STATUS-MODEL.md) — kept distinct from
                // Failed, never folded in, everywhere below (cell background,
                // count badge, overlay dot, tooltip, legend).
                const hasPartial = !isPaused && partial > 0;
                const hasFailed = !isPaused && failed > 0;
                const hasMissed = !isPaused && missed > 0;
                const hasAny = hasPublished || hasScheduled || hasMapped || hasPartial || hasFailed || hasMissed;

                let cellClass = 'bg-gray-50 border border-gray-100';
                if (hasPublished) {
                  cellClass = PUBLISHED_CELL_COLORS[Math.min(published, 4)];
                } else if (hasScheduled) {
                  cellClass = 'bg-primary-50 ring-1 ring-primary-200';
                } else if (hasMapped) {
                  cellClass = 'bg-primary-25 border border-dashed border-primary-300';
                } else if (hasPartial) {
                  cellClass = 'bg-amber-50 ring-1 ring-amber-200';
                } else if (hasMissed) {
                  cellClass = 'bg-gray-100 ring-1 ring-gray-300';
                } else if (hasFailed) {
                  cellClass = 'bg-red-50 ring-1 ring-red-200';
                }

                const countLabel = hasPublished
                  ? published > 1
                    ? published
                    : null
                  : hasScheduled
                  ? scheduled > 1
                    ? scheduled
                    : null
                  : hasMapped
                  ? mapped > 1
                    ? mapped
                    : null
                  : hasPartial
                  ? partial > 1
                    ? partial
                    : null
                  : hasMissed
                  ? missed > 1
                    ? missed
                    : null
                  : hasFailed && failed > 1
                  ? failed
                  : null;
                const countLabelClass = hasPublished
                  ? 'text-white'
                  : hasScheduled
                  ? 'text-primary-700'
                  : hasMapped
                  ? 'text-primary-600'
                  : hasPartial
                  ? 'text-amber-700'
                  : hasMissed
                  ? 'text-gray-600'
                  : 'text-red-600';

                // Task #182: outcome-severity overlay dots, stacked bottom-right,
                // rendered in addition to (never instead of) the cellClass base
                // color above — so a cell can surface e.g. a published-heavy
                // background AND a small failed dot if the aggregated column
                // (week mode) contains both outcomes across different posts.
                const outcomeDots: { key: string; color: string }[] = [
                  ...(hasFailed ? [{ key: 'failed', color: '#EF4444' }] : []),
                  ...(hasPartial ? [{ key: 'partial', color: '#F59E0B' }] : []),
                  ...(hasMissed ? [{ key: 'missed', color: '#6B7280' }] : []),
                ];

                // Campaign (note) + holiday markers that fall on this column.
                // Day mode matches col.iso only; week mode matches any of the
                // column's underlying days and dedupes by id (a campaign
                // spanning the week shows once). Clip to the selected range
                // first — week-mode columns pad to a full Mon-Sun week, so
                // dayIsos can include days outside [range.from, range.to].
                const inRangeDayIsos = col.dayIsos.filter(
                  (iso) => iso >= rangeFromKey && iso <= rangeToKey,
                );
                const dayDates = inRangeDayIsos.map(ymdToNoonDate);
                const matchedNotes: TimelineNote[] = [];
                const seenNoteIds = new Set<number>();
                for (const note of notes) {
                  const calendarNote = noteToCalendarNote(note);
                  const matches = dayDates.some((d) => campaignMatchesDay(calendarNote, d));
                  if (matches && !seenNoteIds.has(note.id)) {
                    seenNoteIds.add(note.id);
                    matchedNotes.push(note);
                  }
                }
                const matchedHolidays: TimelineHoliday[] = [];
                const seenHolidayIds = new Set<number>();
                for (const holiday of holidays) {
                  const holidayDate = ymdToNoonDate(holiday.holidayDate.slice(0, 10));
                  const matches = dayDates.some((d) => isSameDay(d, holidayDate));
                  if (matches && !seenHolidayIds.has(holiday.id)) {
                    seenHolidayIds.add(holiday.id);
                    matchedHolidays.push(holiday);
                  }
                }
                const hasAnnotations = matchedNotes.length > 0 || matchedHolidays.length > 0;
                const totalAnnotations = matchedNotes.length + matchedHolidays.length;
                const allAnnotationDots = [
                  ...matchedNotes.map((n) => ({ key: `note-${n.id}`, color: safeHexColor(n.color, '#3B82F6') })),
                  ...matchedHolidays.map((h) => ({
                    key: `holiday-${h.id}`,
                    color: safeHexColor(h.colorCode, '#FFD700'),
                  })),
                ];
                // 3-slot budget: show all when they fit, otherwise reserve a
                // slot for a "+N" overflow badge instead of silently dropping.
                const annotationOverflowCount = totalAnnotations > 3 ? totalAnnotations - 2 : 0;
                const annotationDots =
                  totalAnnotations <= 3 ? allAnnotationDots : allAnnotationDots.slice(0, 2);
                const annotationParts = [
                  ...matchedNotes.map((n) => `${t('portfolio_timeline_annotation_campaign', { name: n.title })}${n.calendarName ? ` · ${n.calendarName}` : ''}`),
                  ...matchedHolidays.map(
                    (h) =>
                      `${t('portfolio_timeline_annotation_holiday', { name: h.localName })}${h.countryName ? ` (${h.countryName})` : ''}${
                        h.calendarName ? ` · ${h.calendarName}` : ''
                      }`
                  ),
                ];

                const weekOfLabel =
                  col.isoWeek !== null
                    ? `${t('portfolio_timeline_week_of', { date: col.label })} (${t('portfolio_timeline_week_number', { week: col.isoWeek })})`
                    : t('portfolio_timeline_week_of', { date: col.label });
                const tooltipContent = (
                  <div className="text-left max-w-[240px] max-h-[70vh] overflow-y-auto space-y-1">
                    <div className="text-[11px] font-semibold">
                      {mode === 'week' ? weekOfLabel : col.label}
                    </div>
                    {mapped + scheduled + published + partial + failed + missed === 0 ? (
                      <div className="text-[11px] text-gray-300">{t('portfolio_timeline_legend_no_posts')}</div>
                    ) : (
                      <div className="space-y-0.5">
                        {mapped > 0 && (
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <Icon
                              name={STATUS_STYLES.MAPPED.icon}
                              size="xs"
                              className="text-gray-100 shrink-0"
                              ariaHidden
                            />
                            <span>{mapped} {timelineStatusLabels.mapped.toLowerCase()}</span>
                          </div>
                        )}
                        {scheduled > 0 && (
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <Icon
                              name={STATUS_STYLES.SCHEDULED.icon}
                              size="xs"
                              className="text-teal-300 shrink-0"
                              ariaHidden
                            />
                            <span>{scheduled} {timelineStatusLabels.scheduled.toLowerCase()}</span>
                          </div>
                        )}
                        {published > 0 && (
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <Icon
                              name={STATUS_STYLES.PUBLISHED.icon}
                              size="xs"
                              className="text-teal-200 shrink-0"
                              ariaHidden
                            />
                            <span>{published} {timelineStatusLabels.published.toLowerCase()}</span>
                          </div>
                        )}
                        {partial > 0 && (
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <Icon
                              name={STATUS_STYLES.PARTIAL.icon}
                              size="xs"
                              className="text-amber-300 shrink-0"
                              ariaHidden
                            />
                            <span>{partial} {timelineStatusLabels.partial.toLowerCase()}</span>
                          </div>
                        )}
                        {failed > 0 && (
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <Icon
                              name={STATUS_STYLES.FAILED.icon}
                              size="xs"
                              className="text-red-400 shrink-0"
                              ariaHidden
                            />
                            <span>{failed} {timelineStatusLabels.failed.toLowerCase()}</span>
                          </div>
                        )}
                        {missed > 0 && (
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <Icon
                              name={STATUS_STYLES.MISSED.icon}
                              size="xs"
                              className="text-gray-300 shrink-0"
                              ariaHidden
                            />
                            <span>{missed} {timelineStatusLabels.missed.toLowerCase()}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {matchedNotes.length > 0 && (
                      <div>
                        <div className="text-[9px] font-semibold uppercase tracking-wide text-gray-400 mt-1">
                          {t('portfolio_timeline_campaigns_heading', { n: matchedNotes.length })}
                        </div>
                        {matchedNotes.slice(0, 8).map((n) => (
                          <div key={`t-note-${n.id}`} className="flex items-center gap-1.5 text-[11px]">
                            <span
                              className="w-1.5 h-1.5 rounded-full shrink-0"
                              style={{ backgroundColor: safeHexColor(n.color, '#3B82F6') }}
                            />
                            <span className="truncate">{n.title}</span>
                            {n.calendarName && (
                              <span className="text-gray-400 truncate">· {n.calendarName}</span>
                            )}
                          </div>
                        ))}
                        {matchedNotes.length > 8 && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            {t('portfolio_timeline_more', { n: matchedNotes.length - 8 })}
                          </div>
                        )}
                      </div>
                    )}
                    {matchedHolidays.length > 0 && (
                      <div>
                        <div className="text-[9px] font-semibold uppercase tracking-wide text-gray-400 mt-1">
                          {t('portfolio_timeline_holidays_heading', { n: matchedHolidays.length })}
                        </div>
                        {matchedHolidays.slice(0, 5).map((h) => (
                          <div key={`t-hol-${h.id}`} className="flex items-center gap-1.5 text-[11px]">
                            <span
                              className="w-1.5 h-1.5 rounded-full shrink-0"
                              style={{ backgroundColor: safeHexColor(h.colorCode, '#FFD700') }}
                            />
                            <span className="truncate">
                              {h.localName}
                              {h.countryName ? ` (${h.countryName})` : ''}
                            </span>
                            {h.calendarName && (
                              <span className="text-gray-400 truncate">· {h.calendarName}</span>
                            )}
                          </div>
                        ))}
                        {matchedHolidays.length > 5 && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            {t('portfolio_timeline_more', { n: matchedHolidays.length - 5 })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );

                return (
                  <Tooltip key={col.iso} content={tooltipContent} side="top">
                    <div
                      className={`flex-1 ${cellMinWidth} flex items-center justify-center py-1.5 ${
                        col.isToday
                          ? 'border-l-2 border-primary-400 bg-primary-25/50'
                          : mode === 'day' && col.isWeekStart
                          ? 'border-l border-gray-50'
                          : ''
                      }`}
                      role="gridcell"
                      aria-label={t('portfolio_timeline_cell_aria', {
                        client: client.name,
                        date: col.label,
                        counts: [
                          `${mapped} ${timelineStatusLabels.mapped.toLowerCase()}`,
                          `${scheduled} ${timelineStatusLabels.scheduled.toLowerCase()}`,
                          `${published} ${timelineStatusLabels.published.toLowerCase()}`,
                          `${partial} ${timelineStatusLabels.partial.toLowerCase()}`,
                          `${failed} ${timelineStatusLabels.failed.toLowerCase()}`,
                          `${missed} ${timelineStatusLabels.missed.toLowerCase()}`,
                          ...annotationParts,
                        ].join(', '),
                      })}
                    >
                      {hasAny || hasAnnotations ? (
                        <div
                          className={`relative w-7 h-7 rounded-md flex items-center justify-center transition-all ${cellClass}`}
                        >
                          {countLabel !== null && (
                            <span className={`text-[10px] font-bold ${countLabelClass}`}>{countLabel}</span>
                          )}
                          {outcomeDots.length > 0 && (
                            <div className="absolute -bottom-1 -right-1 flex items-center gap-0.5" aria-hidden="true">
                              {outcomeDots.map((dot) => (
                                <span
                                  key={dot.key}
                                  className="w-1.5 h-1.5 rounded-full ring-1 ring-white"
                                  style={{ backgroundColor: dot.color }}
                                />
                              ))}
                            </div>
                          )}
                          {hasScheduled && hasPublished && (
                            <span
                              className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-primary-300 ring-1 ring-white"
                              aria-hidden="true"
                            />
                          )}
                          {hasAnnotations && (
                            <div className="absolute -top-1 -left-1 flex items-center gap-0.5" aria-hidden="true">
                              {annotationDots.map((dot) => (
                                <span
                                  key={dot.key}
                                  className="w-1.5 h-1.5 rounded-full ring-1 ring-white"
                                  style={{ backgroundColor: dot.color }}
                                />
                              ))}
                              {annotationOverflowCount > 0 && (
                                <span className="text-[7px] leading-none font-semibold text-white bg-gray-700 rounded-full px-0.5 ring-1 ring-white">
                                  +{annotationOverflowCount}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-md bg-gray-50 border border-gray-100" />
                      )}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 px-4 pt-2 pb-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 uppercase tracking-wide">{t('portfolio_timeline_legend_fewer')}</span>
          {PUBLISHED_CELL_COLORS.slice(1).map((c) => (
            <div key={c} className={`w-3 h-3 rounded ${c}`} aria-hidden="true" />
          ))}
          <span className="text-[10px] text-gray-400 uppercase tracking-wide">{t('portfolio_timeline_legend_more')}</span>
          <span className="text-[10px] text-gray-400 ml-1">{timelineStatusLabels.published}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-primary-50 ring-1 ring-primary-200" aria-hidden="true" />
          <span className="text-[10px] text-gray-400">{timelineStatusLabels.scheduled}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-primary-25 border border-dashed border-primary-300" aria-hidden="true" />
          <span className="text-[10px] text-gray-400">{timelineStatusLabels.mapped}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
          <span className="text-[10px] text-gray-400">{timelineStatusLabels.partial}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" />
          <span className="text-[10px] text-gray-400">{timelineStatusLabels.failed}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-gray-500" aria-hidden="true" />
          <span className="text-[10px] text-gray-400">{timelineStatusLabels.missed}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#3B82F6' }} aria-hidden="true" />
          <span className="text-[10px] text-gray-400">{t('portfolio_timeline_legend_campaign')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#FFD700' }} aria-hidden="true" />
          <span className="text-[10px] text-gray-400">{t('portfolio_timeline_legend_holiday')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-gray-50 border border-gray-100" aria-hidden="true" />
          <span className="text-[10px] text-gray-400">{t('portfolio_timeline_legend_no_posts')}</span>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const AgencyPortfolioPage = () => {
  useDocumentTitle('Portfolio - Ägile Ässets');
  const overlayOpen = useOverlayOpen();
  const { t } = useTranslation('agency');
  const { formatRelativeDate, formatDateOnly, timezone } = useTimezone();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    savedView,
    setSavedView,
    visibleBadges,
    toggleBadge,
    setVisibleBadges,
    getTimelinePref,
    setTimelinePref,
  } = usePortfolioStore();
  const { user, refreshUser } = useAuth();
  const centerFabOnMobile = usesAgencyStaffNav(user);
  const agencyId = user?.agencyId ?? null;

  // ── Live data ──
  const { data: portfolioData, isLoading, isError, error, refetch } = usePortfolioClients();
  const portfolioClients = portfolioData?.clients ?? [];
  // Agency-package locks (empty unless the backend enforces); never team-scoped
  const { isLocked } = usePortfolioLocks();
  const openUpsell = useGatingStore((state) => state.openUpsell);
  const brandSpaceUsage = useBrandSpaceUsage();
  const saveStatusUpdate = useSaveStatusUpdate();
  const { data: agencyStaff = [] } = useAgencyStaff();

  // Initialize from ?tab= so the active tab is deep-linkable and survives a
  // refresh; falls back to 'grid' for missing/invalid values (never crashes).
  const [view, setViewState] = useState<ViewMode>(() => {
    const tabParam = searchParams.get('tab');
    return isViewMode(tabParam) ? tabParam : 'grid';
  });

  // Wraps setView so every tab switch also syncs ?tab= (replacing history,
  // not pushing, so paging through tabs doesn't spam browser back/forward)
  // while preserving any other query params already on the URL.
  const setView = useCallback(
    (next: ViewMode) => {
      setViewState(next);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('tab', next);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ── Brand Portfolio Timeline: range state + persistence ──
  // Precedence: URL (?tp=/&tfrom=/&tto=) → per-agency localStorage pref →
  // default (current calendar month). A valid URL range wins on mount so a
  // shared link is honored immediately, before agencyId (and therefore the
  // localStorage pref) is even known. Relative presets are re-anchored to
  // TODAY on every restore (URL or localStorage) — never a stale absolute
  // window from an old link/session.
  const [timelineRange, setTimelineRangeState] = useState<TimelineRange>(() => {
    const fromUrl = parseTimelineUrlParams(searchParams, timezone);
    if (fromUrl) return fromUrl;
    return { ...getSpanRange('month', new Date(), timezone), preset: 'month' };
  });
  // Guards the one-time initial hydration below so it never re-derives from
  // the URL again later (e.g. if agencyId resolves/changes after mount) —
  // only the very first agencyId reveal gets URL precedence; that avoids
  // fighting a user's subsequent manual range changes with a stale URL read.
  const timelineHydratedRef = useRef(false);
  useEffect(() => {
    if (!agencyId) return;
    if (timelineHydratedRef.current) {
      // agencyId changed again after initial hydration — just restore that
      // agency's saved pref (URL precedence only applies to the first load).
      // No saved pref for the new agency → fall back to the month default
      // rather than leaving the previous agency's range on screen.
      const saved = getTimelinePref(agencyId, timezone);
      setTimelineRangeState(saved ?? { ...getSpanRange('month', new Date(), timezone), preset: 'month' });
      return;
    }
    timelineHydratedRef.current = true;

    const urlRange = parseTimelineUrlParams(searchParams, timezone);
    if (urlRange) {
      // The URL-driven range already won in the initial state above —
      // just persist it as this agency's pref, so a shared link becomes
      // this viewer's sticky default too.
      setTimelinePref(agencyId, urlRange);
      // One-time cleanup: strip a stale tfrom/tto left over from a
      // hand-edited/old URL when the resolved preset is relative (those
      // keys are meaningless outside tp=custom) — keeps the URL clean.
      if (urlRange.preset !== 'custom' && (searchParams.has('tfrom') || searchParams.has('tto'))) {
        setSearchParams(
          (prev) => {
            const cleaned = new URLSearchParams(prev);
            cleaned.delete('tfrom');
            cleaned.delete('tto');
            return cleaned;
          },
          { replace: true },
        );
      }
      return;
    }

    const saved = getTimelinePref(agencyId, timezone);
    if (saved) setTimelineRangeState(saved);
    // Runs once per agency becoming known — not on every store/function/
    // searchParams identity change (the ref above further scopes the URL
    // precedence branch to the very first resolution only).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId]);
  const handleTimelineRangeChange = useCallback(
    (next: TimelineRange) => {
      setTimelineRangeState(next);
      if (agencyId) setTimelinePref(agencyId, next);

      // Keep the URL in sync so the current Timeline view is shareable and
      // sticky on refresh — preserves every other existing param (tab,
      // openBrand, etc.) and never pushes history (tab-like ?tab= pattern).
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('tp', next.preset);
          if (next.preset === 'custom') {
            p.set('tfrom', toDateInputValue(next.from));
            p.set('tto', toDateInputValue(next.to));
          } else {
            p.delete('tfrom');
            p.delete('tto');
          }
          return p;
        },
        { replace: true },
      );
    },
    [agencyId, setTimelinePref, setSearchParams],
  );

  // Only fetches while the Timeline tab is active.
  const {
    data: timelineData,
    isLoading: isTimelineLoading,
    isError: isTimelineError,
  } = usePortfolioTimeline(
    timelineRange.from.toISOString(),
    timelineRange.to.toISOString(),
    timezone,
    view === 'timeline' && !isLocked('portfolio_timeline'),
  );
  const timelineBucketsByTeam = useMemo(() => {
    const map = new Map<number, Map<string, TimelineBucket>>();
    (timelineData?.teams ?? []).forEach((team) => {
      const dayMap = new Map<string, TimelineBucket>();
      team.days.forEach((d) => {
        dayMap.set(d.date, {
          mapped: d.mapped,
          published: d.published,
          scheduled: d.scheduled,
          partial: d.partial,
          failed: d.failed,
          missed: d.missed,
        });
      });
      map.set(team.teamId, dayMap);
    });
    return map;
  }, [timelineData]);
  const timelineNotesByTeam = useMemo(() => {
    const map = new Map<number, TimelineNote[]>();
    (timelineData?.teams ?? []).forEach((team) => {
      map.set(team.teamId, team.notes);
    });
    return map;
  }, [timelineData]);
  const timelineHolidaysByTeam = useMemo(() => {
    const map = new Map<number, TimelineHoliday[]>();
    (timelineData?.teams ?? []).forEach((team) => {
      map.set(team.teamId, team.holidays);
    });
    return map;
  }, [timelineData]);
  const [selectedOwnerId, setSelectedOwnerId] = useState<number | null>(null);
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<PortfolioClient | null>(null);

  // ── Compare mode state ──
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelectedIds, setCompareSelectedIds] = useState<Set<number>>(new Set());
  const [comparePanelOpen, setComparePanelOpen] = useState(false);

  const toggleCompareSelect = (id: number) => {
    setCompareSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < MAX_COMPARE) {
        next.add(id);
      }
      return next;
    });
  };

  const exitCompareMode = () => {
    setCompareMode(false);
    setCompareSelectedIds(new Set());
    setComparePanelOpen(false);
  };

  // ── Speed dial FAB state ──
  const [fabOpen, setFabOpen] = useState(false);
  const [composeClient, setComposeClient] = useState<PortfolioClient | null>(null);
  // Status-update history modal: opened by clicking the Status column in any
  // view, or via the "View all updates" link in the brand drawer.
  const [historyClient, setHistoryClient] = useState<PortfolioClient | null>(null);
  // Status updates locked by the agency package: entry points explain instead of opening the modals
  const statusUpdatesLocked = isLocked('portfolio_status_update');
  const showStatusHistory = (client: PortfolioClient) => setHistoryClient(client);
  // The status-update lock gates COMPOSE only (history stays readable); compose entry points show the upsell
  const [fabPickerOpen, setFabPickerOpen] = useState(false);
  const { canManageAgencyGovernance: canCreateBrand } = useAgencyGovernance();
  const [createBrandOpen, setCreateBrandOpen] = useState(false);
  const [planBatchOpen, setPlanBatchOpen] = useState(false);
  const [planBatchInitialClientId, setPlanBatchInitialClientId] = useState<number | null>(null);
  const [sendDigestOpen, setSendDigestOpen] = useState(false);

  // Open the create-brand modal when arriving from the onboarding wizard via ?createBrand=1
  useEffect(() => {
    if (searchParams.get('createBrand') === '1') {
      if (canCreateBrand) setCreateBrandOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('createBrand');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, canCreateBrand]);

  // Open a specific brand (and optionally kick off an action) when arriving
  // from an agency-alert email via ?openBrand=<teamId>&action=<plan_batch|compose_status>.
  //
  // `panel=approvals` + `postId` (+ `variationId`, `tab`) comes from a Brand Portfolio bell
  // notification: it enters the brand (same God Mode flow as the drawer's "Enter" button) and
  // opens the Release drawer for that item on the brand's board. `action=open_board` (bundled
  // notifications) lands on the brand's Release Board without a drawer.
  // `panel=channels` enters the brand and opens its channel settings so an
  // email's reconnect action lands on the actual connection controls.
  // Other panel values (highlights) are intentionally not wired.
  const releaseEntryInFlightRef = useRef(false);
  useEffect(() => {
    const openBrandParam = searchParams.get('openBrand');
    if (!openBrandParam) return;
    // Defer until the portfolio list has actually loaded — resolving against
    // an empty/stale array would silently drop the deep link.
    if (isLoading) return;

    const teamId = parseInt(openBrandParam, 10);

    const next = new URLSearchParams(searchParams);
    next.delete('openBrand');
    next.delete('panel');
    next.delete('action');
    if (searchParams.get('panel') === 'approvals') {
      // Release deep-link params only; `tab` also belongs to the portfolio's own view tabs.
      next.delete('postId');
      next.delete('variationId');
      next.delete('tab');
      next.delete('column');
    }

    const rawPostId = searchParams.get('postId');
    const rawVariationId = searchParams.get('variationId');
    const rawTab = searchParams.get('tab');
    const wantsRelease =
      searchParams.get('panel') === 'approvals' &&
      (/^\d+$/.test(rawPostId ?? '') || searchParams.get('action') === 'open_board');

    if (wantsRelease && !Number.isNaN(teamId)) {
      const target = new URLSearchParams();
      if (rawPostId && /^\d+$/.test(rawPostId)) {
        target.set('postId', rawPostId);
        if (rawVariationId && /^\d+$/.test(rawVariationId)) target.set('variationId', rawVariationId);
        if (rawTab === 'comments' || rawTab === 'details' || rawTab === 'history') target.set('tab', rawTab);
      }
      const column = parseBoardColumnParam(searchParams.get('column'));
      if (column) target.set('column', column);
      const query = target.toString();
      if (releaseEntryInFlightRef.current) return;
      releaseEntryInFlightRef.current = true;
      setSearchParams(next, { replace: true });
      void (async () => {
        try {
          const res = await agencyService.enterGodMode(teamId);
          setAccessToken(res.accessToken);
          await refreshUser();
          navigate(query ? `/approvals?${query}` : '/approvals');
        } catch (err) {
          showToast(
            t('portfolio_enter_workspace_error', {
              defaultValue: 'Could not enter brand: {{msg}}',
              msg: formatApiError(err).message,
            }),
            'error',
          );
        } finally {
          releaseEntryInFlightRef.current = false;
        }
      })();
      return;
    }

    if (searchParams.get('panel') === 'channels' && !Number.isNaN(teamId)) {
      if (releaseEntryInFlightRef.current) return;
      releaseEntryInFlightRef.current = true;
      setSearchParams(next, { replace: true });
      void (async () => {
        try {
          const res = await agencyService.enterGodMode(teamId);
          setAccessToken(res.accessToken);
          await refreshUser();
          navigate('/settings/channels');
        } catch (err) {
          showToast(
            t('portfolio_enter_workspace_error', {
              defaultValue: 'Could not enter brand: {{msg}}',
              msg: formatApiError(err).message,
            }),
            'error',
          );
        } finally {
          releaseEntryInFlightRef.current = false;
        }
      })();
      return;
    }

    const brand = Number.isNaN(teamId)
      ? undefined
      : portfolioClients.find((c) => c.id === teamId);

    if (brand) {
      const action = searchParams.get('action');
      // A deep link to a feature the agency package lacks: open the brand drawer and explain once
      const blockedFeature =
        action === 'compose_status' && statusUpdatesLocked
          ? 'portfolio_status_update'
          : action === 'plan_batch' && isLocked('portfolio_plan_batch')
            ? 'portfolio_plan_batch'
            : null;
      if (blockedFeature) {
        openUpsell({ feature: blockedFeature, upsellPlanSlug: null });
        setSelectedClient(brand);
      } else if (action === 'compose_status') {
        setComposeClient(brand);
      } else if (action === 'plan_batch') {
        setPlanBatchInitialClientId(brand.id);
        setPlanBatchOpen(true);
      } else {
        setSelectedClient(brand);
      }
    }

    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, portfolioClients, isLoading, navigate, refreshUser, t, statusUpdatesLocked, isLocked, openUpsell]);

  // Award strip period (Today / Week / Month / Quarter). Hoisted here so the
  // parent's auto-hide layout calc and the status-compose top-post picker
  // share the same period as the Award strip.
  const [highlightsPeriod, setHighlightsPeriod] = useState<PortfolioHighlightsPeriod>('week');
  // Velocity-trend period (Week vs prior week / Month vs prior month).
  const [velocityPeriod, setVelocityPeriod] = useState<'week' | 'month'>('week');

  // Real top-post candidates for the team being status-updated — filtered from agency highlights
  const { data: highlightsData } = usePortfolioHighlights(highlightsPeriod);
  // Visibility probe shared with AwardMomentsStrip: if even the widest window
  // (quarter) is empty, the strip self-hides and so does the layout slot.
  // React Query dedupes this fetch when the strip mounts and asks for the same.
  const { data: highlightsQuarterData } = usePortfolioHighlights('quarter');
  const composeClientTopPosts = useMemo(() => {
    if (!composeClient || !highlightsData) return [];
    const teamId = String(composeClient.id);
    return highlightsData.topPosts
      .filter((p) => p.clientId === teamId)
      .map((p) => ({
        id: p.id,
        caption: p.caption,
        metric: p.metric,
        engagementRate: p.engagementRate,
        thumbnailGradient: p.thumbnailPlaceholderGradient,
      }));
  }, [composeClient, highlightsData]);

  const handleFabAction = (actionId: SpeedDialActionId) => {
    switch (actionId) {
      case 'compose':
        if (statusUpdatesLocked) {
          openUpsell({ feature: 'portfolio_status_update', upsellPlanSlug: null }, true);
        } else {
          setFabPickerOpen(true);
        }
        break;
      case 'create_brand':
        setCreateBrandOpen(true);
        break;
      case 'plan_batch':
        if (isLocked('portfolio_plan_batch')) {
          openUpsell({ feature: 'portfolio_plan_batch', upsellPlanSlug: null }, true);
        } else {
          setPlanBatchOpen(true);
        }
        break;
      case 'send_digest':
        if (isLocked('portfolio_digest')) {
          openUpsell({ feature: 'portfolio_digest', upsellPlanSlug: null }, true);
        } else {
          setSendDigestOpen(true);
        }
        break;
    }
  };

  // ── Inline search query ──
  const [searchQuery, setSearchQuery] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ── Glossary modal ──
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  // ── Badges dropdown ──
  const [badgesDropdownOpen, setBadgesDropdownOpen] = useState(false);
  const badgesDropdownRef = useRef<HTMLDivElement>(null);

  // ── Saved views (server-persisted) ──
  const { data: customViews = [] } = usePortfolioViews();
  const viewsLocked = isLocked('portfolio_saved_views');
  // Tags / filters / search locked by the agency package: no filter UI, the grid shows every brand
  const filtersLocked = isLocked('portfolio_tags_filters');
  const createView = useCreatePortfolioView();
  const deleteView = useDeletePortfolioView();
  const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);
  const [activeCustomViewId, setActiveCustomViewId] = useState<number | null>(null);
  const [viewToDelete, setViewToDelete] = useState<PortfolioViewRecord | null>(null);
  // Toast shown after a successful save
  const [saveViewToast, setSaveViewToast] = useState<string | null>(null);
  const showSaveViewToast = (message: string) => {
    setSaveViewToast(message);
    setTimeout(() => setSaveViewToast(null), 3000);
  };

  // Apply a saved view: restore preset + lead filter + visible badges
  const handleSelectCustomView = useCallback(
    (v: PortfolioViewRecord) => {
      const f = v.filters ?? {};
      if (typeof f.preset === 'string') {
        const allowed: SavedView[] = [
          'all',
          'my_clients',
          'at_risk',
          'enterprise',
          'onboarding',
          'renewal_this_month',
          'paused',
        ];
        if ((allowed as string[]).includes(f.preset)) {
          setSavedView(f.preset as SavedView);
        }
      }
      if (f.ownerId !== undefined) {
        setSelectedOwnerId(f.ownerId ?? null);
      }
      if (Array.isArray(f.visibleBadges)) {
        const allowedBadges: BadgeId[] = [
          'lifecycle',
          'channel_health',
          'review_cliff',
          'carte_blanche',
        ];
        const next = f.visibleBadges.filter((b): b is BadgeId =>
          (allowedBadges as string[]).includes(b),
        );
        setVisibleBadges(next);
      }
      // Restore the layout the user was in when they saved this view
      // (GRID/BOARD/TIMELINE on the server → 'grid'/'board'/'timeline' here).
      switch (v.viewType) {
        case 'BOARD':
          setView('board');
          break;
        case 'TIMELINE':
          setView('timeline');
          break;
        case 'GRID':
          setView('grid');
          break;
      }
      setActiveCustomViewId(v.id);
    },
    [setSavedView, setVisibleBadges, setView],
  );

  // Clear "active custom view" marker whenever the user nudges filters manually
  const handleSelectPreset = useCallback(
    (v: SavedView) => {
      setSavedView(v);
      setActiveCustomViewId(null);
    },
    [setSavedView],
  );

  // Persist the current filter combo under a user-chosen name
  const handleCreateSavedView = useCallback(
    async (name: string) => {
      try {
        const created = await createView.mutateAsync({
          name,
          filters: {
            preset: savedView,
            ownerId: selectedOwnerId,
            visibleBadges,
          },
          viewType: view === 'board' ? 'BOARD' : view === 'timeline' ? 'TIMELINE' : 'GRID',
        });
        setActiveCustomViewId(created.id);
        setSaveViewModalOpen(false);
        showSaveViewToast(`Saved view "${created.name}"`);
      } catch (err) {
        // A refusal the global upsell already explained must not add a second toast
        if (!isGlobalGatingHandledError(err)) showSaveViewToast('Could not save view. Please try again.');
      }
    },
    [createView, savedView, selectedOwnerId, visibleBadges, view],
  );

  const handleConfirmDeleteView = useCallback(async () => {
    if (!viewToDelete) return;
    try {
      await deleteView.mutateAsync(viewToDelete.id);
      if (activeCustomViewId === viewToDelete.id) setActiveCustomViewId(null);
      showSaveViewToast(`Deleted "${viewToDelete.name}"`);
    } catch (err) {
      if (!isGlobalGatingHandledError(err)) showSaveViewToast('Could not delete view. Please try again.');
    } finally {
      setViewToDelete(null);
    }
  }, [deleteView, viewToDelete, activeCustomViewId]);

  const ownerDropdownRef = useRef<HTMLDivElement>(null);

  // Deep search - when query is ≥3 chars, hit the backend searchWorkspaces endpoint
  // (matches name, client name, post content, members) with 300 ms debounce.
  const debouncedQuery = useDebounce(searchQuery.trim(), 300);
  const isDeepSearch = debouncedQuery.length >= 3;
  const { data: searchResults } = useQuery({
    queryKey: ['agency', 'workspaces', 'search', debouncedQuery],
    queryFn: () => agencyService.searchWorkspaces(debouncedQuery),
    enabled: isDeepSearch,
    staleTime: 30_000,
  });
  const deepMatchIds = useMemo(
    () => new Set((searchResults ?? []).map((r) => r.workspaceId)),
    [searchResults],
  );
  const matchContextMap = useMemo(() => {
    if (!isDeepSearch || !searchResults) return new Map<number, string>();
    return new Map(searchResults.map((r) => [r.workspaceId, r.matchContext ?? '']));
  }, [isDeepSearch, searchResults]);

  // Filter: owner filter first, then saved view, then search (deep if ≥3 chars, local otherwise)
  const filteredClients = useMemo(() => {
    if (filtersLocked) return portfolioClients;
    const ownerFiltered = selectedOwnerId
      ? portfolioClients.filter((c) => c.ownerId === selectedOwnerId)
      : portfolioClients;
    // A persisted health-based preset must not filter once the health score is locked
    const effectiveSavedView: SavedView =
      (savedView === 'at_risk' && isLocked('portfolio_health_score')) ||
      (RELATIONSHIP_PRESETS.includes(savedView) && isLocked('portfolio_client_relationship'))
        ? 'all'
        : savedView;
    const viewFiltered = applySavedView(ownerFiltered, effectiveSavedView, user?.id ?? null);
    if (!searchQuery.trim()) return viewFiltered;
    if (isDeepSearch) {
      // Backend deep-search: only keep clients whose team id matched
      return viewFiltered.filter((c) => deepMatchIds.has(c.id));
    }
    // Local substring filter for 1–2 char queries
    const q = searchQuery.toLowerCase();
    return viewFiltered.filter((c) => c.name.toLowerCase().includes(q));
  }, [filtersLocked, isLocked, portfolioClients, selectedOwnerId, savedView, searchQuery, isDeepSearch, deepMatchIds, user?.id]);

  // Reset every filter the grid applies (preset, owner, search) - used by the empty-state "Clear filters".
  const handleClearFilters = useCallback(() => {
    setSavedView('all');
    setActiveCustomViewId(null);
    setSelectedOwnerId(null);
    setSearchQuery('');
  }, [setSavedView]);

  // Saved view label for header subtitle
  const savedViewConfig = SAVED_VIEW_CONFIGS.find((v) => v.id === savedView);
  // Locked filters / hidden presets never carry into the save-view modal
  const presetHidden =
    filtersLocked ||
    (savedView === 'at_risk' && isLocked('portfolio_health_score')) ||
    (RELATIONSHIP_PRESETS.includes(savedView) && isLocked('portfolio_client_relationship'));
  const savedViewLabel = presetHidden
    ? t('portfolio_view_all', { defaultValue: 'All clients' })
    : t(savedViewConfig?.labelKey ?? 'portfolio_view_all', {
        defaultValue: savedViewConfig?.defaultLabel ?? 'All clients',
      });

  const compareClients = portfolioClients.filter((c) => compareSelectedIds.has(c.id));

  const selectedOwner = selectedOwnerId
    ? agencyStaff.find((s) => s.id === selectedOwnerId)
    : null;

  useEffect(() => {
    if (!ownerDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (ownerDropdownRef.current && !ownerDropdownRef.current.contains(e.target as Node)) {
        setOwnerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ownerDropdownOpen]);

  useEffect(() => {
    if (!badgesDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (badgesDropdownRef.current && !badgesDropdownRef.current.contains(e.target as Node)) {
        setBadgesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [badgesDropdownOpen]);

  // Fab picker (compose brand selector) outside click is handled by its own panel

  // Release inbox counts come from the server (one definition of "awaiting" / "changes requested",
  // shared with the dashboard and the nav badge). The chip and the Inbox tab badge both count what
  // waits for MY release; "changes requested" has its own count inside its tab only.
  const { data: inboxCounts } = useReleaseInboxCounts(!isLocked('portfolio_inbox'));
  const needReviewCount = inboxCounts?.awaiting ?? 0;

  // Low-pipeline count: brands with less than 2 weeks of planned + ready content (server SSOT)
  const lowPipelineCount = useMemo(() => countLowPipeline(portfolioClients), [portfolioClients]);

  const VIEW_TABS: { id: ViewMode; icon: string; label: string; tooltip: string; badge?: number }[] = [
    { id: 'grid', icon: 'hm-list', label: t('portfolio_view_grid', { defaultValue: 'Grid' }), tooltip: t('portfolio_tooltip_view_grid', { defaultValue: 'Spreadsheet view - all clients with health signals at a glance.' }) },
    { id: 'board', icon: 'hm-board', label: t('portfolio_view_board', { defaultValue: 'Board' }), tooltip: t('portfolio_tooltip_view_board', { defaultValue: 'Group by health tier. Spot where to focus.' }) },
    { id: 'timeline', icon: 'hm-timeline', label: t('portfolio_view_timeline', { defaultValue: 'Timeline' }), tooltip: t('portfolio_tooltip_view_timeline', { defaultValue: '14-day content heat-map. See scheduling gaps per client.' }) },
    { id: 'workload', icon: 'hm-group', label: t('portfolio_view_workload', { defaultValue: 'Workload' }), tooltip: t('portfolio_tooltip_view_workload', { defaultValue: 'Brands grouped by assigned portfolio lead. Spot overload.' }) },
    { id: 'inbox', icon: 'hm-inbox', label: t('portfolio_view_inbox', { defaultValue: 'Inbox' }), tooltip: t('portfolio_tooltip_view_inbox', { defaultValue: 'Waiting for your release and changes requested, across all brands.' }), badge: isLocked('portfolio_inbox') ? undefined : needReviewCount },
  ];

  // Masthead: week-of date — anchors to the current week's Monday so the
  // string updates as the week rolls over (no more hardcoded demo date).
  const mastheadDate = useMemo(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday = 0 offset
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return t('portfolio_masthead_week_of', { date: formatDateOnly(d.toISOString()), defaultValue: 'Week of {{date}}' }).toUpperCase();
  }, [formatDateOnly, t]);

  // ── Error state ──────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-coral-50 flex items-center justify-center mb-4">
          <i className="hm hm-bell text-coral-500" style={{ fontSize: 28 }} aria-hidden="true" />
        </div>
        <h2 className="font-wagner text-2xl text-gray-900 mb-2">
          {t('portfolio_load_error_title', { defaultValue: 'Could not load portfolio' })}
        </h2>
        <p className="text-sm text-gray-500 max-w-md mb-4">
          {error instanceof Error ? error.message : t('portfolio_load_error_desc', { defaultValue: 'Something went wrong.' })}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-500 text-primary-contrast text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors"
        >
          {t('portfolio_load_error_retry', { defaultValue: 'Try again' })}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white min-h-screen relative">
      {/* Grain overlay - static, atmospheric, neutral */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        aria-hidden="true"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          opacity: 0.025,
        }}
      />

      {/* ── Page header banner (whitelabel-aware) ────────────────────────── */}
      <div className="relative z-10">
        <PageHeader
          title={t('portfolio_title', { defaultValue: 'Brand Portfolio' })}
          subtitle={mastheadDate}
          icon="board"
          headerType="agencycc"
          documentTitle={t('portfolio_title', { defaultValue: 'Brand Portfolio' })}
        />
      </div>

      {/* ── Command chips strip - compact secondary toolbar ──────────────── */}
      <div className="relative z-10 bg-white px-4 sm:px-6 pt-3 pb-2 portfolio-section flex items-center justify-end gap-1.5 flex-wrap">
        {/* Brand count */}
        <span className="text-xs font-medium text-gray-700 tabular-nums whitespace-nowrap">
          {t('portfolio_masthead_brands', { defaultValue_one: '{{count}} brand', defaultValue_other: '{{count}} brands', count: portfolioClients.length })}
        </span>

        {brandSpaceUsage && (
          <>
            <span className="text-gray-300 text-xs" aria-hidden="true">·</span>
            <BrandSpaceUsageText usage={brandSpaceUsage} className="text-xs font-medium text-gray-500 tabular-nums whitespace-nowrap" />
          </>
        )}

        {/* Need review chip (the release inbox is a package feature) */}
        {!isLocked('portfolio_inbox') && (
        <>
        <span className="text-gray-300 text-xs" aria-hidden="true">·</span>

        <button
          type="button"
          onClick={() => setView('inbox')}
          className={`flex items-center gap-1 text-xs font-medium transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600 rounded tabular-nums whitespace-nowrap ${
            needReviewCount > 0
              ? 'text-coral-600 hover:text-coral-700'
              : 'text-gray-400 cursor-default'
          }`}
          aria-label={t('portfolio_masthead_need_review_aria', { defaultValue: 'Jump to inbox - {{count}} posts need review', count: needReviewCount })}
          tabIndex={needReviewCount === 0 ? -1 : 0}
        >
          {needReviewCount > 0 && (
            <span className="w-1.5 h-1.5 rounded-full bg-coral-500 shrink-0" aria-hidden="true" />
          )}
          {t('portfolio_masthead_need_review', { defaultValue: '{{count}} need review', count: needReviewCount })}
        </button>
        </>
        )}

        {/* Low pipeline chip (a runway reading) */}
        {!isLocked('portfolio_runway') && (
        <>
        <span className="text-gray-300 text-xs" aria-hidden="true">·</span>
        <Tooltip
          content={t('portfolio_masthead_low_pipeline_tooltip', {
            defaultValue: 'Brands with less than 2 weeks of planned and ready content.',
          })}
          side="bottom"
          delayDuration={300}
        >
          <button
            type="button"
            onClick={() => {
              if (lowPipelineCount > 0) {
                setView('grid');
              }
            }}
            className={`text-xs font-medium transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600 rounded tabular-nums whitespace-nowrap ${
              lowPipelineCount > 0
                ? 'text-coral-700 hover:text-coral-700'
                : 'text-gray-400 cursor-default'
            }`}
            aria-label={t('portfolio_masthead_low_pipeline_aria', {
              defaultValue: '{{count}} brands with a low pipeline',
              count: lowPipelineCount,
            })}
            tabIndex={lowPipelineCount === 0 ? -1 : 0}
          >
            {t('portfolio_masthead_low_pipeline', {
              defaultValue: '{{count}} low pipeline',
              count: lowPipelineCount,
            })}
          </button>
        </Tooltip>
        </>
        )}

        <span className="hidden sm:inline text-gray-300 text-xs" aria-hidden="true">·</span>

        {/* Settings also lives in the mobile bottom navigation. */}
        <Tooltip content={t('portfolio_masthead_admin_tooltip', { defaultValue: 'Brand Portfolio settings' })} side="bottom" delayDuration={300}>
          <button
            type="button"
            onClick={() => navigate('/agency/settings')}
            className="hidden sm:flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
            aria-label={t('portfolio_masthead_admin_aria', { defaultValue: 'Open Brand Portfolio settings' })}
          >
            <i className="hm hm-tools" style={{ fontSize: 14 }} aria-hidden="true" />
          </button>
        </Tooltip>

        {/* Glossary */}
        <Tooltip content={t('portfolio_glossary_trigger_tooltip', { defaultValue: 'Open glossary - definitions for every term on this page.' })} side="bottom" delayDuration={300}>
          <button
            type="button"
            onClick={() => setGlossaryOpen(true)}
            className="flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
            aria-label={t('portfolio_glossary_trigger_aria', { defaultValue: 'Open portfolio glossary' })}
          >
            <i className="hm hm-question" style={{ fontSize: 14 }} aria-hidden="true" />
          </button>
        </Tooltip>
      </div>

      {/* ── Hairline rule before controls ── */}
      <div className="border-b border-gray-900/10" />

      {/* ── Controls strip (view tabs + search + filters + compare + refresh) ──
           Mobile: stacked 3-row layout (view pills full-width / search full-width /
           filter+action cluster). Desktop (sm+): single horizontal row.
           Sticky to top of scroll container (offset by the page navbar) so the
           view switcher stays accessible when the user scrolls down. */}
      <div className="sticky top-navbar-height z-30 bg-white px-4 sm:px-6 pt-3 pb-3 portfolio-section">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 sm:flex-wrap">
            {/* View toggle (segmented pill) - desktop */}
            <div className="hidden sm:flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-lg" role="tablist" aria-label={t('portfolio_view_mode_aria')}>
              {VIEW_TABS.map((tab) => (
                <Tooltip key={tab.id} content={tab.tooltip} side="bottom" delayDuration={300}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={view === tab.id}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors duration-150 border border-transparent focus:outline-none focus-visible:border-primary-600 ${
                      view === tab.id
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setView(tab.id)}
                  >
                    <i className={`hm ${tab.icon}`} style={{ fontSize: 14 }} aria-hidden="true" />
                    {tab.label}
                    {tab.badge !== undefined && tab.badge > 0 && (
                      <span
                        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold leading-none px-1.5 ${
                          view === tab.id
                            ? 'bg-coral-500 text-coral-contrast'
                            : 'bg-coral-100 text-coral-600'
                        }`}
                        aria-label={t('portfolio_tab_badge_aria', { count: tab.badge })}
                      >
                        {tab.badge > 99 ? '99+' : tab.badge}
                      </span>
                    )}
                  </button>
                </Tooltip>
              ))}
            </div>

            {/* Mobile: icon-only segmented pills filling the full row */}
            <div
              className="sm:hidden flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-lg w-full"
              role="tablist"
              aria-label={t('portfolio_view_mode_aria')}
            >
              {VIEW_TABS.map((tab) => {
                const isActive = view === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={tab.label}
                    title={tab.label}
                    onClick={() => setView(tab.id)}
                    className={`relative flex-1 inline-flex items-center justify-center h-9 rounded-md transition-colors duration-150 border border-transparent focus:outline-none focus-visible:border-primary-600 ${
                      isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                    }`}
                  >
                    <i className={`hm ${tab.icon}`} style={{ fontSize: 16 }} aria-hidden="true" />
                    {tab.badge !== undefined && tab.badge > 0 && (
                      <span
                        className="absolute top-0.5 right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-none bg-coral-500 text-coral-contrast ring-2 ring-gray-100"
                        aria-label={t('portfolio_tab_badge_aria', { count: tab.badge })}
                      >
                        {tab.badge > 99 ? '99+' : tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Right-aligned cluster: search + filters + badges + actions */}
            <div className="flex items-center gap-1.5 sm:gap-3 ml-auto flex-wrap justify-end">
            {/* Inline search - hidden when inbox is active */}
            {view !== 'inbox' && !filtersLocked && (
              <>
                {/* Desktop: always-visible input */}
                <div className="hidden sm:block relative w-52">
                  <i className="hm hm-search absolute left-2.5 top-1/2 -translate-y-1/2 z-10 text-gray-400" style={{ fontSize: 13 }} aria-hidden="true" />
                  <Input
                    type="text"
                    inputSize="sm"
                    placeholder={t('portfolio_search_placeholder', { defaultValue: 'Find in a brand' })}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="!pl-8 !pr-7 !h-[30px] !py-0 lg:!h-[30px] lg:!py-0 lg:!text-xs"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 z-10 px-2 flex items-center text-gray-300 hover:text-gray-500 transition-colors"
                      aria-label={t('portfolio_search_clear_aria')}
                    >
                      <i className="hm hm-cross leading-none" style={{ fontSize: 10 }} aria-hidden="true" />
                    </button>
                  )}
                </div>

                {/* Mobile: tap icon → search takes over the whole row.
                    Expanded mode renders a full-width input + close button and
                    hides every other control (we use `[data-search-open]` on the
                    cluster wrapper to drive that via CSS). */}
                <div className="sm:hidden flex-1 min-w-0">
                  {searchExpanded ? (
                    <div className="relative w-full">
                      <i className="hm hm-search absolute left-2.5 top-1/2 -translate-y-1/2 z-10 text-gray-400" style={{ fontSize: 13 }} aria-hidden="true" />
                      <Input
                        ref={searchInputRef}
                        type="text"
                        inputSize="sm"
                        placeholder={t('portfolio_search_placeholder', { defaultValue: 'Find in a brand' })}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="!pl-8 !pr-9 !h-[34px] !py-0"
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery('');
                          setSearchExpanded(false);
                        }}
                        aria-label={t('common_cancel', { defaultValue: 'Cancel' })}
                        className="absolute inset-y-0 right-0 z-10 px-2.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <i className="hm hm-cross" style={{ fontSize: 12 }} aria-hidden="true" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSearchExpanded(true)}
                      className="flex items-center justify-center w-[30px] h-[30px] rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:border-primary-600"
                      aria-label={t('portfolio_search_placeholder', { defaultValue: 'Find a brand…' })}
                      title={t('portfolio_search_placeholder', { defaultValue: 'Find a brand…' })}
                    >
                      <i className="hm hm-search" style={{ fontSize: 14 }} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </>
            )}

            {/* On mobile, hide filters/actions while the search input has the row.
                Desktop always shows them inline. */}
            <div
              className={`flex items-center gap-1.5 sm:gap-3 ${
                searchExpanded ? 'hidden sm:flex' : 'flex'
              }`}
            >

            {/* Filters locked by the agency package: ONE compact locked control (search, saved views and owner filter all stay out: filters are ignored while locked, so a selectable view would be a no-op) */}
            {filtersLocked && view !== 'inbox' && (
              <LockedActionButton
                feature="portfolio_tags_filters"
                label={t('portfolio_filters_button', { defaultValue: 'Filters' })}
                iconClass="hm-filter"
              />
            )}

            {/* Filters group - views + owner, visually joined */}
            {!filtersLocked && (
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white">
              {/* Saved views dropdown */}
              <SavedViewsDropdown
                savedView={savedView}
                onSelect={handleSelectPreset}
                onSaveStub={() => setSaveViewModalOpen(true)}
                customViews={customViews}
                viewsLocked={viewsLocked}
                activeCustomViewId={activeCustomViewId}
                onSelectCustomView={handleSelectCustomView}
                onDeleteCustomView={setViewToDelete}
              />
              <div className="w-px h-5 bg-gray-200 shrink-0" aria-hidden="true" />
              {/* Owner filter */}
              <div ref={ownerDropdownRef} className="relative">
                <Tooltip content={t('portfolio_tooltip_owner_filter', { defaultValue: 'Filter to clients where you (or selected staff) are the assigned lead.' })} side="bottom" delayDuration={300}>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150 border border-transparent focus:outline-none focus-visible:border-primary-600"
                    onClick={() => setOwnerDropdownOpen((v) => !v)}
                    aria-label={t('portfolio_filter_owner_aria')}
                    title={t('portfolio_filter_owner_aria')}
                    aria-expanded={ownerDropdownOpen}
                  >
                  {selectedOwner ? (
                    <>
                      <span
                        className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[9px] font-bold ${avatarTextClass(selectedOwner.avatarColor)} leading-none tracking-[0.02em] ${selectedOwner.avatarColor}`}
                      >
                        <span className="translate-y-px">{selectedOwner.initials}</span>
                      </span>
                      <span className="hidden sm:inline">{selectedOwner.name}</span>
                    </>
                  ) : (
                    <>
                      <i className="hm hm-group" style={{ fontSize: 14 }} aria-hidden="true" />
                      <span className="hidden sm:inline">{t('portfolio_filter_all_owners', { defaultValue: 'All Owners' })}</span>
                    </>
                  )}
                  <SelectChevron className="ml-0.5 shrink-0" />
                  </button>
                </Tooltip>

                {ownerDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1.5 z-30 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1 overflow-hidden">
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                      onClick={() => { setSelectedOwnerId(null); setOwnerDropdownOpen(false); }}
                    >
                      <i className="hm hm-group text-gray-400" style={{ fontSize: 14 }} aria-hidden="true" />
                      {t('portfolio_filter_all_owners', { defaultValue: 'All Owners' })}
                    </button>
                    {agencyStaff.map((staff) => (
                      <button
                        key={staff.id}
                        type="button"
                        className={`w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 ${selectedOwnerId === staff.id ? 'bg-primary-25 text-primary-700 font-medium' : ''}`}
                        onClick={() => { setSelectedOwnerId(staff.id); setOwnerDropdownOpen(false); }}
                      >
                        <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${avatarTextClass(staff.avatarColor)} leading-none tracking-[0.02em] shrink-0 overflow-hidden ${staff.profilePhotoUrl ? '' : staff.avatarColor}`}>
                          {staff.profilePhotoUrl ? (
                            <img
                              src={staff.profilePhotoUrl}
                              alt=""
                              className="w-full h-full rounded-full object-cover"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <span className="translate-y-px">{staff.initials}</span>
                          )}
                        </span>
                        {staff.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Badges dropdown - Grid view only */}
            {view === 'grid' && (
              <div ref={badgesDropdownRef} className="relative">
                <Tooltip content={t('portfolio_tooltip_badges_filter', { defaultValue: 'Choose which tags to show in the Tags column.' })} side="bottom" delayDuration={300}>
                  <button
                    type="button"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 ${filterTriggerHover} transition-colors duration-150 ${filterTriggerFocus}`}
                    onClick={() => setBadgesDropdownOpen((v) => !v)}
                    aria-expanded={badgesDropdownOpen}
                    aria-label={t('portfolio_tags_configure_aria')}
                    title={t('portfolio_tags_configure_aria')}
                  >
                    <i className="hm hm-tag" style={{ fontSize: 13 }} aria-hidden="true" />
                    <span className="hidden sm:inline">{t('portfolio_badges_button', { defaultValue: 'Tags' })}</span>
                    <SelectChevron className="ml-0.5 shrink-0" />
                  </button>
                </Tooltip>

                {badgesDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1.5 z-30 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 overflow-hidden">
                    <p className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.15em] text-gray-400 font-medium">
                      {t('portfolio_badges_dropdown_title', { defaultValue: 'Show tags' })}
                    </p>
                    {BADGE_CONFIGS.map((badge) => {
                      const isOn = visibleBadges.includes(badge.id);
                      return (
                        <label
                          key={badge.id}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <Checkbox
                            checked={isOn}
                            onCheckedChange={() => toggleBadge(badge.id)}
                            inline
                          />
                          {t(badge.labelKey, { defaultValue: badge.defaultLabel })}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Actions group: Compare + Refresh */}
            <div className="flex items-center gap-1.5">
              {/* Compare button - Grid view only. Locked by the agency package: stays, with a lock, click opens the upsell. */}
              {view === 'grid' && isLocked('portfolio_compare') && (
                <LockedActionButton
                  feature="portfolio_compare"
                  label={t('compare_toggle', { defaultValue: 'Compare' })}
                  iconClass="hm-compare"
                />
              )}
              {view === 'grid' && !isLocked('portfolio_compare') && (
                compareMode ? (
                  <Tooltip content={t('portfolio_tooltip_compare_exit', { defaultValue: 'Leave compare mode without opening the comparison.' })} side="bottom" delayDuration={300}>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-coral-200 bg-coral-50 text-sm font-medium text-coral-700 hover:bg-coral-100 transition-colors duration-150 focus:outline-none focus-visible:border-coral-500"
                      onClick={exitCompareMode}
                      aria-label={t('compare_exit', { defaultValue: 'Exit compare mode' })}
                      title={t('compare_exit', { defaultValue: 'Exit compare mode' })}
                    >
                      <i className="hm hm-cross" style={{ fontSize: 13 }} aria-hidden="true" />
                      <span className="hidden sm:inline">
                        {t('compare_exit', { defaultValue: 'Exit compare' })}
                      </span>
                    </button>
                  </Tooltip>
                ) : (
                  <Tooltip content={t('portfolio_tooltip_compare_toggle', { defaultValue: 'Select 2–3 clients to compare KPIs side by side.' })} side="bottom" delayDuration={300}>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus-visible:border-primary-600"
                      onClick={() => { setCompareMode(true); setCompareSelectedIds(new Set()); }}
                      aria-label={t('compare_toggle', { defaultValue: 'Compare clients' })}
                      title={t('compare_toggle', { defaultValue: 'Compare clients' })}
                    >
                      <i className="hm hm-compare" style={{ fontSize: 14 }} aria-hidden="true" />
                      <span className="hidden sm:inline">
                        {t('compare_toggle', { defaultValue: 'Compare' })}
                      </span>
                    </button>
                  </Tooltip>
                )
              )}

              {/* Refresh - icon only */}
              <Tooltip content={t('portfolio_refresh_aria')}>
                <button
                  type="button"
                  onClick={() => { void refetch(); }}
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus-visible:border-primary-600"
                  aria-label={t('portfolio_refresh_aria')}
                  title={t('portfolio_refresh_aria')}
                >
                  <i className={`hm hm-refresh${isLoading ? ' animate-spin' : ''}`} style={{ fontSize: 15 }} aria-hidden="true" />
                </button>
              </Tooltip>
            </div>
            </div>{/* end filters/actions wrapper (hidden when mobile search open) */}
            </div>{/* end right-aligned cluster */}
          </div>
        </div>

      {/* ── KPI Strip - hidden when inbox active ──────────────────────────── */}
      {view !== 'inbox' && <KpiStrip clients={filteredClients} isLoading={isLoading} />}

      {/* ── Highlight strips (Award + Repurpose) ────────────────────────────
           Award self-hides when even the widest period (quarter) has no
           award-worthy posts. Repurpose self-hides when no candidates exist
           in its fixed 14–90 day age band.
           Layout: both visible → 50/50 on lg+; only one visible → full width;
           neither → nothing rendered. */}
      {view !== 'inbox' && (() => {
        const awardLocked = isLocked('portfolio_highlights');
        const repurposeLocked = isLocked('portfolio_repurposing');
        // Really locked: a slim teaser row. Allowed but nothing eligible: hidden, as before.
        const hasAward = awardLocked || (highlightsQuarterData?.topPosts?.length ?? 0) > 0;
        const hasRepurpose = repurposeLocked || (highlightsData?.repurposeCandidates?.length ?? 0) > 0;
        if (!hasAward && !hasRepurpose) return null;
        const bothVisible = hasAward && hasRepurpose;
        return (
          <div
            className={
              bothVisible
                ? 'grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-gray-900/8'
                : ''
            }
          >
            {hasAward &&
              (awardLocked ? (
                <LockedTeaserRow feature="portfolio_highlights" icon={ICONS.TROPHY} className="border-x-0" />
              ) : (
                <AwardMomentsStrip
                  period={highlightsPeriod}
                  onPeriodChange={setHighlightsPeriod}
                />
              ))}
            {hasRepurpose &&
              (repurposeLocked ? (
                <LockedTeaserRow feature="portfolio_repurposing" icon={ICONS.REFRESH} className="border-x-0" />
              ) : (
                <RepurposingOpportunitiesStrip />
              ))}
          </div>
        );
      })()}

      {/* ── Timeline range controls — only shown on the Timeline tab ────────── */}
      {view === 'timeline' && !isLocked('portfolio_timeline') && (
        <div className="px-4 sm:px-6 py-2 border-b border-gray-900/10 bg-white">
          <TimelineControls
            value={timelineRange}
            onChange={handleTimelineRangeChange}
            timezone={timezone}
          />
        </div>
      )}

      {/* ── Main Body ───────────────────────────────────────────────────────
           Bottom padding clears the floating Speed Dial so the last row's
           click target isn't hidden behind the FAB on either viewport. */}
      <div className="relative z-10 flex-1 overflow-auto bg-white pt-4 pb-32 lg:pb-24">
        {view === 'grid' && (
          <GridView
            clients={filteredClients}
            onRowClick={setSelectedClient}
            onShowStatusHistory={showStatusHistory}
            formatRelativeDate={formatRelativeDate}
            isLoading={isLoading}
            compareMode={compareMode}
            selectedIds={compareSelectedIds}
            onToggleSelect={toggleCompareSelect}
            visibleBadges={visibleBadges}
            matchContextMap={matchContextMap}
            velocityPeriod={velocityPeriod}
            onVelocityPeriodChange={setVelocityPeriod}
            onClearFilters={handleClearFilters}
          />
        )}
        {view === 'board' && (
          <BoardView
            clients={filteredClients}
            onCardClick={setSelectedClient}
            onShowStatusHistory={showStatusHistory}
            isLoading={isLoading}
          />
        )}
        {view === 'timeline' && (
          <PortfolioFeatureGate feature="portfolio_timeline">
          <TimelineView
            clients={filteredClients}
            range={timelineRange}
            bucketsByTeam={timelineBucketsByTeam}
            notesByTeam={timelineNotesByTeam}
            holidaysByTeam={timelineHolidaysByTeam}
            onClientClick={setSelectedClient}
            isLoading={isLoading || isTimelineLoading}
            isError={isTimelineError}
          />
          </PortfolioFeatureGate>
        )}
        {view === 'workload' && (
          <PortfolioFeatureGate feature="portfolio_workload">
            <WorkloadView
              filteredClients={filteredClients}
              isLoading={isLoading}
            />
          </PortfolioFeatureGate>
        )}
        {view === 'inbox' && (
          <PortfolioFeatureGate feature="portfolio_inbox">
            <div className="px-4 sm:px-6 py-6">
              <PortfolioInboxView
                clients={filteredClients}
                totalClientCount={portfolioClients.length}
              />
            </div>
          </PortfolioFeatureGate>
        )}
      </div>

      {/* ── Brand overview drawer ──────────────────────────────────────────── */}
      {selectedClient && !compareMode && (
        <MockDrawerOverlay
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
          onShowStatusHistory={showStatusHistory}
          onPlanBatch={(client) => {
            setSelectedClient(null);
            setPlanBatchInitialClientId(client.id);
            setPlanBatchOpen(true);
          }}
        />
      )}

      {/* ── Compare floating bar ────────────────────────────────────────────
           Mobile (<sm): full-width, sits flush above the BottomNav (--bottom-nav-h),
           inner content uses gap-2 + smaller hit targets. The "selected
           count" label collapses to just the number to free space.
           Desktop (sm+): centered pill, bottom-6, full labels.
           Compare mode hides the FAB entirely so the right edge is free. */}
      {compareMode && (
        <div
          className="fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+var(--floating-stack-offset))] left-0 right-0 z-40 sm:bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+1.5rem+var(--floating-stack-offset))] lg:bottom-6 sm:left-1/2 sm:-translate-x-1/2 sm:w-auto pointer-events-none"
          role="status"
          aria-live="polite"
          aria-label={t('compare_bar_label', { defaultValue: 'Compare selection' })}
        >
          <div className="pointer-events-auto bg-gray-900 text-white px-3 py-2.5 sm:px-4 sm:py-3 sm:rounded-2xl rounded-none shadow-2xl flex items-center gap-2 sm:gap-4">
            {/* Selection avatars */}
            <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
              {compareClients.map((c) => (
                <div
                  key={c.id}
                  className={`w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg flex items-center justify-center text-[11px] sm:text-xs font-bold ${avatarTextClass(c.avatarColor)} shrink-0 ${c.avatarColor}`}
                  aria-label={c.name}
                >
                  {c.avatarInitial}
                </div>
              ))}
              {/* Empty slots */}
              {Array.from({ length: MAX_COMPARE - compareClients.length }, (_, i) => (
                <div
                  key={i}
                  className="w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg border-2 border-dashed border-gray-600 flex items-center justify-center"
                  aria-hidden="true"
                >
                  <i className="hm hm-plus-gap text-gray-600" style={{ fontSize: 9 }} />
                </div>
              ))}
            </div>

            {/* Count label - full text on desktop, just numerator/denom on mobile */}
            <span className="text-xs sm:text-sm text-gray-300 whitespace-nowrap tabular-nums">
              <span className="sm:hidden">
                {compareClients.length}/{MAX_COMPARE}
              </span>
              <span className="hidden sm:inline">
                {t('compare_bar_count', {
                  defaultValue: '{{selected}} of {{max}} selected',
                  selected: compareClients.length,
                  max: MAX_COMPARE,
                })}
              </span>
            </span>

            {/* Open Compare button - icon-only on small screens */}
            <button
              type="button"
              disabled={compareClients.length < 2}
              className={`ml-auto sm:ml-0 flex items-center justify-center sm:justify-start gap-1.5 h-8 sm:h-auto px-3 sm:px-4 sm:py-1.5 rounded-xl text-xs sm:text-sm font-semibold transition-all shrink-0
                ${compareClients.length >= 2
                  ? 'bg-primary-500 text-primary-contrast hover:bg-primary-400 active:scale-95'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }
                border border-transparent focus:outline-none focus-visible:border-white
              `}
              onClick={() => { if (compareClients.length >= 2) setComparePanelOpen(true); }}
              aria-label={t('compare_bar_open', { defaultValue: 'Open compare panel' })}
              aria-disabled={compareClients.length < 2}
            >
              <i className="hm hm-compare" style={{ fontSize: 14 }} aria-hidden="true" />
              <span className="hidden xs:inline sm:inline">
                {t('compare_bar_open', { defaultValue: 'Open Compare' })}
              </span>
            </button>

            {/* Exit */}
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 sm:w-7 sm:h-7 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors border border-transparent focus:outline-none focus-visible:border-white shrink-0"
              onClick={exitCompareMode}
              aria-label={t('compare_exit', { defaultValue: 'Exit compare mode' })}
            >
              <i className="hm hm-cross" style={{ fontSize: 14 }} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* ── Compare Panel ────────────────────────────────────────────────── */}
      <ComparePanel
        clients={compareClients}
        isOpen={comparePanelOpen}
        onClose={() => { setComparePanelOpen(false); exitCompareMode(); }}
      />

      {/* ── Speed Dial FAB ─────────────────────────────────────────────────
           Hidden during compare mode to avoid overlapping the floating
           compare action bar at the bottom of the viewport. */}
      {!compareMode && !overlayOpen && (
        <SpeedDialFab
          centerOnMobile={centerFabOnMobile}
          isOpen={fabOpen && !composeClient && !createBrandOpen && !planBatchOpen && !sendDigestOpen}
          onToggle={() => setFabOpen((v) => !v)}
          onClose={() => setFabOpen(false)}
          onAction={handleFabAction}
          hiddenActions={[
            ...(canCreateBrand ? [] : (['create_brand'] as const)),
          ]}
          lockedActions={{
            ...(statusUpdatesLocked ? { compose: 'portfolio_status_update' } : {}),
            ...(isLocked('portfolio_plan_batch') ? { plan_batch: 'portfolio_plan_batch' } : {}),
            ...(isLocked('portfolio_digest') ? { send_digest: 'portfolio_digest' } : {}),
          }}
        />
      )}

      {/* ── Brand picker overlay (Compose Status action) ─────────────────── */}
      {fabPickerOpen && (
        <div
          className="fixed left-0 right-0 top-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:inset-0 z-50 flex items-end sm:items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={t('status_fab_picker_label', { defaultValue: 'Select brand to update' })}
        >
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
            onClick={() => setFabPickerOpen(false)}
            aria-hidden="true"
          />
          <div className="relative bg-white shadow-2xl border-t border-gray-100 sm:border-x sm:border-b overflow-hidden z-10 w-full rounded-t-2xl rounded-b-none sm:w-[calc(100vw-2rem)] sm:max-w-xs sm:rounded-2xl sm:mb-0">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-medium mb-0.5">
                {t('fab_compose_status', { defaultValue: 'Compose status' })}
              </p>
              <p className="text-xs font-semibold text-gray-700">
                {t('status_fab_picker_title', { defaultValue: 'Compose status for…' })}
              </p>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-50 py-1">
              {portfolioClients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
                  onClick={() => {
                    setComposeClient(client);
                    setFabPickerOpen(false);
                  }}
                >
                  <div
                    className={`w-7 h-7 rounded-lg inline-flex items-center justify-center font-bold ${avatarTextClass(client.avatarColor)} text-xs leading-none tracking-[0.02em] shrink-0 ${client.avatarColor}`}
                    aria-hidden="true"
                  >
                    <span className="translate-y-px">{client.avatarInitial}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{client.name}</p>
                    <p className="text-xs text-gray-400">{lifecycleStageLabel(t, client.lifecycleStage)}</p>
                  </div>
                  {client.latestStatusUpdate && (
                    <span className="text-base leading-none shrink-0" aria-hidden="true">
                      {client.latestStatusUpdate.vibe}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Status Update History Modal ───────────────────────────────────── */}
      <StatusUpdateHistoryModal
        client={historyClient}
        onClose={() => setHistoryClient(null)}
        onComposeNew={() => {
          // Open compose for the same brand from history → seamless flow
          if (historyClient) {
            if (statusUpdatesLocked) {
              openUpsell({ feature: 'portfolio_status_update', upsellPlanSlug: null }, true);
              return;
            }
            setComposeClient(historyClient);
            setHistoryClient(null);
          }
        }}
      />

      {/* ── Status Update Compose Modal ───────────────────────────────────── */}
      {composeClient && (
        <StatusUpdateComposeModal
          key={composeClient.id}
          client={composeClient}
          isOpen={true}
          onClose={() => setComposeClient(null)}
          availableTopPosts={composeClientTopPosts}
          onShowHistory={() => {
            // Open the dedicated history modal for the same brand
            setHistoryClient(composeClient);
          }}
          onSave={async (update: StatusUpdateDraft) => {
            try {
              // weekStart from the draft is "YYYY-MM-DD"; convert to full ISO datetime for the backend
              const weekStartIso = update.weekStart.includes('T')
                ? update.weekStart
                : `${update.weekStart}T00:00:00.000Z`;

              // periodEnd from the draft is "YYYY-MM-DD"; convert to end-of-day ISO datetime
              const periodEndIso = update.periodEnd
                ? update.periodEnd.includes('T')
                  ? update.periodEnd
                  : `${update.periodEnd}T23:59:59.999Z`
                : undefined;

              // topPostId is a string in the draft (mock post id like "p1"); only send numeric ids
              const topPostIdNum = update.topPostId
                ? parseInt(update.topPostId, 10)
                : null;

              await saveStatusUpdate.mutateAsync({
                teamId: parseInt(update.clientId, 10),
                weekStart: weekStartIso,
                periodEnd: periodEndIso,
                colorPill: update.colorPill,
                tldr: update.tldr,
                vibe: update.vibe ?? null,
                wins: update.wins,
                blockers: update.blockers,
                nextFocus: update.nextFocus,
                strategicNote: update.strategicNote,
                topPostId: topPostIdNum && !isNaN(topPostIdNum) ? topPostIdNum : null,
                postsPublished: update.postsPublished,
                postsPlanned: update.postsPlanned,
              });
              setComposeClient(null);
            } catch {
              showToast('Failed to save status update. Please try again.', 'error');
            }
          }}
        />
      )}

      {/* ── Create Brand Space Modal ──────────────────────────────────────── */}
      <CreateBrandSpaceModal
        isOpen={createBrandOpen}
        onClose={() => setCreateBrandOpen(false)}
      />

      {/* ── Plan Batch Modal ──────────────────────────────────────────────── */}
      <PlanBatchModal
        isOpen={planBatchOpen}
        initialClientId={planBatchInitialClientId}
        onClose={() => {
          setPlanBatchOpen(false);
          setPlanBatchInitialClientId(null);
        }}
      />

      {/* ── Send Digest Modal ─────────────────────────────────────────────── */}
      <SendDigestModal
        isOpen={sendDigestOpen}
        onClose={() => setSendDigestOpen(false)}
      />

      {/* ── Saved-view toast (success/error) ──────────────────────────────── */}
      {saveViewToast && (
        <div
          className="fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+18px+var(--floating-stack-offset))] lg:bottom-24 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gray-900 text-white text-sm font-medium rounded-2xl shadow-xl"
          role="status"
          aria-live="polite"
        >
          <i className="hm hm-info text-gray-300" style={{ fontSize: 14 }} aria-hidden="true" />
          {saveViewToast}
        </div>
      )}

      {/* ── Save View Modal ──────────────────────────────────────────────── */}
      <SaveViewModal
        isOpen={saveViewModalOpen}
        isSaving={createView.isPending}
        currentPreset={savedViewLabel}
        onSave={handleCreateSavedView}
        onClose={() => setSaveViewModalOpen(false)}
      />

      {/* ── Delete Saved View confirm ───────────────────────────────────── */}
      <ConfirmDialog
        open={viewToDelete !== null}
        onOpenChange={(o) => {
          if (!o) setViewToDelete(null);
        }}
        title={t('portfolio_delete_view_title', { defaultValue: 'Delete saved view?' })}
        description={
          viewToDelete
            ? t('portfolio_delete_view_description', { name: viewToDelete.name })
            : ''
        }
        confirmText={t('common_delete', { defaultValue: 'Delete' })}
        cancelText={t('common_cancel', { defaultValue: 'Cancel' })}
        variant="danger"
        loading={deleteView.isPending}
        onConfirm={handleConfirmDeleteView}
      />

      {/* ── Glossary Modal ────────────────────────────────────────────────── */}
      <GlossaryModal isOpen={glossaryOpen} onClose={() => setGlossaryOpen(false)} />

      {/* ── Global God Mode loading overlay ───────────────────────────────── */}
      <GodModeLoadingOverlay />

    </div>
  );
};

// ── Save View Modal ──────────────────────────────────────────────────────────
//
// Small focused modal that prompts the user to name the current filter combo
// so it can be persisted as a PortfolioView on the server. Kept intentionally
// simple: a name input, a hint that the current preset is what will be saved,
// and Save / Cancel actions.

interface SaveViewModalProps {
  isOpen: boolean;
  isSaving: boolean;
  currentPreset: string;
  onSave: (name: string) => void;
  onClose: () => void;
}

const SaveViewModal = ({
  isOpen,
  isSaving,
  currentPreset,
  onSave,
  onClose,
}: SaveViewModalProps) => {
  const { t } = useTranslation('agency');
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset field whenever the modal re-opens so old names don't stick around
  useEffect(() => {
    if (isOpen) {
      setName('');
      // Autofocus after the mount animation
      const id = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [isOpen]);

  if (!isOpen) return null;

  const canSave = name.trim().length > 0 && !isSaving;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    onSave(name.trim());
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40"
      role="dialog"
      aria-modal="true"
      aria-label={t('portfolio_save_view_title')}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSaving) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <form onSubmit={handleSubmit}>
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">
              {t('portfolio_save_view_title', { defaultValue: 'Save current view' })}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              {t('portfolio_save_view_subtitle', {
                defaultValue: 'Capture your current filter & lead selection under a name you can return to.',
              })}
            </p>
          </div>

          <div className="px-5 py-4 space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 uppercase tracking-[0.1em]">
                {t('portfolio_save_view_name_label', { defaultValue: 'View name' })}
              </span>
              <Input
                ref={inputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('portfolio_save_view_name_placeholder', {
                  defaultValue: 'e.g. Enterprise – renewal watchlist',
                })}
                maxLength={100}
                disabled={isSaving}
              />
            </label>

            <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
              <span className="text-gray-400">
                {t('portfolio_save_view_preview', { defaultValue: 'Will save filter:' })}
              </span>{' '}
              <span className="text-gray-700 font-medium">{currentPreset}</span>
            </div>
          </div>

          <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
              {t('common_cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={!canSave} loading={isSaving}>
              {t('portfolio_save_view_submit', { defaultValue: 'Save view' })}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/** Full-screen overlay shown while any Enter-God-Mode mutation is in flight.
 *  Uses React Query's useIsMutating to detect mutations keyed 'enter-god-mode'
 *  regardless of which component started them. */
const GodModeLoadingOverlay = () => {
  const { t } = useTranslation('agency');
  const isEnteringGodMode = useIsMutating({ mutationKey: ['enter-god-mode'] }) > 0;

  if (!isEnteringGodMode) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] flex items-center justify-center"
      role="status"
      aria-live="polite"
      aria-label={t('agency_cc_entering_god_mode')}
    >
      <div className="bg-white rounded-2xl p-8 shadow-xl text-center max-w-md mx-4">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {t('agency_cc_entering_god_mode', { defaultValue: 'Entering brand' })}
        </h3>
        <p className="text-gray-600">
          {t('agency_cc_switching_workspace', { defaultValue: 'Hang tight - switching context…' })}
        </p>
      </div>
    </div>
  );
};

/** Provides the agency-package locks (read once from the portfolio query) to every surface of the page. */
export const AgencyPortfolio = () => (
  <PortfolioLocksProvider>
    <AgencyPortfolioPage />
  </PortfolioLocksProvider>
);

export default AgencyPortfolio;
